import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import { io } from "socket.io-client";
import AddressAutocomplete from "../components/AddressAutocomplete";
import DateTimeField from "../components/DateTimeField";
import ArtworkUpload from "../components/ArtworkUpload";
import EmailDeliveryBadge from "../components/EmailDeliveryBadge";
import { getUserFromLocalStorage } from "../utilities/user";
import { Page, Hero, Panel, Field, Kpi, PInput, PSelect, PButton, S, C } from "../theme/pbi";
import {
  apiTicketingMyOrganizers, apiTicketingCreateOrganizer,
  apiTicketingVenues, apiTicketingCreateVenue, apiTicketingUpdateVenue, apiTicketingDeleteVenue,
  apiTicketingSeatMaps, apiTicketingCreateSeatMap, apiTicketingUpdateSeatMap, apiTicketingDeleteSeatMap,
  apiTicketingEvents, apiTicketingCreateEvent,
  apiTicketingShowings, apiTicketingCreateShowing, apiTicketingPublishShowing,
  apiTicketingUnpublishShowing, apiTicketingUpdateShowing,
  apiTicketingSales,
  apiTicketingProvisionAccount, apiTicketingResetAccount, apiTicketingDeleteShowing,
  apiTicketingListStaff, apiTicketingAddStaff, apiTicketingRemoveStaff,
  apiTicketingListRefunds, apiTicketingDecideRefund, apiTicketingMarkRefundProcessed, apiTicketingCancelShowing,
  apiTicketingUpdateOrganizer, apiTicketingUpdateEvent, apiTicketingDeleteEvent, apiTicketingDeleteOrganizer,
  apiTicketingGetOnboarding, apiTicketingSubmitOnboarding, apiTicketingReviewOnboarding,
  dashboardApiBaseUrl,
} from "../api";
import { AppButton, AppConfirmDialog } from "../components/ui";
import SetVendorLoginAction from "../components/VendorCredentials/SetVendorLoginAction";

const money = (n) => "UGX " + Number(n || 0).toLocaleString();

// "STANDARD", "2D" and "English" are cinema answers. This rail also carries
// concerts, sports, theatre, festivals and conferences — seeding those values
// for a promoter put "Standard · 2D" on the ticket row of every arena show.
const blankShowingForm = (category) => ({
  venueId: "", inventoryModel: "RESERVED_SEATING", inventoryUnitLabel: "Seat", seatMapId: "",
  startsAt: "", doorsAt: "", salesCloseAt: "",
  presentation: String(category || "").toUpperCase() === "CINEMA"
    ? { format: "STANDARD", dimension: "2D", sound: "", language: "English", subtitles: "", screenName: "" }
    : { format: "", dimension: "", sound: "", language: "", subtitles: "", screenName: "" },
  externalShowingRef: "",
  ticketTypes: [{ key: "STANDARD", name: "Standard", price: "", seatClasses: "STANDARD", gaCapacity: "", admissionsPerUnit: "1", unitLabel: "ticket" }],
});
// Time-of-day only — the week grid's columns already carry the date.
const fmtClock = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isFinite(d.getTime())
    ? d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : "—";
};
const errMsg = (e, f) => e?.response?.data?.message || f;
const toISO = (v) => (v ? new Date(v).toISOString() : null);
const CATEGORIES = [
  "CINEMA", "CONCERT", "SPORTS", "THEATER", "FESTIVAL", "CONFERENCE",
  "NIGHTLIFE", "EXHIBITION", "COMMUNITY", "EDUCATION", "GENERAL",
];
const isReservedModel = (value) => ["RESERVED_SEATING", "RESERVED_UNITS"].includes(value);
const TABS = [["events", "Events & Showings"], ["venues", "Venues"], ["seatmaps", "Reserved Layouts"], ["sales", "Sales"]];

const seatMapClasses = (m) => {
  const set = new Set();
  for (const sec of (m && m.sections) || []) for (const row of (sec.rows || [])) for (const s of (row.seats || [])) if (s.seatClass) set.add(s.seatClass);
  return [...set];
};
// ── bulk showtime helpers ────────────────────────────────────────────────────
// "15:30, 18:45, 10:00 pm" → [{h,m}]. Accepts 24h "HH:MM" or 12h "h:mm am/pm";
// anything unreadable is returned in `bad` so the operator gets told exactly
// which token to fix instead of silently losing a showtime.
const parseTimeList = (raw) => {
  const times = []; const bad = [];
  String(raw || "").split(/[,;]+/).map((s) => s.trim()).filter(Boolean).forEach((tok) => {
    const m = tok.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
    let h = m ? Number(m[1]) : NaN; const min = m ? Number(m[2]) : NaN;
    if (m && m[3]) { const ap = m[3].toLowerCase(); if (h === 12) h = 0; if (ap === "pm") h += 12; }
    if (m && h >= 0 && h <= 23 && min >= 0 && min <= 59) times.push({ h, m: min }); else bad.push(tok);
  });
  return { times, bad };
};
// Every startsAt the form describes: the picked start plus each extra time,
// repeated daily through repeatUntil (inclusive). De-duped and sorted. An
// empty result means repeatUntil is before the start date.
const expandSlots = (startsAt, times, repeatUntil) => {
  const base = new Date(startsAt);
  if (isNaN(base.getTime())) return [];
  const slotTimes = [{ h: base.getHours(), m: base.getMinutes() }, ...times];
  const lastDay = repeatUntil ? new Date(repeatUntil + "T23:59:59") : base;
  const seen = new Set(); const out = [];
  for (let day = new Date(base.getFullYear(), base.getMonth(), base.getDate()); day <= lastDay; day.setDate(day.getDate() + 1)) {
    for (const t of slotTimes) {
      const d = new Date(day.getFullYear(), day.getMonth(), day.getDate(), t.h, t.m);
      if (!seen.has(d.getTime())) { seen.add(d.getTime()); out.push(d); }
    }
  }
  return out.sort((a, b) => a - b);
};

// Dirty tracking. A form is dirty when its current value differs from the
// snapshot it was seeded with, so a save button can tell "nothing to save"
// from "unsaved work" instead of always looking the same.
const snapshot = (value) => JSON.stringify(value);

const focusField = (id) => {
  if (typeof document === "undefined") return;
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  // The id sometimes sits on a wrapper div (DateTimeField) — focus the real
  // control inside it, or the element itself when it is already focusable.
  const target = ["INPUT", "SELECT", "TEXTAREA", "BUTTON"].includes(el.tagName) ? el : el.querySelector("input, select, textarea, button");
  try { (target || el).focus({ preventScroll: true }); } catch (e) {}
};

// Seat-class strings are matched IGNORING case and spacing ("MAX · 2D" ≡
// "Max · 2D" ≡ "max·2d"). The payload always sends the layout map's exact
// spelling so the server's seat matching stays byte-identical.
const normClass = (s) => String(s || "").trim().toUpperCase().replace(/\s+/g, " ");
// Times are picked against the browser clock but every showing is stamped
// Africa/Kampala server-side. Naming the zone the operator is actually typing
// in is the honest minimum until the showing carries a real zone.
const BROWSER_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return ""; }
})();
// ── "Add a showing" guided layout ────────────────────────────────────────────
// The form is a 3-column guided page: a coloured progress rail, step CARDS
// (each with its own icon, colour and done/summary header), and a sticky
// "Showing preview" panel that assembles the showing live. Submitting opens a
// read-it-back REVIEW dialog before anything is created — the wizard's safety
// without the wizard's clicks.
const SHX_STEPS = {
  when: {
    color: C.blue, soft: C.blueSoft,
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
  },
  exp: {
    color: C.teal, soft: C.tealSoft,
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 4v16M17 4v16M3 8h4M3 16h4M17 8h4M17 16h4M3 4h18v16H3z" /></svg>,
  },
  seats: {
    color: C.purple, soft: C.purpleSoft,
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" /></svg>,
  },
  price: {
    color: C.orange, soft: C.orangeSoft,
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a3 3 0 0 0 0 6v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a3 3 0 0 0 0-6z" /><line x1="13" y1="5" x2="13" y2="19" strokeDasharray="2 3" /></svg>,
  },
};
// Row icons for the summary rail and review dialog — same SVG family as the
// step cards (no emoji glyphs, per Izi's UI-copy preference).
const SHX_ROW_ICONS = {
  when: SHX_STEPS.when.icon,
  venue: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>,
  exp: SHX_STEPS.exp.icon,
  seats: SHX_STEPS.seats.icon,
  price: SHX_STEPS.price.icon,
};
const StepCard = ({ id, step, n, title, done, summary, hint, children }) => {
  const t = SHX_STEPS[step];
  return (
    <div id={id} style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: C.shadow, marginBottom: 16, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", borderBottom: `1px solid ${C.border}`, background: `linear-gradient(90deg, ${t.color}10 0%, transparent 55%)` }}>
        <span style={{ width: 32, height: 32, borderRadius: 9, background: t.soft, color: t.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{t.icon}</span>
        <span style={{ fontSize: 12.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".5px", color: t.color, whiteSpace: "nowrap" }}>{n} · {title}{done ? " ✓" : ""}</span>
        <span style={{ fontSize: 12, marginLeft: "auto", fontWeight: done ? 600 : 700, textAlign: "right", color: done ? C.textMid : "#b26a00" }}>{done ? summary : hint}</span>
      </div>
      <div style={{ padding: 18 }}>{children}</div>
    </div>
  );
};
// Vertical progress rail beside the step cards. Nodes light up as their step is
// satisfied; clicking one scrolls to that card (focusField already knows how to
// scroll to a wrapper id and focus the first control inside it).
const StepRail = ({ steps }) => (
  <div className="shx-rail-col">
    {steps.map((s, i) => (
      <React.Fragment key={s.key}>
        {i > 0 && <div style={{ width: 2, flex: 1, minHeight: 44, background: steps[i - 1].done && s.done ? C.green : C.border, transition: "background .2s" }} />}
        <button type="button" title={s.label} onClick={() => focusField(s.target)}
          style={{ width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                   border: `2px solid ${s.done ? SHX_STEPS[s.step].color : C.border}`, background: s.done ? SHX_STEPS[s.step].soft : "#fff",
                   color: SHX_STEPS[s.step].color, cursor: "pointer", transition: "all .2s", padding: 0 }}>
          {SHX_STEPS[s.step].icon}
        </button>
      </React.Fragment>
    ))}
  </div>
);
const SubDivider = ({ children }) => (
  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: C.textLight, margin: "14px 0 10px", display: "flex", alignItems: "center", gap: 10 }}>
    {children}
    <span style={{ flex: 1, height: 1, background: C.border }} />
  </div>
);
// Stable colour per seat class (by its position in the layout's class list) and
// per ticket type (by index) — differentiated, never monochrome.
const CLASS_PALETTE = [[C.blue, C.blueSoft], [C.purple, C.purpleSoft], [C.orange, C.orangeSoft], [C.teal, C.tealSoft], [C.green, C.greenSoft], ["#b42318", "#fee4e2"]];
const classColor = (classes, cl) => CLASS_PALETTE[Math.max(0, classes.findIndex((c) => normClass(c) === normClass(cl))) % CLASS_PALETTE.length];
const TT_COLORS = [C.blue, C.purple, C.orange, C.teal, C.green];
// Miniature of the chosen layout: one dot per seat, coloured by seat class, so
// "Screen 1 — 120 units" stops being an abstract dropdown entry.
const SeatPreview = ({ map, classes }) => {
  const rows = ((map && map.sections) || []).flatMap((sec) => sec.rows || []);
  if (!rows.length) return null;
  const shown = rows.slice(0, 14);
  const wide = Math.max(...shown.map((r) => (r.seats || []).length));
  const dot = wide > 26 ? 6 : 8;
  return (
    <div style={{ border: `1px dashed ${C.border}`, borderRadius: 10, padding: 12, background: "#fbfcff", marginTop: 12 }}>
      <div style={{ textAlign: "center", fontSize: 9, fontWeight: 800, letterSpacing: 2, color: C.textLight, marginBottom: 4 }}>SCREEN / STAGE</div>
      <div style={{ height: 4, borderRadius: 4, background: "linear-gradient(90deg, transparent, #b9c6dd, transparent)", margin: "0 30px 10px" }} />
      {shown.map((row, ri) => (
        <div key={ri} style={{ display: "flex", justifyContent: "center", gap: 3, marginBottom: 3 }}>
          {(row.seats || []).map((s, si) => (
            <span key={si} style={{ width: dot, height: dot, borderRadius: 2.5, background: classColor(classes, s.seatClass || "")[0], opacity: s.seatClass ? 1 : 0.35 }} />
          ))}
        </div>
      ))}
      {rows.length > shown.length && <div style={{ textAlign: "center", fontSize: 10.5, color: C.textLight, marginTop: 4 }}>+ {rows.length - shown.length} more row{rows.length - shown.length === 1 ? "" : "s"}</div>}
      <div style={{ display: "flex", gap: 14, justifyContent: "center", marginTop: 10, fontSize: 10.5, color: C.textMid, fontWeight: 700, flexWrap: "wrap" }}>
        {classes.map((cl) => (
          <span key={cl}><i style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2.5, background: classColor(classes, cl)[0], marginRight: 4, verticalAlign: -1 }} />{cl}</span>
        ))}
      </div>
    </div>
  );
};
// The ticket type's internal code follows the customer-facing name until the
// operator overrides it by hand — one less field to explain.
const autoKey = (name) => String(name || "").toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 24);
// Layout rules that need media queries live in a scoped <style> block — inline
// styles can't collapse the rail/summary columns on narrow screens.
const SHX_CSS = `
.shx-grid{display:grid;grid-template-columns:52px minmax(0,1fr) 292px;gap:18px;align-items:start}
.shx-rail-col{position:sticky;top:14px;display:flex;flex-direction:column;align-items:center;padding-top:4px;align-self:stretch;max-height:520px}
.shx-summary-col{position:sticky;top:14px}
.shx-mobile-actions{display:none}
@media(max-width:1150px){
  .shx-grid{grid-template-columns:minmax(0,1fr)}
  .shx-rail-col,.shx-summary-col{display:none}
  .shx-mobile-actions{display:flex;align-items:center;gap:12px;margin-top:4px;flex-wrap:wrap}
}
.shx-modelgrid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px}
@media(max-width:900px){.shx-modelgrid{grid-template-columns:1fr 1fr}}
.shx-model{border:2px solid ${C.border};border-radius:10px;padding:12px;cursor:pointer;background:#fff;text-align:left;transition:all .15s;font-family:inherit}
.shx-model:hover{border-color:#b9c6dd}
.shx-model.on{border-color:${C.purple};background:${C.purpleSoft}}
/* Step 4 ticket types. A card lays out against its own width: the form column
   sits between the step rail and the sticky preview, so a viewport breakpoint
   can't tell how much room a card has. Rows wrap on flex-basis and auto-fit
   tracks instead of bootstrap md-columns (five of those in a ~560px column
   left the price showing "e.g." and the code "STAN"). */
.shx-tt-intro{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px 16px;margin-bottom:14px}
.shx-tt-intro>span{flex:1 1 260px;font-size:12px;line-height:1.55;color:${C.textLight}}
.shx-tt{position:relative;background:#fff;border:1px solid ${C.border};border-radius:12px;box-shadow:0 1px 4px rgba(15,23,42,.05);margin-bottom:14px;overflow:hidden}
.shx-tt::before{content:"";position:absolute;left:0;top:0;bottom:0;width:4px;background:var(--tt)}
.shx-tt-head{display:flex;align-items:center;gap:12px;padding:12px 16px 12px 20px;border-bottom:1px solid ${C.border};background:linear-gradient(90deg,var(--tt-soft) 0%,#fff 70%)}
.shx-tt-num{width:28px;height:28px;border-radius:8px;background:var(--tt);color:#fff;font-size:12px;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.shx-tt-title{flex:1 1 0;min-width:0}
.shx-tt-name{font-size:14px;font-weight:800;line-height:1.25;color:${C.text};white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.shx-tt-name.is-empty{color:${C.textLight}}
.shx-tt-meta{display:flex;flex-wrap:wrap;gap:2px 12px;margin-top:3px;font-size:11px;font-weight:700;letter-spacing:.2px;color:${C.textMid}}
.shx-tt-meta i{display:inline-block;width:8px;height:8px;border-radius:2px;margin-right:5px}
.shx-tt-meta .warn{color:#b26a00}
.shx-tt-price{margin-left:auto;text-align:right;line-height:1.15;flex-shrink:0}
.shx-tt-price strong{display:block;font-size:16px;font-weight:800;letter-spacing:-.2px;color:${C.text};font-variant-numeric:tabular-nums;white-space:nowrap}
.shx-tt-price span{display:block;margin-top:2px;font-size:10.5px;font-weight:700;color:${C.textLight}}
.shx-tt-price .missing{margin:0;font-size:11.5px;font-weight:800;color:#b26a00}
.shx-tt-body{padding:16px 16px 2px 20px}
.shx-tt-r1{display:flex;flex-wrap:wrap;column-gap:14px}
.shx-tt-r1>.f-name{flex:3 1 220px;min-width:0}
.shx-tt-r1>.f-price{flex:2 1 170px;min-width:0}
.shx-tt-r2{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));column-gap:14px}
.shx-tt-r2>*{min-width:0}
.shx-tt-sec{border-top:1px dashed ${C.border};padding-top:14px}
.shx-tt-sec .f-cap{max-width:260px}
.shx-adorn{display:flex;align-items:stretch;height:38px;background:${C.inputBg};border:1px solid ${C.border};border-radius:6px;overflow:hidden;transition:border-color .15s}
.shx-adorn:focus-within{border-color:${C.green}}
.shx-adorn.is-err{border-color:${C.accent}}
.shx-adorn>input{flex:1;min-width:0;height:100%;border:none;background:transparent;outline:none;padding:0 11px;font-family:inherit;font-size:14px;font-weight:700;color:${C.text};font-variant-numeric:tabular-nums}
.shx-adorn>input::placeholder{font-weight:500;color:#a0aec0}
.shx-mono{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,"Liberation Mono",monospace;font-weight:700;letter-spacing:.6px}
.shx-mono::placeholder{letter-spacing:0;font-weight:500}
.shx-adorn-pre{display:flex;align-items:center;padding:0 10px;background:#edf1f7;border-right:1px solid ${C.border};font-size:11px;font-weight:800;letter-spacing:.6px;color:${C.textMid}}
.shx-step{display:inline-flex;align-items:stretch;height:38px;border:1px solid ${C.border};border-radius:6px;overflow:hidden;background:${C.inputBg};transition:border-color .15s}
.shx-step:focus-within{border-color:${C.green}}
.shx-step.is-err{border-color:${C.accent}}
.shx-step>button{width:36px;padding:0;border:none;display:flex;align-items:center;justify-content:center;background:var(--lk-action,#083860);color:#fff;cursor:pointer;transition:background .12s}
.shx-step>button:hover:not(:disabled){background:var(--lk-action-hover,#0a4577)}
.shx-step>button:disabled{opacity:.35;cursor:not-allowed}
.shx-step>button:focus-visible{outline:2px solid #fff;outline-offset:-4px}
.shx-step>input{width:54px;height:100%;border:none;background:transparent;outline:none;text-align:center;font-family:inherit;font-size:14px;font-weight:800;color:${C.text};font-variant-numeric:tabular-nums;-moz-appearance:textfield;appearance:textfield}
.shx-step>input::-webkit-outer-spin-button,.shx-step>input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
.shx-cls-row{display:flex;flex-wrap:wrap;gap:8px;padding-top:2px}
.shx-cls-row.is-err{outline:1px solid ${C.accent};outline-offset:4px;border-radius:8px}
.shx-cls{display:inline-flex;align-items:center;gap:6px;height:32px;padding:0 13px 0 10px;border:none;border-radius:7px;font-family:inherit;font-size:11.5px;font-weight:800;letter-spacing:.4px;text-transform:uppercase;cursor:pointer;transition:background .12s,color .12s,box-shadow .12s}
.shx-cls.on{background:var(--cls);color:#fff;box-shadow:0 1px 3px rgba(15,23,42,.2)}
.shx-cls.off{background:#e9eef6;color:${C.textMid}}
.shx-cls.off:hover{background:#dde5f0;color:${C.text}}
.shx-cls:focus-visible{outline:none;box-shadow:0 0 0 3px var(--lk-action-ring,rgba(8,56,96,.28))}
.shx-tt-foot{display:flex;align-items:center;flex-wrap:wrap;gap:10px 14px;padding:12px 16px 12px 20px;background:#fbfcff;border-top:1px dashed ${C.border}}
.shx-stub{display:inline-flex;align-items:stretch;max-width:100%;min-width:0;background:#fff;border:1px solid ${C.border};border-radius:9px;overflow:hidden;box-shadow:0 1px 3px rgba(15,23,42,.06)}
.shx-stub-qr{width:38px;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:${C.header};color:#fff}
.shx-stub-main{display:flex;flex-direction:column;justify-content:center;min-width:0;padding:6px 14px 6px 11px}
.shx-stub-main b{max-width:190px;font-size:12px;font-weight:800;color:${C.text};white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.shx-stub-main b.is-empty{color:${C.textLight}}
.shx-stub-main small{font-size:9.5px;font-weight:800;letter-spacing:.6px;text-transform:uppercase;color:${C.textLight}}
.shx-stub-price{position:relative;display:flex;align-items:center;padding:0 14px;border-left:1.5px dashed ${C.border};font-size:12.5px;font-weight:800;color:${C.green};white-space:nowrap;font-variant-numeric:tabular-nums}
.shx-stub-price.is-empty{color:${C.textLight}}
.shx-stub-price::before,.shx-stub-price::after{content:"";position:absolute;left:-7px;width:12px;height:12px;border-radius:50%;background:#fbfcff;border:1px solid ${C.border}}
.shx-stub-price::before{top:-7px}
.shx-stub-price::after{bottom:-7px}
.shx-tt-foot-text{flex:1 1 220px;font-size:12px;line-height:1.5;color:${C.textMid}}
.shx-tt-foot-text strong{color:${C.text};white-space:nowrap}
.shx-cover{border-radius:10px;padding:10px 14px;margin-top:4px;font-size:12px;font-weight:600;line-height:1.5}
.shx-cover.ok{display:flex;align-items:center;gap:8px;background:${C.greenSoft};border:1px solid #c8e6c9;color:#2e7d32}
.shx-cover.warn{background:${C.orangeSoft};border:1px solid #f5d78e;color:#8a5a00}
`;
// Run `worker` over `items`, at most `concurrency` at a time, and report after
// every item the SERVER has answered. Progress used to be set before each call
// ("Removing 13 of 31"), which counted the request in flight, not the work
// done; now the numbers only move when a response lands. Never rejects: each
// result is { ok, value } or { ok: false, error }, in input order.
const runEach = async (items, worker, { concurrency = 1, onProgress } = {}) => {
  const results = new Array(items.length);
  let next = 0;
  let ok = 0;
  let failed = 0;
  if (onProgress) onProgress({ ok, failed });
  const lane = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      try {
        results[i] = { ok: true, value: await worker(items[i], i) };
        ok += 1;
      } catch (error) {
        results[i] = { ok: false, error };
        failed += 1;
      }
      if (onProgress) onProgress({ ok, failed });
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, items.length)) }, lane));
  return results;
};
// "12 of 31 removed, 1 failed" with a bar. Counts come from runEach, so they are
// what the server confirmed. `block` fills its container (inside a dialog).
const BulkProgress = ({ progress, block = false }) => {
  if (!progress || !(progress.total > 1)) return null;
  const { ok = 0, failed = 0, total, past = "done" } = progress;
  const pct = Math.min(100, Math.round(((ok + failed) / total) * 100));
  return (
    <span role="status" aria-live="polite" style={{ display: block ? "flex" : "inline-flex", flexDirection: "column", gap: 5, minWidth: block ? 0 : 150, width: block ? "100%" : undefined }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: C.text, fontVariantNumeric: "tabular-nums" }}>
        {ok} of {total} {past}
        {failed > 0 && <span style={{ color: "#b42318" }}>, {failed} failed</span>}
        {ok + failed < total && <span style={{ color: C.textLight, fontWeight: 600 }}> · {total - ok - failed} to go</span>}
      </span>
      <span style={{ height: 6, borderRadius: 4, background: "#e6ebf2", overflow: "hidden" }}>
        <span style={{ display: "block", height: "100%", width: `${pct}%`, background: failed > 0 ? "#d97706" : C.green, transition: "width .25s" }} />
      </span>
    </span>
  );
};
const splitClasses = (s) => String(s || "").split(",").map((c) => c.trim()).filter(Boolean);
const shortWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isFinite(d.getTime())
    ? d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : "";
};

// ── One showtime per slot ─────────────────────────────────────────────────────
// A slot is one venue, one hall and one start time within the event on screen.
// The hall is the layout for reserved seating, otherwise the named space.
// Mirrors services/ticketing/showingSlot.js, which refuses the same duplicates
// server-side; checking here lets the read-back say so before any call.
//
// 15 Oct 2026: every successful save cleared the form to "Start time missing",
// which read as a failure, so the same week was saved again and again. 27
// slots on Screen MAX ended up with two to four copies, and publishing them
// all would have sold each of those 96 seats more than once.
const showingSlotKey = (s) => {
  const start = new Date(s?.startsAt).getTime();
  if (!Number.isFinite(start)) return null;
  const id = (v) => String(v?._id || v || "");
  return [id(s.venueId), id(s.seatMapId) || "space:" + normClass(s.presentation?.screenName), start].join("|");
};
// Copies of one slot: the live copy is kept if there is one, otherwise the
// first one saved. Everything else in the group that is still a draft is
// surplus and safe to delete (it was never on sale).
const splitDuplicateDrafts = (list) => {
  const groups = new Map();
  list.forEach((s) => {
    if (s.status === "CANCELLED") return;
    const k = showingSlotKey(s);
    if (!k) return;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(s);
  });
  const surplus = [];
  let slots = 0;
  groups.forEach((group) => {
    if (group.length < 2) return;
    slots += 1;
    const ordered = [...group].sort((a, b) =>
      (b.status === "PUBLISHED") - (a.status === "PUBLISHED") ||
      new Date(a.createdAt || 0) - new Date(b.createdAt || 0) ||
      String(a._id).localeCompare(String(b._id)));
    ordered.slice(1).forEach((s) => { if (s.status !== "PUBLISHED") surplus.push(s); });
  });
  return { surplus, slots };
};
// "Fri, 16 Oct: 15:30, 18:45, 19:00" for a day or two; a long run reads as its
// range ("Fri, 16 Oct to Sat, 24 Oct: 27 showtimes") instead of a wall of times.
const listTimes = (items) => {
  const day = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  const time = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const sorted = [...items].sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
  const days = [];
  sorted.forEach((m) => {
    const d = day(m.startsAt);
    if (!days.length || days[days.length - 1].d !== d) days.push({ d, times: [] });
    days[days.length - 1].times.push(time(m.startsAt));
  });
  if (days.length <= 2) return days.map((g) => `${g.d}: ${g.times.join(", ")}`).join("; ");
  return `${days[0].d} to ${days[days.length - 1].d}: ${sorted.length} showtimes`;
};
// What the last "create" actually did. Before this, a successful save cleared
// the form and the preview went amber ("Start time missing"), so success looked
// exactly like an empty form; the toast that said otherwise was gone in seconds.
const BatchResult = ({ batch, canPublish, publishing, onPublish, onDismiss }) => {
  if (!batch) return null;
  const drafts = batch.made.filter((m) => !m.live);
  const live = batch.made.filter((m) => m.live);
  const nothing = batch.made.length === 0;
  const tone = nothing ? { bg: "#fee4e2", border: "#f5c2bd", fg: "#b42318" } : { bg: C.greenSoft, border: "#c8e6c9", fg: "#2e7d32" };
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  return (
    <div role="status" style={{ background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: 10, padding: "12px 14px", fontSize: 12, lineHeight: 1.5, color: C.text }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: tone.fg, flex: 1 }}>
          {nothing ? "Nothing was created"
            : drafts.length === 0 ? `${plural(live.length, "showtime is", "showtimes are")} on sale`
              : live.length ? `${live.length} on sale, ${plural(drafts.length, "draft", "drafts")} saved`
                : `${plural(drafts.length, "draft showtime", "draft showtimes")} saved`}
        </span>
        <AppButton variant="quiet" size="sm" icon="fa fa-times" aria-label="Dismiss" title="Dismiss" onClick={onDismiss} />
      </div>
      {!nothing && <div style={{ fontWeight: 700, marginTop: 2 }}>{listTimes(batch.made)}</div>}
      {!nothing && drafts.length === 0 && <div style={{ color: C.textMid, marginTop: 4 }}>Customers can book {live.length === 1 ? "it" : "them"} in the app now.</div>}
      {drafts.length > 0 && <div style={{ color: "#8a5a00", fontWeight: 700, marginTop: 4 }}>Drafts are hidden from customers until they are put on sale.</div>}
      {batch.skippedExisting > 0 && <div style={{ color: C.textMid, marginTop: 4 }}>{plural(batch.skippedExisting, "time already existed and was", "times already existed and were")} skipped.</div>}
      {batch.skippedPast > 0 && <div style={{ color: C.textMid, marginTop: 4 }}>{plural(batch.skippedPast, "time had", "times had")} already passed and {batch.skippedPast === 1 ? "was" : "were"} skipped.</div>}
      {batch.failed.length > 0 && <div style={{ color: "#b42318", fontWeight: 700, marginTop: 4 }}>Not created: {batch.failed.join("; ")}</div>}
      {batch.publishFailed.length > 0 && <div style={{ color: "#b42318", fontWeight: 700, marginTop: 4 }}>Saved but not put on sale: {batch.publishFailed.join("; ")}</div>}
      {drafts.length > 0 && (canPublish
        ? <AppButton icon="fa fa-ticket-alt" loading={publishing} onClick={() => onPublish(drafts.map((m) => m.id))} style={{ marginTop: 10, width: "100%", justifyContent: "center" }}>
            Put {drafts.length === 1 ? "it" : `these ${drafts.length}`} on sale
          </AppButton>
        : <div style={{ color: "#8a5a00", marginTop: 6 }}>Sales open once Lusuku approves this organizer.</div>)}
    </div>
  );
};

// ── Step 4: one ticket type card ─────────────────────────────────────────────
// Whole-shilling amounts shown with thousands separators. The form keeps the
// raw digits ("30000") so validation and the save payload are unchanged; only
// what the operator reads is grouped ("30,000"). The caret is put back by
// counting digits, so typing inside a grouped number does not jump to the end.
const groupDigits = (raw) => {
  const [whole, frac] = String(raw ?? "").split(".");
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (frac !== undefined ? "." + frac : "");
};
const GroupedNumberInput = ({ id, value, onChange, prefix, invalid, placeholder, maxDigits = 12 }) => {
  const ref = useRef(null);
  const caret = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || caret.current === null || document.activeElement !== el) return;
    let pos = 0;
    for (let seen = 0; pos < el.value.length && seen < caret.current; pos += 1) if (/\d/.test(el.value[pos])) seen += 1;
    el.setSelectionRange(pos, pos);
    caret.current = null;
  });
  return (
    <div className={"shx-adorn" + (invalid ? " is-err" : "")}>
      {prefix && <span className="shx-adorn-pre">{prefix}</span>}
      <input ref={ref} id={id} type="text" inputMode="numeric" autoComplete="off" placeholder={placeholder} value={groupDigits(value)}
        onChange={(e) => {
          const el = e.target;
          caret.current = el.value.slice(0, el.selectionStart ?? el.value.length).replace(/\D/g, "").length;
          onChange(el.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, maxDigits));
        }} />
    </div>
  );
};
const TT_ICONS = {
  minus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M5 12h14" /></svg>,
  plus: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  check: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>,
  qr: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.2" /><rect x="14" y="3" width="7" height="7" rx="1.2" /><rect x="3" y="14" width="7" height="7" rx="1.2" /><path d="M14 14h3v3h-3zM20 14v.01M20 20h.01M14 20h.01M17 20h.01" /></svg>,
};
// Guests let in per QR code, 1 to 100 (the same bounds createShowing checks).
const GuestStepper = ({ id, value, onChange, invalid }) => {
  const n = Number(value);
  const cur = Number.isInteger(n) && n >= 1 ? n : 1;
  const step = (d) => onChange(String(Math.min(100, Math.max(1, cur + d))));
  return (
    <div className={"shx-step" + (invalid ? " is-err" : "")}>
      <button type="button" aria-label="Fewer guests" disabled={cur <= 1} onClick={() => step(-1)}>{TT_ICONS.minus}</button>
      <input id={id} type="number" min="1" max="100" value={value} onChange={(e) => onChange(e.target.value)} />
      <button type="button" aria-label="More guests" disabled={cur >= 100} onClick={() => step(1)}>{TT_ICONS.plus}</button>
    </div>
  );
};
const UNIT_SUGGESTIONS = ["ticket", "seat", "table", "booth", "box", "family pass", "package"];
// One price customers can pick. The header repeats what the operator typed
// (name, seat classes, price) so a collapsed glance reads the whole tier, and
// the footer previews the QR ticket a customer receives. Field ids keep the
// "tt-<i>-<field>" shape createShowing uses to flag and focus a bad field.
const TicketTypeCard = ({ index: i, t, reserved, mapClasses, errorField, canRemove, onName, onField, onRemove }) => {
  const color = TT_COLORS[i % TT_COLORS.length];
  const fid = (f) => "tt-" + i + "-" + f;
  const bad = (f) => errorField === fid(f);
  const unit = String(t.unitLabel || "").trim() || "ticket";
  const guests = Number.isInteger(Number(t.admissionsPerUnit)) && Number(t.admissionsPerUnit) >= 1 ? Number(t.admissionsPerUnit) : 1;
  const priced = t.price !== "";
  const picked = splitClasses(t.seatClasses);
  const isPicked = (cl) => picked.some((tok) => normClass(tok) === normClass(cl));
  const toggleClass = (cl) => onField("seatClasses", (isPicked(cl) ? picked.filter((tok) => normClass(tok) !== normClass(cl)) : [...picked, cl]).join(", "));
  const keyFollowsName = Boolean(t.key) && t.key === autoKey(t.name);
  const errBorder = (f) => (bad(f) ? { borderColor: C.accent } : {});
  return (
    <div className="shx-tt" style={{ "--tt": color, "--tt-soft": color + "14" }}>
      <div className="shx-tt-head">
        <span className="shx-tt-num">{i + 1}</span>
        <div className="shx-tt-title">
          <div className={"shx-tt-name" + (t.name ? "" : " is-empty")}>{t.name || "New ticket type"}</div>
          <div className="shx-tt-meta">
            {reserved
              ? (picked.length
                ? picked.map((cl) => <span key={cl}><i style={{ background: classColor(mapClasses, cl)[0] }} />{cl}</span>)
                : (mapClasses.length ? <span className="warn">No seat class picked</span> : null))
              : (t.gaCapacity !== "" ? <span>{groupDigits(t.gaCapacity)} on sale</span> : <span className="warn">Capacity not set</span>)}
            {guests > 1 && <span>Admits {guests}</span>}
          </div>
        </div>
        <div className="shx-tt-price">
          {priced ? <><strong>{money(t.price)}</strong><span>per {unit}</span></> : <span className="missing">Price not set</span>}
        </div>
        {canRemove && <AppButton variant="danger" size="sm" icon="fa fa-trash" onClick={onRemove} title={"Remove " + (t.name || "this ticket type")}>Remove</AppButton>}
      </div>

      <div className="shx-tt-body">
        <div className="shx-tt-r1">
          <div className="f-name">
            <Field label="Name customers see *" hint="Shown on the booking screen and on the ticket.">
              <PInput id={fid("name")} placeholder="e.g. VIP seat" value={t.name} style={errBorder("name")} onChange={(e) => onName(e.target.value)} />
            </Field>
          </div>
          <div className="f-price">
            <Field label="Price *" hint={"Charged once per " + unit + "."}>
              <GroupedNumberInput id={fid("price")} prefix="UGX" placeholder="e.g. 30,000" value={t.price} invalid={bad("price")} onChange={(v) => onField("price", v)} />
            </Field>
          </div>
        </div>
        <div className="shx-tt-r2">
          <Field label="Guests admitted" hint="People let in per QR code.">
            <GuestStepper id={fid("adm")} value={t.admissionsPerUnit} invalid={bad("adm")} onChange={(v) => onField("admissionsPerUnit", v)} />
          </Field>
          <Field label="Sold as" hint="What one purchase is called.">
            <PInput list={fid("units")} value={t.unitLabel} placeholder="ticket" onChange={(e) => onField("unitLabel", e.target.value)} />
            <datalist id={fid("units")}>{UNIT_SUGGESTIONS.map((u) => <option key={u} value={u} />)}</datalist>
          </Field>
          <Field label="Internal code *" hint={keyFollowsName ? "Follows the name. Never shown to customers." : "Never shown to customers."}>
            <PInput id={fid("key")} className="shx-mono" placeholder="e.g. VIP" maxLength={24} value={t.key}
              style={{ fontSize: 12.5, ...errBorder("key") }}
              onChange={(e) => onField("key", e.target.value.toUpperCase())} />
          </Field>
        </div>
        <div className="shx-tt-sec">
          {reserved
            ? <Field label="Seat classes this price books *" hint={mapClasses.length ? "Tap a class to add or remove it. Every class in the layout needs at least one ticket type." : "Pick a layout in step 3 first. Its seat classes will appear here to tap."}>
                {mapClasses.length === 0
                  ? <PInput id={fid("classes")} placeholder="e.g. STANDARD" value={t.seatClasses} style={errBorder("classes")} onChange={(e) => onField("seatClasses", e.target.value)} />
                  : <div id={fid("classes")} className={"shx-cls-row" + (bad("classes") ? " is-err" : "")}>
                      {mapClasses.map((cl) => {
                        const on = isPicked(cl);
                        return (
                          <button key={cl} type="button" aria-pressed={on} className={"shx-cls " + (on ? "on" : "off")} style={{ "--cls": classColor(mapClasses, cl)[0] }} onClick={() => toggleClass(cl)}>
                            {on ? TT_ICONS.check : TT_ICONS.plus}{cl}
                          </button>
                        );
                      })}
                    </div>}
              </Field>
            : <div className="f-cap">
                <Field label="Tickets on sale *" hint="How many of this type can be sold in total.">
                  <GroupedNumberInput id={fid("cap")} placeholder="e.g. 200" value={t.gaCapacity} invalid={bad("cap")} maxDigits={7} onChange={(v) => onField("gaCapacity", v)} />
                </Field>
              </div>}
        </div>
      </div>

      <div className="shx-tt-foot">
        <div className="shx-stub" aria-hidden="true">
          <span className="shx-stub-qr">{TT_ICONS.qr}</span>
          <span className="shx-stub-main">
            <b className={t.name ? "" : "is-empty"}>{t.name || "Ticket name"}</b>
            <small>Admits {guests}</small>
          </span>
          <span className={"shx-stub-price" + (priced ? "" : " is-empty")}>{priced ? money(t.price) : "No price"}</span>
        </div>
        <div className="shx-tt-foot-text">
          {priced
            ? <>Customers buy one {unit} at <strong>{money(t.price)}</strong>. Each QR code admits {guests} guest{guests === 1 ? "" : "s"}.</>
            : <>Set a price to see what customers pay for one {unit}.</>}
        </div>
      </div>
    </div>
  );
};

const Status = ({ s, startsAt }) => {
  // Cancelled used to render as "Draft" — indistinguishable from a showing that
  // was simply never published, which now matters because Draft rows offer
  // Publish and a cancelled showing can never be published again.
  if (s === "CANCELLED") return <span style={S.badge("#b42318", "#fee4e2")}>Cancelled</span>;
  if (s === "COMING_SOON") return <span style={S.badge(C.blue, C.blueSoft)}>Coming soon</span>;
  if (s !== "PUBLISHED") return <span style={S.badge(C.textMid, "#eef1f5")}>Draft</span>;
  // A published showing whose start time has passed no longer sells (the API
  // rejects holds with SHOWING_STARTED) — label it honestly instead of "Live".
  if (startsAt && new Date(startsAt).getTime() < Date.now()) {
    return <span style={S.badge(C.textMid, "#eef1f5")}>Ended</span>;
  }
  return <span style={S.badge(C.green, C.greenSoft)}>Live</span>;
};

// Refund lifecycle chip — same badge styling as the showing Status chip.
const refundChip = (st) =>
  st === "PROCESSED" ? S.badge(C.green, C.greenSoft)
  : st === "APPROVED" ? S.badge(C.blue, C.blueSoft)
  : st === "REJECTED" ? S.badge(C.textMid, "#eef1f5")
  : S.badge("#b26a00", "#fff8e1");

// Event-artwork "studio" — each upload sits in its own white card with a
// name, a shape chip and a where-it-shows caption, inside one soft tray.
const ART = {
  tray: { background: "#f8fafc", border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 14px 6px", marginBottom: 14 },
  trayHead: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 },
  trayTitle: { fontSize: 12.5, fontWeight: 800, color: C.text },
  trayNote: { fontSize: 11, color: C.textLight, marginLeft: "auto" },
  card: { background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", width: "100%", display: "flex", flexDirection: "column", boxShadow: "0 1px 4px rgba(15,23,42,.05)", marginBottom: 8 },
  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 },
  cardTitle: { fontSize: 12, fontWeight: 800, letterSpacing: ".4px", textTransform: "uppercase", color: C.text },
  cardFoot: { fontSize: 11, color: C.textLight, marginTop: "auto", paddingTop: 8, lineHeight: 1.45 },
};

// A clearly-visible loading indicator (SVG SMIL spin — no dependency on Bootstrap CSS).
const Loader = ({ label }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 56 }}>
    <svg width="42" height="42" viewBox="0 0 44 44" fill="none" role="status" aria-label="Loading">
      <circle cx="22" cy="22" r="18" stroke="#e2e8f0" strokeWidth="4" />
      <path d="M22 4 A18 18 0 0 1 40 22" stroke={C.green} strokeWidth="4" strokeLinecap="round">
        <animateTransform attributeName="transform" type="rotate" from="0 22 22" to="360 22 22" dur="0.75s" repeatCount="indefinite" />
      </path>
    </svg>
    {label && <div style={{ fontSize: 13, color: C.textLight, fontWeight: 600 }}>{label}</div>}
  </div>
);

const TicketingConsole = () => {
  const [organizers, setOrganizers] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("events");

  const [venues, setVenues] = useState([]);
  const [seatMaps, setSeatMaps] = useState([]);
  const [events, setEvents] = useState([]);
  const [sales, setSales] = useState(null);
  const [eventId, setEventId] = useState("");
  const [showings, setShowings] = useState([]);

  const [orgF, setOrgF] = useState({ name: "", category: "CINEMA", commissionRate: "", logoUrl: "", contactEmail: "", contactPhone: "" });
  const [venueF, setVenueF] = useState({ name: "", address: "", latitude: null, longitude: null });
  const [smF, setSmF] = useState({ name: "", venueId: "", rows: 8, cols: 12, seatClass: "STANDARD" });
  const blankEventForm = () => ({
    title: "", category: "CINEMA", contentRating: "", durationMinutes: "", posterUrl: "", bannerUrl: "",
    synopsis: "", trailerUrl: "", director: "", cast: "", genres: "", language: "English",
    subtitleLanguage: "", releaseDate: "", studio: "", status: "DRAFT",
  });
  const [evF, setEvF] = useState(blankEventForm);
  const [editEvF, setEditEvF] = useState(blankEventForm);
  // Set while the "Add a showing" form is being reused to EDIT an existing
  // showtime. Held as an id, not an object: the socket listener refetches
  // `showings` wholesale every 350ms, so an object reference would go stale.
  const [editShowingId, setEditShowingId] = useState(null);
  // Baselines for the two panel forms. null = not currently seeded, so a
  // never-opened form is never reported dirty.
  const orgBaselineRef = useRef(null);
  const shBaselineRef = useRef(null);
  const [selectedShowings, setSelectedShowings] = useState([]);
  // Bulk create and bulk publish are sequential HTTP loops (publish also
  // materializes one row per seat), so 12 showtimes is ~30s and 35 is over a
  // minute. A bare spinner for that long reads as "hung" and gets re-clicked.
  const [bulkProgress, setBulkProgress] = useState(null);
  // The outcome of the last create run, shown until dismissed or the film changes.
  const [lastBatch, setLastBatch] = useState(null);
  const [showingFilter, setShowingFilter] = useState("UPCOMING");
  // The flat table is sorted by time, which is not how anyone thinks about a
  // week of cinema. The grid is rows = screen, columns = day — the operator's
  // own mental model, and the same shape the customer sees.
  // "AUTO" until the operator picks. A cinema week is a grid; a promoter's
  // one-night concert is a 1×1 grid, which is worse than a list. Let the shape
  // of the schedule decide rather than assuming everyone runs a film programme.
  const [showingView, setShowingView] = useState("AUTO");
  const [shF, setShF] = useState({
    venueId: "", inventoryModel: "RESERVED_SEATING", inventoryUnitLabel: "Seat", seatMapId: "",
    startsAt: "", doorsAt: "", salesCloseAt: "",
    presentation: { format: "STANDARD", dimension: "2D", sound: "", language: "English", subtitles: "", screenName: "" },
    externalShowingRef: "",
    ticketTypes: [{ key: "STANDARD", name: "Standard", price: "", seatClasses: "STANDARD", gaCapacity: "", admissionsPerUnit: "1", unitLabel: "ticket" }],
  });
  // Bulk create only — kept OUT of shF so entering/leaving edit mode can't
  // drag stray extra times into an overwrite of an existing showing.
  const [bulkF, setBulkF] = useState({ times: "", repeatUntil: "" });
  const [busy, setBusy] = useState("");
  const [acctF, setAcctF] = useState({ email: "", phoneNumber: "", name: "" });
  const [acctResult, setAcctResult] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [staffF, setStaffF] = useState({ email: "", phoneNumber: "", name: "" });
  const [staffResult, setStaffResult] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [procRefF, setProcRefF] = useState({});
  const [shErr, setShErr] = useState(null);
  const [showNewOrg, setShowNewOrg] = useState(false);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmNote, setConfirmNote] = useState("");
  const [showEditOrg, setShowEditOrg] = useState(false);
  const [editOrgF, setEditOrgF] = useState({
    name: "", category: "CINEMA", commissionRate: "", contactEmail: "", contactPhone: "", description: "",
    primaryColor: "#83B524", secondaryColor: "#0D1A2F",
    customerPolicies: { ticketPurchase: "", admission: "", showtime: "", refunds: "", venueRules: "", recording: "", foodAndDrinks: "" },
    fulfillmentProvider: "LUSUKU_NATIVE", integrationBaseUrl: "", integrationApiKeyEnv: "", integrationHmacSecretEnv: "",
  });
  const [onboarding, setOnboarding] = useState(null);
  const [onboardingAttested, setOnboardingAttested] = useState(false);
  const [onboardingReviewNote, setOnboardingReviewNote] = useState("");
  const isSuper = (() => {
    try {
      const user = getUserFromLocalStorage()?.result || {};
      const role = String(user.user_role || user.role || "").toUpperCase();
      return user.isSuperAdmin === true || ["SUPERUSER", "SUPERADMIN"].includes(role);
    } catch (e) { return false; }
  })();

  // `silent` is used by the socket handler. Without it every broadcast — from
  // ANY organizer, since the server emits to one global "ticketing" room —
  // flipped `loading`, and the top-level render gate swapped the whole console
  // for a full-page spinner. React tore down every open form mid-typing.
  const loadOrganizers = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await apiTicketingMyOrganizers();
      setOrganizers(data.organizers || []);
      if (!orgId && data.organizers?.length) setOrgId(data.organizers[0]._id);
    } catch (e) { if (!silent) toast.error(errMsg(e, "Could not load organizers.")); }
    finally { if (!silent) setLoading(false); }
  }, [orgId]);
  useEffect(() => { loadOrganizers(); }, []);

  const loadOrg = useCallback(async (id) => {
    if (!id) return;
    try {
      const [v, s, e, sl, ob] = await Promise.all([
        apiTicketingVenues(id), apiTicketingSeatMaps(id), apiTicketingEvents(id), apiTicketingSales(id),
        apiTicketingGetOnboarding(id),
      ]);
      setVenues(v.data.venues || []);
      setSeatMaps(s.data.seatMaps || []);
      setEvents(e.data.events || []);
      setSales(sl.data || null);
      setOnboarding(ob.data.onboarding || null);
    } catch (err) { toast.error(errMsg(err, "Could not load organizer data.")); }
  }, []);
  useEffect(() => { setEventId(""); setShowings([]); setStaffResult(null); setOnboardingAttested(false); setOnboardingReviewNote(""); if (orgId) { loadOrg(orgId); loadStaff(orgId); loadRefunds(orgId); } else { setStaffList([]); setRefunds([]); setOnboarding(null); } }, [orgId, loadOrg]);

  const loadShowings = useCallback(async (evId) => {
    if (!evId) { setShowings([]); return; }
    try { const { data } = await apiTicketingShowings(evId); setShowings(data.showings || []); }
    catch (e) { toast.error(errMsg(e, "Could not load showings.")); }
  }, []);
  useEffect(() => { loadShowings(eventId); }, [eventId, loadShowings]);
  // Leaving an event must also leave edit mode. Without this the form and its
  // "Editing an existing showtime" banner kept pointing at a showtime from the
  // PREVIOUS film, so "Save changes" silently overwrote a showtime that was no
  // longer on screen.
  useEffect(() => {
    setEditShowingId(null);
    setShErr(null);
    shBaselineRef.current = null;
    setSelectedShowings([]);
    setLastBatch(null);
  }, [eventId, orgId]);
  // Seed the movie-details form from the SERVER doc — but only when it is
  // genuinely a different document.
  //
  // This effect used to run on every refetch, because `loadOrg` assigns a new
  // array to `events` each time even when nothing changed. Publishing a
  // showtime, updating artwork, or another organizer's socket broadcast would
  // therefore wipe whatever was being typed here, with no warning. Now the
  // seed is keyed on the document's identity+version, and a dirty form is
  // never overwritten — the operator is told the server copy moved instead.
  const evSeedRef = useRef(null);      // "<id>:<updatedAt>" of the seeded doc
  const evBaselineRef = useRef(null);  // snapshot of the form as seeded
  const evDirtyRef = useRef(false);    // kept in sync during render, read by the effect
  const [evServerMoved, setEvServerMoved] = useState(false);
  const seedEditEv = useCallback((event) => {
    const next = {
      ...blankEventForm(),
      ...event,
      cast: (event.cast || []).join(", "),
      genres: (event.genres || []).join(", "),
      releaseDate: event.releaseDate ? String(event.releaseDate).slice(0, 10) : "",
    };
    setEditEvF(next);
    evBaselineRef.current = snapshot(next);
    evDirtyRef.current = false;
    setEvServerMoved(false);
  }, []);
  useEffect(() => {
    const event = events.find((item) => item._id === eventId);
    if (!event) { evSeedRef.current = null; return; }
    const stamp = `${event._id}:${event.updatedAt || ""}`;
    if (evSeedRef.current === stamp) return;           // same doc, same version
    const switchingEvent = evSeedRef.current === null || !evSeedRef.current.startsWith(`${event._id}:`);
    if (evDirtyRef.current && !switchingEvent) { setEvServerMoved(true); return; }
    evSeedRef.current = stamp;
    seedEditEv(event);
  }, [eventId, events, seedEditEv]);

  // One guard for the whole console: warn before a reload/close throws away
  // anything unsaved. `anyDirty` is recomputed each render below.
  const anyDirtyRef = useRef(false);
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!anyDirtyRef.current) return undefined;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // ── realtime: reload when any ticketing change is broadcast ────────────────
  const reloadRef = useRef(() => {});
  reloadRef.current = () => {
    loadOrganizers({ silent: true });
    if (orgId) loadOrg(orgId);
    if (eventId) loadShowings(eventId);
  };
  // Realtime. The server broadcasts to a global "ticketing" room AND to a
  // per-organizer room; we listen on both but IGNORE traffic belonging to a
  // different organizer. Before this, any other organizer on the platform
  // publishing a showtime made this console refetch three endpoints and visibly
  // reload — on someone else's work.
  const orgIdRef = useRef(orgId);
  // The showing form is seeded by handlers that run before `selectedOrg` is in
  // scope, so the current organizer's category is mirrored into a ref.
  const orgCategoryRef = useRef("");
  const editShowingIdRef = useRef(null);
  const shStartedRef = useRef(false);
  orgIdRef.current = orgId;
  const socketRef = useRef(null);
  useEffect(() => {
    let t = null;
    const socket = io(dashboardApiBaseUrl() || undefined, { transports: ["websocket", "polling"], withCredentials: true });
    const joinRooms = () => {
      socket.emit("join", "ticketing");
      if (orgIdRef.current) socket.emit("join", `ticketing:org:${orgIdRef.current}`);
    };
    socket.on("connect", joinRooms);
    const onUpdate = (msg) => {
      const scope = msg?.organizerId;
      if (scope && orgIdRef.current && String(scope) !== String(orgIdRef.current)) return;
      clearTimeout(t);
      t = setTimeout(() => reloadRef.current(), 350);
    };
    socket.on("ticketing:update", onUpdate);
    socketRef.current = socket;
    return () => { clearTimeout(t); socket.off("connect", joinRooms); socket.off("ticketing:update", onUpdate); socket.disconnect(); socketRef.current = null; };
  }, []);
  // Follow the operator when they switch organizer, without rebuilding the socket.
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !orgId) return undefined;
    socket.emit("join", `ticketing:org:${orgId}`);
    return () => { socket.emit("leave", `ticketing:org:${orgId}`); };
  }, [orgId]);

  const createOrganizer = async (e) => {
    e.preventDefault();
    if (!orgF.name.trim()) { toast.error("Enter the organizer's name."); focusField("org-name"); return; }
    setBusy("org");
    try {
      const { data } = await apiTicketingCreateOrganizer({
        name: orgF.name.trim(), category: orgF.category,
        commissionRate: orgF.commissionRate !== "" ? Number(orgF.commissionRate) : undefined,
        logoUrl: orgF.logoUrl || undefined,
        contactEmail: orgF.contactEmail.trim() || undefined,
        contactPhone: orgF.contactPhone.trim() || undefined,
      });
      toast.success("Organizer created."); setShowNewOrg(false);
      setOrgF({ name: "", category: "CINEMA", commissionRate: "", logoUrl: "", contactEmail: "", contactPhone: "" });
      await loadOrganizers();
      setOrgId(data.organizer._id);
    } catch (err) { toast.error(errMsg(err, "Failed to create organizer.")); }
    finally { setBusy(""); }
  };

  const createVenue = async (e) => {
    e.preventDefault();
    if (!venueF.name.trim()) { toast.error("Enter the venue/outlet name."); focusField("venue-name"); return; }
    // A venue with no coordinates fails the onboarding "venue" check forever,
    // which blocks every publish for the whole organizer. Catch it here rather
    // than let it become an unfixable record.
    if (venueF.latitude == null || venueF.longitude == null) {
      toast.error("Pick the venue's location from the suggestions — a venue without map coordinates can't be approved for sales.");
      focusField("venue-name");
      return;
    }
    setBusy("venue");
    try {
      await apiTicketingCreateVenue({ organizerId: orgId, name: venueF.name.trim(), address: venueF.address, latitude: venueF.latitude, longitude: venueF.longitude });
      toast.success("Venue added.");
      setVenueF({ name: "", address: "", latitude: null, longitude: null });
      loadOrg(orgId);
    } catch (err) { toast.error(errMsg(err, "Failed to add venue.")); }
    finally { setBusy(""); }
  };

  const [editVenue, setEditVenue] = useState(null);
  const startEditVenue = (v) => setEditVenue({
    original: v, name: v.name || "", address: v.address || "",
    latitude: Number.isFinite(v.latitude) ? v.latitude : null,
    longitude: Number.isFinite(v.longitude) ? v.longitude : null,
  });
  const saveEditVenue = async () => {
    if (!editVenue) return;
    if (!editVenue.name.trim()) { toast.error("The venue needs a name."); focusField("edit-venue-name"); return; }
    if (editVenue.latitude == null || editVenue.longitude == null) {
      toast.error("Pick the venue's location from the suggestions so its map coordinates are saved.");
      return;
    }
    setBusy("editvenue");
    try {
      await apiTicketingUpdateVenue(editVenue.original._id, {
        name: editVenue.name.trim(), address: editVenue.address,
        latitude: editVenue.latitude, longitude: editVenue.longitude,
      });
      toast.success("Venue updated.");
      setEditVenue(null);
      loadOrg(orgId);
    } catch (err) { toast.error(errMsg(err, "Could not update the venue.")); }
    finally { setBusy(""); }
  };
  const removeVenue = (v) => setConfirmState({
    title: "Delete venue",
    message: `Delete “${v.name}”? Showtimes that use it must be removed first.`,
    confirmLabel: "Delete venue",
    danger: true,
    onConfirm: async () => {
      try {
        await apiTicketingDeleteVenue(v._id);
        toast.success("Venue deleted.");
        if (editVenue?.original?._id === v._id) setEditVenue(null);
        loadOrg(orgId);
      } catch (err) { toast.error(errMsg(err, "Could not delete the venue.")); }
    },
  });

  const createSeatMap = async (e) => {
    e.preventDefault();
    if (!smF.name.trim()) { toast.error("Give the reserved layout a name."); focusField("sm-name"); return; }
    // SeatMap.venueId is `required` in the model, but this field used to read
    // "\u2014 optional \u2014" and send undefined, so the very first hall a new vendor
    // built failed with a raw Mongoose string ("Path `venueId` is required")
    // they had no way to act on. A hall belongs to exactly one outlet.
    if (!smF.venueId) { toast.error("Choose the venue this layout belongs to \u2014 a hall lives in exactly one outlet."); focusField("sm-venue"); return; }
    if (!(Number(smF.rows) > 0 && Number(smF.cols) > 0)) { toast.error("Rows and units per row must each be at least 1."); return; }
    // Two halls with the same name are indistinguishable in the showing form's
    // layout dropdown \u2014 nothing else is rendered there to tell them apart.
    if (seatMaps.some((m) => normClass(m.name) === normClass(smF.name))) {
      toast.error(`You already have a layout called \u201c${smF.name.trim()}\u201d. Give this one a name you can tell apart in the showtime form, e.g. \u201c${(venues.find((v) => v._id === smF.venueId)?.name || "Venue")} - Screen 2\u201d.`);
      focusField("sm-name");
      return;
    }
    setBusy("sm");
    try {
      await apiTicketingCreateSeatMap({ organizerId: orgId, venueId: smF.venueId, name: smF.name || "Screen", rows: Number(smF.rows), cols: Number(smF.cols), seatClass: smF.seatClass || "STANDARD" });
      toast.success(`Reserved layout created (${Number(smF.rows) * Number(smF.cols)} units).`);
      setSmF({ name: "", venueId: "", rows: 8, cols: 12, seatClass: "STANDARD" });
      loadOrg(orgId);
    } catch (err) { toast.error(errMsg(err, "Failed to create reserved layout.")); }
    finally { setBusy(""); }
  };

  // Layouts were create-only, so a mistyped hall name was permanent and the
  // dropdown could offer two identical options forever. Same shape as the venue
  // row edit above.
  const [editMap, setEditMap] = useState(null);
  const startEditMap = (m) => setEditMap({ original: m, name: m.name || "", venueId: m.venueId ? String(m.venueId) : "" });
  const saveEditMap = async () => {
    if (!editMap) return;
    if (!editMap.name.trim()) { toast.error("The layout needs a name."); focusField("edit-sm-name"); return; }
    if (!editMap.venueId) { toast.error("Choose the venue this layout belongs to."); return; }
    if (seatMaps.some((m) => String(m._id) !== String(editMap.original._id) && normClass(m.name) === normClass(editMap.name))) {
      toast.error(`Another layout is already called \u201c${editMap.name.trim()}\u201d.`); focusField("edit-sm-name"); return;
    }
    setBusy("editsm");
    try {
      await apiTicketingUpdateSeatMap(editMap.original._id, { name: editMap.name.trim(), venueId: editMap.venueId });
      toast.success("Layout updated.");
      setEditMap(null);
      loadOrg(orgId);
    } catch (err) { toast.error(errMsg(err, "Could not update the layout.")); }
    finally { setBusy(""); }
  };
  const removeMap = (m) => setConfirmState({
    title: "Delete reserved layout",
    message: `Delete \u201c${m.name}\u201d? Showtimes that use it must be removed first.`,
    confirmLabel: "Delete layout",
    danger: true,
    onConfirm: async () => {
      try {
        await apiTicketingDeleteSeatMap(m._id);
        toast.success("Layout deleted.");
        if (editMap?.original?._id === m._id) setEditMap(null);
        loadOrg(orgId);
      } catch (err) { toast.error(errMsg(err, "Could not delete the layout.")); }
    },
  });

  const createEvent = async (e) => {
    e.preventDefault();
    if (!evF.title.trim()) { toast.error("Enter the event / film title."); focusField("event-title"); return; }
    setBusy("event");
    try {
      await apiTicketingCreateEvent({
        organizerId: orgId, title: evF.title.trim(), category: evF.category,
        contentRating: evF.contentRating, durationMinutes: evF.durationMinutes !== "" ? Number(evF.durationMinutes) : undefined,
        posterUrl: evF.posterUrl || undefined, bannerUrl: evF.bannerUrl || undefined,
        synopsis: evF.synopsis, trailerUrl: evF.trailerUrl, director: evF.director,
        cast: evF.cast, genres: evF.genres, language: evF.language,
        subtitleLanguage: evF.subtitleLanguage, releaseDate: evF.releaseDate || null,
        studio: evF.studio, status: evF.status,
      });
      toast.success("Event created."); setShowNewEvent(false);
      setEvF(blankEventForm());
      loadOrg(orgId);
    } catch (err) { toast.error(errMsg(err, "Failed to create event.")); }
    finally { setBusy(""); }
  };

  const removeEvent = (ev) => setConfirmState({
    title: "Delete event",
    message: "Delete \u201C" + (ev.title || "this event") + "\u201D and all of its showings? This can\u2019t be undone.",
    confirmLabel: "Delete event",
    danger: true,
    onConfirm: async () => {
      try {
        await apiTicketingDeleteEvent(ev._id);
        toast.success("Event deleted.");
        if (eventId === ev._id) setEventId("");
        loadOrg(orgId);
      } catch (err) { toast.error(errMsg(err, "Could not delete the event.")); }
    },
  });

  const setTT = (i, k, val) => setShF((f) => ({ ...f, ticketTypes: f.ticketTypes.map((t, idx) => (idx === i ? { ...t, [k]: val } : t)) }));
  const addTT = () => setShF((f) => ({ ...f, ticketTypes: [...f.ticketTypes, { key: "", name: "", price: "", seatClasses: "", gaCapacity: "", admissionsPerUnit: "1", unitLabel: "ticket" }] }));
  const removeTT = (i) => setShF((f) => ({ ...f, ticketTypes: f.ticketTypes.filter((_, idx) => idx !== i) }));
  // Name is the field operators think in; the internal code shadows it until
  // it has been edited by hand (then it's theirs).
  const setTTName = (i, val) => setShF((f) => ({
    ...f,
    ticketTypes: f.ticketTypes.map((t, idx) => {
      if (idx !== i) return t;
      const keyFollows = !t.key || t.key === autoKey(t.name);
      return { ...t, name: val, key: keyFollows ? autoKey(val) : t.key };
    }),
  }));
  // Same transition the old <select> ran — kept as one function for the four
  // sales-model cards.
  const pickInventoryModel = (inventoryModel) => {
    const unitLabel = inventoryModel === "RESERVED_UNITS" ? "table" : "ticket";
    setShErr(null);
    setShF((f) => ({
      ...f, inventoryModel,
      inventoryUnitLabel: inventoryModel === "RESERVED_SEATING" ? "Seat" : inventoryModel === "RESERVED_UNITS" ? "Table" : "",
      ticketTypes: f.ticketTypes.map((t) => ({ ...t, unitLabel })),
    }));
  };

  // Empty form, used when leaving edit mode. Note createShowing's own reset is
  // deliberately PARTIAL (it keeps venue/seat map/ticket types so an operator
  // can enter a run of showtimes); leaving an edit must clear everything, or
  // the edited showing's ticket types silently become the next create's
  // defaults.

  // Load an existing showing into the form. The inverse of the submit-time
  // transform: the API returns seatClasses as an array and price/gaCapacity as
  // numbers, while the form holds a comma-joined string and strings.
  // DateTimeField takes an ISO string directly, so the dates need no coercion.
  // Shared by "edit this showtime" and the week grid's "+" (which copies a
  // row's setup onto another day). Kept as one function so the two can't drift.
  const showingFormFrom = (sh) => ({
      venueId: sh.venueId ? String(sh.venueId._id || sh.venueId) : "",
      inventoryModel: sh.inventoryModel || "RESERVED_SEATING",
      inventoryUnitLabel: sh.inventoryUnitLabel || (sh.inventoryModel === "RESERVED_SEATING" ? "Seat" : "Reserved unit"),
      seatMapId: sh.seatMapId ? String(sh.seatMapId._id || sh.seatMapId) : "",
      startsAt: sh.startsAt || "",
      doorsAt: sh.doorsAt || "",
      salesCloseAt: sh.salesCloseAt || "",
      presentation: { ...blankShowingForm(orgCategoryRef.current).presentation, ...(sh.presentation || {}) },
      externalShowingRef: sh.externalShowingRef || "",
      ticketTypes: (sh.ticketTypes || []).length
        ? sh.ticketTypes.map((t) => ({
            key: t.key || "",
            name: t.name || "",
            price: t.price == null ? "" : String(t.price),
            seatClasses: (t.seatClasses || []).join(", "),
            gaCapacity: t.gaCapacity == null ? "" : String(t.gaCapacity),
            admissionsPerUnit: String(t.admissionsPerUnit || 1),
            unitLabel: t.unitLabel || "ticket",
          }))
        : blankShowingForm(orgCategoryRef.current).ticketTypes,
  });

  const startEditShowing = (sh) => {
    setShErr(null);
    setEditShowingId(sh._id);
    const nextShowingForm = showingFormFrom(sh);
    setShF(nextShowingForm);
    shBaselineRef.current = snapshot(nextShowingForm);
    focusField("sh-starts");
  };

  // Week grid "+": reuse everything about an existing showtime in that row —
  // venue, layout, experience, prices — and move it to the day that was
  // clicked, keeping its time as the starting suggestion. This is the
  // "same film, same screen, another day" case, which is most of the job.
  const prefillShowing = (template, day) => {
    setShErr(null);
    setEditShowingId(null);
    const base = new Date(template.startsAt);
    const when = new Date(day.getFullYear(), day.getMonth(), day.getDate(), base.getHours(), base.getMinutes());
    const next = { ...showingFormFrom(template), startsAt: when.toISOString(), doorsAt: "", salesCloseAt: "" };
    setShF(next);
    shBaselineRef.current = snapshot(next);
    setBulkF({ times: "", repeatUntil: "" });
    focusField("sh-starts");
  };

  const cancelEditShowing = () => {
    setEditShowingId(null);
    setShErr(null);
    setShF(blankShowingForm(orgCategoryRef.current));
    shBaselineRef.current = null;
    setBulkF({ times: "", repeatUntil: "" });
  };

  // Off sale, back to draft — NOT a cancellation. The server refuses if any
  // ticket has been issued or a checkout is in flight, and says why.
  const unpublish = (sh) => setConfirmState({
    title: "Unpublish showing",
    message:
      "Take the " + (sh.startsAt ? new Date(sh.startsAt).toLocaleString() : "selected") +
      " showing off sale? It goes back to draft and disappears from the customer apps. " +
      "No ticket is refunded and nothing is cancelled — you can publish it again at any time.",
    confirmLabel: "Unpublish",
    onConfirm: async () => {
      try {
        const { data } = await apiTicketingUnpublishShowing(sh._id);
        toast.success(
          data.eventUnpublished
            ? "Showing unpublished — that was the last live showtime, so the event is a draft again."
            : "Showing unpublished — it is a draft again and no longer on sale."
        );
        loadShowings(eventId); loadOrg(orgId);
      } catch (err) { toast.error(errMsg(err, "Could not unpublish the showing.")); }
    },
  });

  // Review-before-create. Submitting the form only VALIDATES (same checks,
  // same toasts, same field focusing as before) and opens a read-it-back
  // dialog; the API is not touched until the operator confirms there. The
  // validated ticket types + expanded slots ride along in `reviewState` so the
  // dialog shows exactly what will be sent.
  const [reviewState, setReviewState] = useState(null);

  const validateShowing = () => {
    setShErr(null);
    const reserved = isReservedModel(shF.inventoryModel);
    const mapClasses = seatMapClasses(seatMaps.find((m) => m._id === shF.seatMapId));
    if (!shF.startsAt) { setShErr("starts"); toast.error("Set when the showing starts."); focusField("sh-starts"); return; }
    if (!shF.venueId) { setShErr("venue"); toast.error("Choose the venue where this showing takes place."); focusField("sh-venue"); return; }
    if (selectedOrg?.fulfillmentProvider === "EXTERNAL_API" && !shF.externalShowingRef.trim()) { toast.error("Enter the cinema provider's external showing reference."); return; }
    if (reserved && !shF.seatMapId) { setShErr("seatmap"); toast.error("Pick a layout for reserved inventory."); focusField("sh-seatmap"); return; }
    for (let i = 0; i < shF.ticketTypes.length; i++) {
      const t = shF.ticketTypes[i];
      if (!(t.key || t.name || t.price !== "")) continue;
      if (!t.name) { setShErr("tt-" + i + "-name"); toast.error("Ticket type " + (i + 1) + " needs the name customers will see (e.g. VIP seat)."); focusField("tt-" + i + "-name"); return; }
      if (!t.key) { setShErr("tt-" + i + "-key"); toast.error("Ticket type " + (i + 1) + " needs an internal code (e.g. STANDARD or VIP)."); focusField("tt-" + i + "-key"); return; }
      if (t.price === "" || !(Number(t.price) >= 0)) { setShErr("tt-" + i + "-price"); toast.error("Ticket type " + (i + 1) + " needs a price per unit."); focusField("tt-" + i + "-price"); return; }
      if (!Number.isInteger(Number(t.admissionsPerUnit)) || Number(t.admissionsPerUnit) < 1 || Number(t.admissionsPerUnit) > 100) { setShErr("tt-" + i + "-adm"); toast.error("Ticket type " + (i + 1) + " must admit between 1 and 100 guests per unit."); focusField("tt-" + i + "-adm"); return; }
      if (!reserved && (!Number.isInteger(Number(t.gaCapacity)) || Number(t.gaCapacity) < 1)) { setShErr("tt-" + i + "-cap"); toast.error("Ticket type " + (i + 1) + " needs a positive whole-number capacity."); focusField("tt-" + i + "-cap"); return; }
      if (reserved && mapClasses.length) {
        const unknown = splitClasses(t.seatClasses).find((tok) => !mapClasses.some((mc) => normClass(mc) === normClass(tok)));
        if (unknown != null) { setShErr("tt-" + i + "-classes"); toast.error("“" + unknown + "” isn't a seat class in this layout — it has: " + mapClasses.join(", ") + ". Use the chips under the field instead of typing."); focusField("tt-" + i + "-classes"); return; }
      }
    }
    const complete = shF.ticketTypes.filter((t) => t.key && t.name && t.price !== "");
    if (!complete.length) { setShErr("tt-0-name"); toast.error("Add at least one ticket type (customer-facing name, price and code)."); focusField("tt-0-name"); return; }
    const ticketTypes = complete.map((t) => {
      const base = {
        key: String(t.key).toUpperCase(), name: t.name, price: Number(t.price),
        admissionsPerUnit: Number(t.admissionsPerUnit || 1),
        unitLabel: String(t.unitLabel || (Number(t.admissionsPerUnit) > 1 ? "package" : "ticket")).trim(),
      };
      if (!reserved) return { ...base, gaCapacity: t.gaCapacity !== "" ? Number(t.gaCapacity) : null };
      // Canonicalize each entered class to the layout map's EXACT spelling
      // (matching is case/spacing-insensitive). Uppercasing here while the
      // coverage check compared raw map strings meant a map class like
      // "Max \u00b7 2D" could NEVER validate \u2014 the error Izi kept hitting.
      let classes = [...new Set(splitClasses(t.seatClasses).map((tok) => mapClasses.find((mc) => normClass(mc) === normClass(tok)) || tok.toUpperCase()))];
      if (!classes.length) classes = mapClasses;
      return { ...base, seatClasses: classes };
    });
    if (reserved && mapClasses.length) {
      const covered = new Set();
      ticketTypes.forEach((t) => (t.seatClasses || []).forEach((cl) => covered.add(normClass(cl))));
      const missing = mapClasses.filter((cl) => !covered.has(normClass(cl)));
      if (missing.length) { setShErr("tt-0-classes"); toast.error("No ticket type covers seat class: " + missing.join(", ") + ". Tap that chip on one of the ticket types below so every seat has a price."); focusField("tt-0-classes"); return; }
    }
    // Bulk expansion (create mode only): the picked start plus every "more
    // times" entry, repeated daily through "repeat until". Validated BEFORE
    // busy is set so an early return can't strand the button in a spinner.
    let slots = null;
    let skippedPast = 0;
    let skippedExisting = 0;
    if (editShowingId && shF.startsAt) {
      const editing = showings.find((s) => String(s._id) === String(editShowingId));
      const nextKey = showingSlotKey({ venueId: shF.venueId, seatMapId: reserved ? shF.seatMapId : null, presentation: shF.presentation, startsAt: shF.startsAt });
      const moved = !editing || showingSlotKey(editing) !== nextKey;
      if (moved && showings.some((s) => String(s._id) !== String(editShowingId) && s.status !== "CANCELLED" && showingSlotKey(s) === nextKey)) {
        setShErr("starts"); toast.error("Another showing already starts at that time on this screen. Pick a different time or screen."); focusField("sh-starts"); return;
      }
    }
    if (!editShowingId) {
      const { times: extraTimes, bad: badTimes } = parseTimeList(bulkF.times);
      if (badTimes.length) { toast.error("Couldn\u2019t read time(s): " + badTimes.join(", ") + ". Use 24h HH:MM or h:mm am/pm."); return; }
      slots = expandSlots(shF.startsAt, extraTimes, bulkF.repeatUntil);
      if (!slots.length) { toast.error("\u201cRepeat daily until\u201d must be on or after the first showtime\u2019s date."); return; }
      // A repeat range that starts today also generates this morning's slots.
      // The server rejects each one (STARTS_IN_PAST) and the operator got
      // "Created 9 of 12" with three failures to decode. Drop them up front.
      skippedPast = slots.filter((d) => d.getTime() <= Date.now()).length;
      if (skippedPast) slots = slots.filter((d) => d.getTime() > Date.now());
      if (!slots.length) { toast.error("Every one of those times has already passed today \u2014 customers could never buy them. Pick a later time, or start the range tomorrow."); return; }
      // A time that already exists on this screen is skipped, not created again.
      const taken = new Set(showings.filter((s) => s.status !== "CANCELLED").map(showingSlotKey));
      const slotOf = (d) => showingSlotKey({ venueId: shF.venueId, seatMapId: reserved ? shF.seatMapId : null, presentation: shF.presentation, startsAt: d });
      skippedExisting = slots.filter((d) => taken.has(slotOf(d))).length;
      if (skippedExisting) slots = slots.filter((d) => !taken.has(slotOf(d)));
      if (!slots.length) { setShErr("starts"); toast.error(skippedExisting === 1 ? "That showtime already exists on this screen. Nothing new to create." : "All " + skippedExisting + " of those showtimes already exist on this screen. Nothing new to create."); focusField("sh-starts"); return; }
      if (slots.length > 62) { toast.error("That would create " + slots.length + " showings \u2014 the per-batch cap is 62. Shorten the repeat range."); return null; }
    }
    return { reserved, ticketTypes, slots, skippedPast, skippedExisting };
  };

  const createShowing = (e) => {
    e.preventDefault();
    const v = validateShowing();
    if (v) setReviewState(v);
  };

  // goLive: publish each showing straight after creating it (the read-back's
  // "Create and put on sale"). Without it the run ends as drafts.
  const submitShowing = async (goLive = false) => {
    const v = reviewState;
    if (!v) return;
    const { reserved, ticketTypes, slots, skippedPast, skippedExisting } = v;
    setBusy("showing");
    const payload = {
      eventId,
      venueId: shF.venueId,
      inventoryModel: shF.inventoryModel,
      inventoryUnitLabel: reserved ? shF.inventoryUnitLabel : undefined,
      seatMapId: reserved ? shF.seatMapId : undefined,
      ticketTypes,
      startsAt: toISO(shF.startsAt),
      doorsAt: toISO(shF.doorsAt),
      salesCloseAt: toISO(shF.salesCloseAt),
      presentation: shF.presentation,
      externalShowingRef: shF.externalShowingRef.trim() || undefined,
    };
    try {
      if (editShowingId) {
        await apiTicketingUpdateShowing(editShowingId, payload);
        toast.success("Showing updated.");
        cancelEditShowing();
      } else {
        // Doors-open and sales-close keep their DISTANCE from the picked start
        // on every generated slot (doors 30 min before 12:15 → 30 min before
        // 18:45 too), rather than being copied verbatim.
        const baseMs = new Date(shF.startsAt).getTime();
        const doorsDelta = shF.doorsAt ? new Date(shF.doorsAt).getTime() - baseMs : null;
        const salesDelta = shF.salesCloseAt ? new Date(shF.salesCloseAt).getTime() - baseMs : null;
        const progressOf = (total, past) => ({ ok, failed }) => setBulkProgress({ ok, failed, total, past });
        // One at a time: the server's duplicate check sees each save before the next.
        const created = await runEach(slots, (d) => apiTicketingCreateShowing({
          ...payload,
          startsAt: d.toISOString(),
          doorsAt: doorsDelta == null ? null : new Date(d.getTime() + doorsDelta).toISOString(),
          salesCloseAt: salesDelta == null ? null : new Date(d.getTime() + salesDelta).toISOString(),
        }), { onProgress: progressOf(slots.length, "created") });
        const made = []; const failed = [];
        created.forEach((r, i) => {
          const iso = slots[i].toISOString();
          if (r.ok) made.push({ id: String(r.value?.data?.showing?._id || ""), startsAt: iso, live: false });
          else failed.push(`${shortWhen(iso)} (${errMsg(r.error, "failed")})`);
        });
        const publishFailed = [];
        if (goLive) {
          const toPublish = made.filter((m) => m.id);
          const opened = await runEach(toPublish, (m) => apiTicketingPublishShowing(m.id), { onProgress: progressOf(toPublish.length, "put on sale") });
          opened.forEach((r, i) => {
            if (r.ok) toPublish[i].live = true;
            else publishFailed.push(`${shortWhen(toPublish[i].startsAt)} (${errMsg(r.error, "failed")})`);
          });
        }
        setLastBatch({ made, failed, publishFailed, skippedExisting: skippedExisting || 0, skippedPast: skippedPast || 0 });
        const liveCount = made.filter((m) => m.live).length;
        if (!made.length) toast.error("Nothing was created: " + failed.join("; "));
        else if (failed.length || publishFailed.length) toast.error(`Created ${made.length} of ${slots.length}${goLive ? `, ${liveCount} on sale` : ""}. See the result panel for what failed.`);
        else if (goLive) toast.success(`${liveCount === 1 ? "Showtime is" : liveCount + " showtimes are"} on sale.`);
        else toast.success(`${made.length === 1 ? "Draft showtime" : made.length + " draft showtimes"} saved. Put ${made.length === 1 ? "it" : "them"} on sale when ready.`);
        // Partial reset on purpose — venue, seat map and ticket types stay so a
        // run of showtimes can be entered back to back.
        setShF((f) => ({ ...f, startsAt: "", doorsAt: "", salesCloseAt: "" }));
        setBulkF({ times: "", repeatUntil: "" });
      }
      loadShowings(eventId);
      if (goLive) loadOrg(orgId);
    } catch (err) {
      toast.error(errMsg(err, editShowingId ? "Failed to update showing." : "Failed to create showing."));
    }
    finally { setBusy(""); setBulkProgress(null); setReviewState(null); }
  };

  // Pre-flight the one rejection the operator can actually act on: a seat class
  // in the layout that no ticket type prices. The server refuses at publish
  // time, and before this the only signal was a red toast on an already-saved
  // showing. Matching is case/spacing-insensitive to agree with the server.
  const uncoveredClasses = (showing) => {
    if (!isReservedModel(showing.inventoryModel)) return [];
    const map = seatMaps.find((m) => String(m._id) === String(showing.seatMapId?._id || showing.seatMapId));
    const classes = seatMapClasses(map);
    if (!classes.length) return [];
    const covered = new Set();
    (showing.ticketTypes || []).forEach((t) => {
      const list = (t.seatClasses && t.seatClasses.length) ? t.seatClasses : (t.key ? [t.key] : []);
      list.forEach((cl) => covered.add(normClass(cl)));
    });
    return classes.filter((cl) => !covered.has(normClass(cl)));
  };

  const publish = async (id) => {
    const showing = showings.find((s) => String(s._id) === String(id));
    if (showing) {
      const missing = uncoveredClasses(showing);
      if (missing.length) {
        toast.error(`This showtime can't go on sale yet — no ticket type prices ${missing.join(", ")}. Click Edit and tap that seat-class chip on one of the ticket types.`);
        return;
      }
    }
    setBusy("pub" + id);
    try {
      const { data } = await apiTicketingPublishShowing(id);
      toast.success(`Published — ${data.materialized?.created ?? 0} reserved units are now live.`);
      loadShowings(eventId); loadOrg(orgId);
    } catch (err) { toast.error(errMsg(err, "Failed to publish.")); }
    finally { setBusy(""); }
  };

  // Bulk publish. Sequential on purpose: each publish materialises seat
  // inventory, and firing a dozen of those at once is a needless spike.
  //
  // Takes the ids explicitly. "Publish all" used to set the selection and then
  // call this on a timeout, but the function it called still held the PREVIOUS
  // render's (empty) selection, so it answered "Select at least one draft
  // showtime" and published nothing.
  const publishIds = async (ids) => {
    if (!ids.length) { toast.error("Select at least one draft showtime."); return []; }
    setBusy("bulkpub");
    const showingOf = (id) => showings.find((s) => String(s._id) === String(id));
    // Sequential on purpose: each publish materialises seat inventory.
    const results = await runEach(ids, (id) => {
      const missing = showingOf(id) ? uncoveredClasses(showingOf(id)) : [];
      if (missing.length) throw new Error(`no price for ${missing.join(", ")}`);
      return apiTicketingPublishShowing(id);
    }, { onProgress: ({ ok, failed }) => setBulkProgress({ ok, failed, total: ids.length, past: "published" }) });
    const publishedIds = ids.filter((_, i) => results[i].ok).map(String);
    const failed = ids.flatMap((id, i) => (results[i].ok ? [] : [
      `${showingOf(id)?.startsAt ? shortWhen(showingOf(id).startsAt) : id}: ${errMsg(results[i].error, results[i].error?.message || "failed")}`,
    ]));
    const published = publishedIds.length;
    if (published) toast.success(`${published} showtime${published === 1 ? "" : "s"} now on sale.`);
    if (failed.length) toast.error(`Couldn't publish ${failed.length}: ${failed.join("; ")}`);
    setSelectedShowings([]);
    loadShowings(eventId); loadOrg(orgId);
    setBusy(""); setBulkProgress(null);
    return publishedIds;
  };
  const publishSelected = () => publishIds(showings.filter((s) => selectedShowings.includes(String(s._id)) && s.status !== "PUBLISHED" && s.status !== "CANCELLED").map((s) => String(s._id)));
  // "Put these on sale" on the result panel: publish, then mark them live there.
  const publishBatch = async (ids) => {
    const publishedIds = await publishIds(ids);
    if (publishedIds.length) setLastBatch((b) => (b ? { ...b, made: b.made.map((m) => (publishedIds.includes(m.id) ? { ...m, live: true } : m)) } : b));
  };
  // Delete the surplus copies of duplicated slots, one at a time. Only drafts
  // are ever in the list (splitDuplicateDrafts), and the server refuses to
  // delete a published showing anyway.
  const removeDuplicateDrafts = (surplus) => setConfirmState({
    title: "Remove duplicate showtimes",
    message: `Delete ${surplus.length} duplicate draft showtime${surplus.length === 1 ? "" : "s"}? One copy of each showtime stays: the one on sale if there is one, otherwise the first one saved. Drafts were never on sale, so no customer or ticket is affected.`,
    confirmLabel: `Delete ${surplus.length} duplicate${surplus.length === 1 ? "" : "s"}`,
    danger: true,
    onConfirm: async () => {
      setBusy("dedupe");
      // Deleting independent drafts is safe in parallel; three at a time keeps
      // a 31-row clean-up to seconds without hammering the server.
      const results = await runEach(surplus, (s) => apiTicketingDeleteShowing(s._id), {
        concurrency: 3,
        onProgress: ({ ok, failed }) => setBulkProgress({ ok, failed, total: surplus.length, past: "removed" }),
      });
      const removed = results.filter((r) => r.ok).length;
      const failed = surplus.flatMap((s, i) => (results[i].ok ? [] : [`${shortWhen(s.startsAt)}: ${errMsg(results[i].error, "failed")}`]));
      if (removed) toast.success(`Removed ${removed} duplicate showtime${removed === 1 ? "" : "s"}.`);
      if (failed.length) toast.error(`Couldn't remove ${failed.length}: ${failed.join("; ")}`);
      loadShowings(eventId);
      setBusy(""); setBulkProgress(null);
    },
  });

  const provisionAccount = async (e) => {
    e.preventDefault();
    if (!acctF.email && !acctF.phoneNumber) return toast.error("Enter an email or phone for the organizer login.");
    setBusy("acct");
    try {
      const { data } = await apiTicketingProvisionAccount(orgId, { email: acctF.email || undefined, phoneNumber: acctF.phoneNumber || undefined, name: acctF.name || undefined });
      setAcctResult(data);
      // Report what the server says happened. The organizer's way in is an
      // emailed set-password link, so "created" without "and the link went
      // out" would be a half-truth the admin acts on.
      if (data.credentialsEmailed) {
        toast.success(`${data.linkedExisting ? "Linked an existing account" : "Organizer login created"} — set-password link emailed to ${data.sentTo}.`);
      } else {
        toast.warn(data.message || "Login ready, but the set-password email could not be sent.");
      }
      setAcctF({ email: "", phoneNumber: "", name: "" });
      loadOrganizers();
    } catch (err) { toast.error(errMsg(err, "Failed to create the login.")); }
    finally { setBusy(""); }
  };

  const loadStaff = async (id) => {
    try {
      const { data } = await apiTicketingListStaff(id);
      setStaffList(data.staff || []);
    } catch (e) { setStaffList([]); }
  };

  const addStaff = async (e) => {
    e.preventDefault();
    if (!staffF.email && !staffF.phoneNumber) return toast.error("Enter an email or phone for the staff login.");
    setBusy("staff");
    try {
      const { data } = await apiTicketingAddStaff(orgId, { email: staffF.email || undefined, phoneNumber: staffF.phoneNumber || undefined, name: staffF.name || undefined });
      setStaffResult(data);
      if (data.linkedExisting) toast.success("Linked an existing account as gate staff.");
      else if (data.credentialsEmailed) toast.success(`Staff login created — set-password link emailed to ${data.sentTo}.`);
      else if (data.mailFallback) toast.warn("Staff login created, but the invite email could not be sent — a one-time password is shown below.");
      else toast.success("Staff login created.");
      setStaffF({ email: "", phoneNumber: "", name: "" });
      loadStaff(orgId);
    } catch (err) { toast.error(errMsg(err, "Failed to add staff.")); }
    finally { setBusy(""); }
  };

  const removeStaff = (member) => setConfirmState({
    title: "Remove staff",
    message: `Remove ${member.name || member.email || member.phoneNumber} from this organizer's gate staff? They keep their account but can no longer scan or view this organizer.`,
    confirmLabel: "Remove staff",
    danger: true,
    onConfirm: async () => {
      try {
        await apiTicketingRemoveStaff(orgId, member._id);
        toast.success("Staff removed.");
        loadStaff(orgId);
      } catch (err) { toast.error(errMsg(err, "Failed to remove staff.")); }
    },
  });

  const loadRefunds = async (id) => {
    try {
      const { data } = await apiTicketingListRefunds(id);
      setRefunds(data.refunds || []);
    } catch (e) { setRefunds([]); }
  };

  const refundAmount = (r) => (r.currency || "UGX") + " " + Number(r.amount || 0).toLocaleString();

  const decideRefund = (r, approve) => {
    setConfirmNote("");
    setConfirmState({
      title: approve ? "Approve refund" : "Reject refund",
      message: approve
        ? `Approve this refund of ${refundAmount(r)}? The customer's tickets are voided and the amount is queued for payout.`
        : `Reject this refund request of ${refundAmount(r)}? The tickets stay valid and the customer is notified.`,
      confirmLabel: approve ? "Approve refund" : "Reject refund",
      danger: !approve,
      withNote: !approve,
      notePlaceholder: "Reason shared with the customer (optional)",
      onConfirm: async (note) => {
        try {
          const { data } = await apiTicketingDecideRefund(r._id, { approve, note: (note || "").trim() || undefined });
          // The server refuses to void tickets that were already scanned in and
          // reports them as `skipped`. Swallowing that told the organizer every
          // ticket was refunded when some were not, so the payout wouldn't match.
          if (approve && Number(data?.skipped || 0) > 0) {
            toast.warn(`Refund approved — ${data.refunded} ticket${Number(data.refunded) === 1 ? "" : "s"} refunded, ${data.skipped} already admitted and left valid. Adjust the payout amount to match.`, { autoClose: 12000 });
          } else {
            toast.success(approve ? "Refund approved." : "Refund rejected.");
          }
          loadRefunds(orgId);
        } catch (err) { toast.error(errMsg(err, "Could not record the refund decision.")); }
      },
    });
  };

  const markRefundProcessed = async (r) => {
    const ref = (procRefF[r._id] || "").trim();
    if (!ref) { toast.error("Enter the wallet refund reference."); return; }
    setBusy("proc" + r._id);
    try {
      await apiTicketingMarkRefundProcessed(r._id, { walletRefundRef: ref });
      toast.success("Refund marked as processed.");
      setProcRefF((f) => ({ ...f, [r._id]: "" }));
      loadRefunds(orgId);
    } catch (err) { toast.error(errMsg(err, "Could not mark the refund as processed.")); }
    finally { setBusy(""); }
  };

  const cancelShowing = (s) => setConfirmState({
    title: "Cancel showing",
    message: "Cancel the " + (s.startsAt ? new Date(s.startsAt).toLocaleString() : "selected") + " showing? Cancelling refunds every sold ticket and cannot be undone.",
    confirmLabel: "Cancel showing",
    danger: true,
    onConfirm: async () => {
      try {
        const { data } = await apiTicketingCancelShowing(s._id);
        const n = data.ticketsRefunded ?? 0;
        toast.success(`Showing cancelled — ${n} ticket${n === 1 ? "" : "s"} refunded.`);
        loadShowings(eventId);
        loadRefunds(orgId);
      } catch (err) { toast.error(errMsg(err, "Could not cancel the showing.")); }
    },
  });

  const resetAccount = async () => {
    if (!orgId) return;
    setBusy("reset");
    try {
      const { data } = await apiTicketingResetAccount(orgId);
      setAcctResult({ reset: true, account: data.account, sentTo: data.sentTo, credentialsEmailed: true, linkExpiresAt: data.linkExpiresAt, deliveryRefId: data.deliveryRefId });
      toast.success(`Set-password link emailed to ${data.sentTo}.`);
    } catch (err) { toast.error(errMsg(err, "Could not reset the password.")); }
    finally { setBusy(""); }
  };

  const removeShowing = (id) => setConfirmState({
    title: "Delete showing",
    message: "Delete this draft showing? This can\u2019t be undone.",
    confirmLabel: "Delete showing",
    danger: true,
    onConfirm: async () => {
      try {
        await apiTicketingDeleteShowing(id);
        toast.success("Draft showing deleted.");
        loadShowings(eventId);
      } catch (err) { toast.error(errMsg(err, "Could not delete the showing.")); }
    },
  });

  const startEditOrg = () => {
    if (!selectedOrg) return;
    const nextOrgForm = {
      name: selectedOrg.name || "",
      category: selectedOrg.category || "CINEMA",
      commissionRate: selectedOrg.commissionRate != null ? String(selectedOrg.commissionRate) : "",
      contactEmail: selectedOrg.contactEmail || "",
      contactPhone: selectedOrg.contactPhone || "",
      description: selectedOrg.description || "",
      primaryColor: selectedOrg.theme?.primaryColor || "#83B524",
      secondaryColor: selectedOrg.theme?.secondaryColor || "#0D1A2F",
      customerPolicies: {
        ticketPurchase: "", admission: "", showtime: "", refunds: "", venueRules: "", recording: "", foodAndDrinks: "",
        ...(selectedOrg.customerPolicies || {}),
      },
      fulfillmentProvider: selectedOrg.fulfillmentProvider || "LUSUKU_NATIVE",
      integrationBaseUrl: selectedOrg.integrationConfig?.baseUrl || "",
      integrationApiKeyEnv: selectedOrg.integrationConfig?.apiKeyEnv || "",
      integrationHmacSecretEnv: selectedOrg.integrationConfig?.hmacSecretEnv || "",
      integrationTimeoutMs: String(selectedOrg.integrationConfig?.timeoutMs || 5000),
      integrationMaxSnapshotAgeMs: String(selectedOrg.integrationConfig?.maxSnapshotAgeMs || 15000),
      integrationAvailabilityPath: selectedOrg.integrationConfig?.endpoints?.availability || "/showings/{showingRef}/availability",
      integrationReservePath: selectedOrg.integrationConfig?.endpoints?.reserve || "/reservations",
      integrationReleasePath: selectedOrg.integrationConfig?.endpoints?.release || "/reservations/{reservationId}/release",
      integrationConfirmPath: selectedOrg.integrationConfig?.endpoints?.confirm || "/reservations/{reservationId}/confirm",
    };
    setEditOrgF(nextOrgForm);
    orgBaselineRef.current = snapshot(nextOrgForm);
    setShowEditOrg(true);
  };
  // Closing this panel used to discard seven policy textareas without a word.
  const closeEditOrg = () => {
    if (orgBaselineRef.current !== null && snapshot(editOrgF) !== orgBaselineRef.current) {
      setConfirmState({
        title: "Discard changes",
        message: "You have unsaved changes to this organizer. Close without saving?",
        confirmLabel: "Discard changes",
        danger: true,
        onConfirm: async () => { orgBaselineRef.current = null; setShowEditOrg(false); },
      });
      return;
    }
    orgBaselineRef.current = null;
    setShowEditOrg(false);
  };
  const saveEditOrg = async (e) => {
    e.preventDefault();
    if (!editOrgF.name.trim()) { toast.error("Organizer name can’t be empty."); focusField("edit-org-name"); return; }
    setBusy("editorg");
    try {
      await apiTicketingUpdateOrganizer(orgId, {
        name: editOrgF.name.trim(),
        category: editOrgF.category,
        contactEmail: editOrgF.contactEmail.trim(),
        contactPhone: editOrgF.contactPhone.trim(),
        description: editOrgF.description.trim(),
        theme: { primaryColor: editOrgF.primaryColor, secondaryColor: editOrgF.secondaryColor },
        customerPolicies: editOrgF.customerPolicies,
        ...(isSuper
          ? {
              commissionRate: editOrgF.commissionRate === "" ? null : Number(editOrgF.commissionRate),
              fulfillmentProvider: editOrgF.fulfillmentProvider,
              integrationConfig: editOrgF.fulfillmentProvider === "EXTERNAL_API" ? {
                baseUrl: editOrgF.integrationBaseUrl,
                apiKeyEnv: editOrgF.integrationApiKeyEnv,
                hmacSecretEnv: editOrgF.integrationHmacSecretEnv,
                timeoutMs: Number(editOrgF.integrationTimeoutMs),
                maxSnapshotAgeMs: Number(editOrgF.integrationMaxSnapshotAgeMs),
                endpoints: {
                  availability: editOrgF.integrationAvailabilityPath,
                  reserve: editOrgF.integrationReservePath,
                  release: editOrgF.integrationReleasePath,
                  confirm: editOrgF.integrationConfirmPath,
                },
              } : {},
            }
          : {}),
      });
      toast.success("Organizer updated.");
      orgBaselineRef.current = null;
      setShowEditOrg(false);
      loadOrganizers();
    } catch (err) { toast.error(errMsg(err, "Could not update the organizer.")); }
    finally { setBusy(""); }
  };
  const removeOrganizer = () => {
    if (!selectedOrg) return;
    setConfirmState({
      title: "Delete organizer",
      message: "Delete “" + selectedOrg.name + "” and everything under it — its events, showings, venues and seat maps? This can’t be undone.",
      confirmLabel: "Delete organizer",
      danger: true,
      onConfirm: async () => {
        try {
          await apiTicketingDeleteOrganizer(orgId);
          toast.success("Organizer deleted.");
          setOrgId("");
          loadOrganizers();
        } catch (err) { toast.error(errMsg(err, "Could not delete the organizer.")); }
      },
    });
  };

  const updateOrgLogo = async (url) => {
    setOrganizers((prev) => prev.map((o) => (o._id === orgId ? { ...o, logoUrl: url } : o)));
    try {
      await apiTicketingUpdateOrganizer(orgId, { logoUrl: url });
      toast.success(url ? "Logo saved." : "Logo removed.");
    } catch (err) {
      toast.error(errMsg(err, "Could not update the logo."));
      loadOrganizers();
    }
  };

  const updateOrgBanner = async (url) => {
    setOrganizers((prev) => prev.map((o) => (o._id === orgId ? { ...o, bannerUrl: url } : o)));
    try {
      await apiTicketingUpdateOrganizer(orgId, { bannerUrl: url });
      toast.success(url ? "Banner saved." : "Banner removed.");
    } catch (err) {
      toast.error(errMsg(err, "Could not update the banner."));
      loadOrganizers();
    }
  };

  const submitOnboarding = async () => {
    if (!orgId) return;
    setBusy("onboarding-submit");
    try {
      const { data } = await apiTicketingSubmitOnboarding(orgId, {
        authorityAttested: onboardingAttested,
      });
      setOnboarding(data.onboarding);
      setOnboardingAttested(false);
      toast.success("Onboarding submitted for Lusuku review.");
      loadOrganizers();
    } catch (err) {
      const next = err?.response?.data?.onboarding;
      if (next) setOnboarding(next);
      toast.error(errMsg(err, "Could not submit onboarding."));
    } finally { setBusy(""); }
  };

  const reviewOnboarding = async (decision) => {
    if (!orgId) return;
    setBusy("onboarding-review");
    try {
      const { data } = await apiTicketingReviewOnboarding(orgId, {
        decision,
        note: onboardingReviewNote.trim(),
      });
      setOnboarding(data.onboarding);
      setOnboardingReviewNote("");
      toast.success(decision === "APPROVED" ? "Organizer approved." : "Changes requested.");
      loadOrganizers();
    } catch (err) {
      const next = err?.response?.data?.onboarding;
      if (next) setOnboarding(next);
      toast.error(errMsg(err, "Could not review onboarding."));
    } finally { setBusy(""); }
  };

  const jumpTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const openOnboardingStep = (check) => {
    const action = typeof check === "string" ? check : check?.action;
    // "Showtime, inventory, and prices" used to drop the operator on the events
    // list with no hint that the work is further down the page.
    if (check?.key === "sales_configuration") {
      setTab("events");
      window.setTimeout(() => document.getElementById("showtimes-section")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      return;
    }
    if (action === "PROFILE") {
      startEditOrg();
      window.setTimeout(() => focusField("edit-org-name"), 50);
    } else if (action === "ACCOUNT") {
      document.getElementById("organizer-login")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (action === "VENUES") {
      setTab("venues");
    } else if (action === "EVENTS") {
      setTab("events");
    } else if (action === "STAFF") {
      document.getElementById("organizer-gate-staff")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const updateEventArt = async (patch) => {
    if (!eventId) return;
    setEvents((prev) => prev.map((e) => (e._id === eventId ? { ...e, ...patch } : e)));
    try {
      await apiTicketingUpdateEvent(eventId, patch);
      toast.success("Artwork saved.");
    } catch (err) {
      toast.error(errMsg(err, "Could not update the artwork."));
      loadOrg(orgId);
    }
  };

  const saveEventDetails = async (e) => {
    e.preventDefault();
    if (!eventId) return;
    // Used to `return` silently on an empty title — the button simply did
    // nothing, unlike every other form in this file.
    if (!editEvF.title.trim()) { toast.error("The movie needs a title."); focusField("edit-ev-title"); return; }
    setBusy("event-details");
    try {
      await apiTicketingUpdateEvent(eventId, {
        title: editEvF.title.trim(), category: editEvF.category,
        contentRating: editEvF.contentRating, durationMinutes: editEvF.durationMinutes,
        synopsis: editEvF.synopsis, trailerUrl: editEvF.trailerUrl,
        director: editEvF.director, cast: editEvF.cast, genres: editEvF.genres,
        language: editEvF.language, subtitleLanguage: editEvF.subtitleLanguage,
        releaseDate: editEvF.releaseDate || null, studio: editEvF.studio,
        ...(selectedEvent?.status === "PUBLISHED" ? {} : { status: editEvF.status }),
      });
      toast.success("Movie details saved.");
      // Accept the just-saved values as the new clean baseline, and let the
      // next refetch reseed from the server copy.
      evBaselineRef.current = snapshot(editEvF);
      evDirtyRef.current = false;
      evSeedRef.current = null;
      setEvServerMoved(false);
      loadOrg(orgId);
    } catch (err) { toast.error(errMsg(err, "Could not update the movie details.")); }
    finally { setBusy(""); }
  };

  const reserved = isReservedModel(shF.inventoryModel);
  const reservedUnits = shF.inventoryModel === "RESERVED_UNITS";
  // How many showings the current form would create — drives the submit label
  // ("Create 4 showings"). 0 while the bulk fields are invalid/incomplete.
  const bulkCount = (() => {
    if (editShowingId || !shF.startsAt) return 0;
    const { times, bad } = parseTimeList(bulkF.times);
    if (bad.length) return 0;
    // Slots earlier than now are dropped before the count is shown — the server
    // refuses them, so promising "12 showings" and delivering 9 was a lie.
    return expandSlots(shF.startsAt, times, bulkF.repeatUntil).filter((d) => d.getTime() > Date.now()).length;
  })();
  const mapClasses = seatMapClasses(seatMaps.find((m) => m._id === shF.seatMapId));
  // A layout is a hall inside ONE venue. Offering every layout made two
  // same-named halls indistinguishable and let one outlet's screen back another
  // outlet's showtime (the server now refuses that). Legacy layouts with no
  // venue stay listed so showtimes created before this remain editable.
  const venueMaps = seatMaps.filter((m) => !m.venueId || !shF.venueId || String(m.venueId) === String(shF.venueId));
  const venueName = (id) => venues.find((v) => String(v._id) === String(id))?.name || "—";
  const presentationLabel = (sh) =>
    [sh?.presentation?.format, sh?.presentation?.dimension, sh?.presentation?.sound].filter(Boolean).join(" \u00b7 ") || "\u2014";
  const selectedEvent = events.find((e) => e._id === eventId);
  const selectedOrg = organizers.find((o) => o._id === orgId);
  // Cinema vocabulary ("screen", "projection", "2D") is wrong for an arena, a
  // conference hall or a festival field. Follow the organizer's category; fall
  // back to the event's so a CINEMA event under a GENERAL promoter still reads
  // correctly.
  const orgCategory = String(selectedOrg?.category || selectedEvent?.category || "").toUpperCase();
  const isCinemaOrg = orgCategory === "CINEMA";
  const spaceNoun = isCinemaOrg ? "Screen" : orgCategory === "CONFERENCE" ? "Room" : "Space";
  orgCategoryRef.current = orgCategory;
  editShowingIdRef.current = editShowingId;
  shStartedRef.current = !!shF.startsAt;

  // Switching to an organizer of another kind must not leave cinema defaults
  // ("STANDARD · 2D") sitting in an untouched form. Only ever resets a form
  // nobody has started.
  useEffect(() => {
    if (editShowingIdRef.current || shStartedRef.current) return;
    setShF((f) => ({ ...f, presentation: blankShowingForm(orgCategoryRef.current).presentation }));
  }, [orgCategory]);
  // Dirty flags drive every save button's enabled state and the
  // unsaved-changes guards below. Computed on each render; the ref lets the
  // reseed effect (which runs after render) see the latest value.
  const evDirty = evBaselineRef.current !== null && snapshot(editEvF) !== evBaselineRef.current;
  evDirtyRef.current = evDirty;
  const orgDirty = orgBaselineRef.current !== null && snapshot(editOrgF) !== orgBaselineRef.current;
  const showingDirty = shBaselineRef.current !== null && snapshot(shF) !== shBaselineRef.current;
  const anyDirty = evDirty || orgDirty || showingDirty;
  anyDirtyRef.current = anyDirty;
  // Showtime list slicing. Default is UPCOMING: the list is returned oldest
  // first and unbounded, so a month-old run of ended showtimes used to sit
  // between the operator and today's schedule.
  const isPast = (s) => s.startsAt && new Date(s.startsAt).getTime() < Date.now();
  const isDraft = (s) => s.status !== "PUBLISHED" && s.status !== "CANCELLED";
  const counts = {
    upcoming: showings.filter((s) => !isPast(s)).length,
    draft: showings.filter((s) => isDraft(s) && !isPast(s)).length,
    live: showings.filter((s) => s.status === "PUBLISHED" && !isPast(s)).length,
    past: showings.filter(isPast).length,
  };
  const visibleShowings = showings.filter((s) => {
    if (showingFilter === "ALL") return true;
    if (showingFilter === "PAST") return isPast(s);
    if (showingFilter === "DRAFT") return isDraft(s) && !isPast(s);
    if (showingFilter === "LIVE") return s.status === "PUBLISHED" && !isPast(s);
    return !isPast(s);
  });
  // Rows are keyed the way the customer page groups: venue, then experience.
  // A screen running Standard and 3D shows as two rows, exactly as it does to a
  // customer.
  const gridDays = [...new Set(visibleShowings.filter((s) => s.startsAt).map((s) => new Date(s.startsAt).toDateString()))]
    .map((d) => new Date(d))
    .sort((a, b) => a - b);
  const gridRows = (() => {
    const rows = new Map();
    visibleShowings.filter((s) => s.startsAt).forEach((s) => {
      const screen = s.presentation?.screenName || "";
      const key = `${String(s.venueId)}|${presentationLabel(s) || "Admission"}|${screen}`;
      if (!rows.has(key)) {
        rows.set(key, { key, venue: venueName(s.venueId), experience: presentationLabel(s) || "Admission", screen, template: s, cells: new Map() });
      }
      const row = rows.get(key);
      const day = new Date(s.startsAt).toDateString();
      if (!row.cells.has(day)) row.cells.set(day, []);
      row.cells.get(day).push(s);
    });
    rows.forEach((row) => row.cells.forEach((list) => list.sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))));
    return [...rows.values()].sort((a, b) => a.venue.localeCompare(b.venue) || a.experience.localeCompare(b.experience));
  })();

  const gridWorthIt = gridDays.length >= 2 && gridRows.length >= 1;
  const resolvedView = showingView === "AUTO" ? (gridWorthIt ? "GRID" : "LIST") : showingView;

  const selectableShowings = visibleShowings.filter(isDraft);
  const draftCount = counts.draft;

  // ── Progress ────────────────────────────────────────────────────────────
  // Two different questions, two different answers. "Where am I in this form?"
  // is the step headers; "is this film ready to sell?" is the strip above the
  // table. Neither was answerable before without re-reading every field and
  // counting rows by hand.
  const dayLabel = (d) => d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  const selectedMap = seatMaps.find((m) => m._id === shF.seatMapId);
  const extraSlots = Math.max(0, bulkCount - 1);
  const stepWhenDone = !!shF.startsAt && !!shF.venueId;
  const stepWhenSummary = [
    venueName(shF.venueId),
    shF.presentation.screenName,
    shortWhen(shF.startsAt),
    extraSlots ? `+${extraSlots} more time${extraSlots === 1 ? "" : "s"}` : "",
    bulkF.repeatUntil ? `through ${dayLabel(new Date(bulkF.repeatUntil + "T00:00:00"))}` : "",
  ].filter(Boolean).join(" · ");
  const stepWhenHint = !shF.startsAt ? "Set the date and time" : "Choose the venue";
  const modelLabel = shF.inventoryModel === "RESERVED_SEATING" ? "Reserved seats"
    : shF.inventoryModel === "RESERVED_UNITS" ? `Reserved ${String(shF.inventoryUnitLabel || "unit").toLowerCase()}s`
      : shF.inventoryModel === "GENERAL_ADMISSION" ? "General admission" : "Zoned admission";
  const stepInvDone = !reserved || !!shF.seatMapId;
  const stepInvSummary = [
    modelLabel,
    selectedMap ? `${selectedMap.name} · ${selectedMap.seatCount} units` : "",
    mapClasses.join(", "),
  ].filter(Boolean).join(" · ");
  const completeTypes = shF.ticketTypes.filter((t) => t.key && t.name && t.price !== "");
  const stepPriceDone = completeTypes.length > 0;
  const stepPriceSummary = completeTypes.map((t) => `${t.name} ${money(t.price)}`).join(" · ");

  // Film-level readiness. The amber draft banner only appeared when something
  // was wrong, so "everything is fine" looked identical to "I haven't started".
  const upcomingShowings = showings.filter((s) => !isPast(s) && s.status !== "CANCELLED");
  const liveUpcoming = upcomingShowings.filter((s) => s.status === "PUBLISHED").length;
  const { surplus: duplicateDrafts, slots: duplicateSlots } = splitDuplicateDrafts(upcomingShowings);
  const duplicateIds = new Set(duplicateDrafts.map((s) => String(s._id)));
  const liveSlots = new Set(upcomingShowings.filter((s) => s.status === "PUBLISHED").map(showingSlotKey));
  // "Publish all" puts one copy of each slot on sale and never a second.
  const publishableDraftIds = upcomingShowings
    .filter((s) => isDraft(s) && !duplicateIds.has(String(s._id)) && !liveSlots.has(showingSlotKey(s)))
    .map((s) => String(s._id));
  const scheduleVenues = new Set(upcomingShowings.map((s) => String(s.venueId))).size;
  const scheduleDays = [...new Set(upcomingShowings.filter((s) => s.startsAt).map((s) => new Date(s.startsAt).toDateString()))]
    .map((d) => new Date(d)).sort((a, b) => a - b);
  const scheduleRange = scheduleDays.length === 0 ? ""
    : scheduleDays.length === 1 ? dayLabel(scheduleDays[0])
      : `${dayLabel(scheduleDays[0])} – ${dayLabel(scheduleDays[scheduleDays.length - 1])}`;

  // The onboarding checklist already knows what's outstanding, but the tabs —
  // where the work actually happens — showed nothing. Same state, mirrored.
  // "warn" is amber and always means something is fixable right here.
  const venuesNeedPin = venues.filter((v) => !Number.isFinite(v.latitude) || !Number.isFinite(v.longitude)).length;
  const mapsNeedVenue = seatMaps.filter((m) => !m.venueId).length;
  const tabState = {
    events: events.length === 0
      ? { mark: "·", tone: "todo", title: "No events yet" }
      : draftCount > 0
        ? { mark: "●", tone: "warn", title: `${draftCount} showtime${draftCount === 1 ? "" : "s"} not on sale` }
        : liveUpcoming > 0
          ? { mark: "✓", tone: "done", title: "Showtimes on sale" }
          : { mark: "·", tone: "todo", title: "No upcoming showtimes" },
    venues: venues.length === 0
      ? { mark: "·", tone: "todo", title: "No venues yet" }
      : venuesNeedPin
        ? { mark: "●", tone: "warn", title: `${venuesNeedPin} venue${venuesNeedPin === 1 ? "" : "s"} without a map location` }
        : { mark: "✓", tone: "done", title: "Venues ready" },
    seatmaps: seatMaps.length === 0
      ? { mark: "·", tone: "todo", title: "No reserved layouts yet" }
      : mapsNeedVenue
        ? { mark: "●", tone: "warn", title: `${mapsNeedVenue} layout${mapsNeedVenue === 1 ? "" : "s"} not linked to a venue` }
        : { mark: "✓", tone: "done", title: "Layouts ready" },
  };
  const onboardingApproved = !onboarding || onboarding.status === "APPROVED";
  // Set the organizer owner's password by hand. Super admins only (the route is
  // superAdminOnly too), and only once a login is linked. Same dialog and
  // server path as a vendor's Set login: services/auth/vendorLoginSetter.js.
  const setPasswordButton = isSuper && orgId && selectedOrg?.owner ? (
    <SetVendorLoginAction vendorType="EVENT_ORGANIZER" vendorId={orgId} vendorName={selectedOrg.name} visible>
      {({ open }) => <AppButton size="sm" icon="fa fa-key" onClick={open}>Set password</AppButton>}
    </SetVendorLoginAction>
  ) : null;
  // STAFF sees a read-only console: the server refuses every mutation from a
  // staff login; these flags just make the UI honest about it.
  const isStaffViewer = !isSuper && selectedOrg?.myRole === "STAFF";
  const canManageSelected = !!orgId && !isStaffViewer;

  // The quick-nav strip under the hero. Only sections that are actually
  // rendered for this user appear, so an organizer owner never sees a chip
  // for the super-admin-only login panel. Dot colours mirror the panel dots.
  const navSections = [
    { id: "organizer-profile", label: "Profile & branding", color: C.green, show: true },
    { id: "organizer-onboarding", label: onboarding ? `Onboarding · ${onboarding.progressPercent}%` : "Onboarding", color: C.orange, show: Boolean(orgId && onboarding) },
    { id: "organizer-login", label: "Organizer login", color: C.blue, show: Boolean(orgId && isSuper) },
    { id: "organizer-gate-staff", label: "Gate staff", color: C.teal, show: Boolean(orgId && canManageSelected) },
    { id: "organizer-refunds", label: "Refunds", color: C.purple, show: Boolean(orgId && canManageSelected) },
    { id: "console-workspace", label: "Events & sales", color: C.green, show: Boolean(orgId) },
  ].filter((s) => s.show);

  return (
    <Page>
      <AppConfirmDialog
        open={Boolean(confirmState)}
        title={confirmState?.title}
        consequence={confirmState?.message}
        confirmLabel={confirmState?.confirmLabel || "Confirm"}
        tone={confirmState?.danger ? "danger" : "primary"}
        busy={confirmBusy}
        onClose={() => { if (!confirmBusy) setConfirmState(null); }}
        onConfirm={async () => {
          setConfirmBusy(true);
          try { await confirmState.onConfirm(confirmNote); } finally { setConfirmBusy(false); setConfirmState(null); }
        }}
      >
        {confirmState?.withNote && (
          <div style={{ marginTop: 12 }}>
            <PInput placeholder={confirmState.notePlaceholder || "Add a note (optional)"} value={confirmNote} onChange={(e) => setConfirmNote(e.target.value)} disabled={confirmBusy} />
          </div>
        )}
        {confirmBusy && bulkProgress && (
          <div style={{ marginTop: 14 }}><BulkProgress progress={bulkProgress} block /></div>
        )}
      </AppConfirmDialog>
      <div style={{ padding: "16px 12px 0" }}>
        <Hero
          title="Ticketing — Organizer Console"
          subtitle="Profile and branding, venues, showtimes and prices — then publish a showing to open sales. Use the shortcuts below to jump to a section."
          right={<span style={S.chip(false)}>{organizers.length} organizer{organizers.length === 1 ? "" : "s"}</span>}
        />

        {loading ? (
          <Loader label="Loading the console…" />
        ) : (
          <>
            {/* Section shortcuts — this console is one long page; each chip
                jumps straight to its section. Dot colours match the panel
                header dots, so the chip teaches the page's colour language. */}
            {navSections.length > 1 && (
              <div style={{ position: "sticky", top: 0, zIndex: 5, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: C.shadow, padding: "8px 10px", marginBottom: 20 }}>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.8px", textTransform: "uppercase", color: C.textLight, padding: "0 6px" }}>Jump to</span>
                {navSections.map((s) => (
                  <button key={s.id} type="button" onClick={() => jumpTo(s.id)} style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 20, padding: "5px 12px", fontSize: 12, fontWeight: 700, color: C.textMid, cursor: "pointer", whiteSpace: "nowrap" }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, flexShrink: 0 }} />{s.label}
                  </button>
                ))}
              </div>
            )}
            <div id="organizer-profile" style={{ scrollMarginTop: 56 }} />
            <Panel title="Organizer" sub="who is selling">
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 12, color: C.textLight }}>
                    {isSuper
                      ? (organizers.length > 1
                        ? organizers.length + " organizers on the platform — select one to manage"
                        : organizers.length === 1
                        ? "The only organizer on the platform — everything below applies to it."
                        : "No organizers yet")
                      : "Everything below applies to your organizer."}
                  </div>
                  {isSuper && <PButton variant="outline" onClick={() => setShowNewOrg((v) => !v)} style={{ padding: "6px 14px", fontSize: 12, whiteSpace: "nowrap" }}>{showNewOrg ? "Close ✕" : "+ New organizer"}</PButton>}
                </div>
                {/* The picker list only earns its space when there is a choice
                    to make — with a single organizer it repeated the identity
                    card below it almost line for line. */}
                {organizers.length === 0 && <div style={{ fontSize: 12.5, color: C.textLight, padding: "10px 0" }}>{isSuper ? "No organizers yet — click “+ New organizer” to add one." : "No organizer profile is linked to this account. Complete Event Organizer registration or ask Lusuku support to link your account."}</div>}
                {organizers.length > 1 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {organizers.map((o) => {
                      const active = o._id === orgId;
                      return (
                        <div key={o._id} onClick={() => setOrgId(o._id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8, cursor: "pointer", border: "1px solid " + (active ? C.green : C.border), background: active ? C.greenSoft : "#fff" }}>
                          <div style={{ width: 34, height: 34, borderRadius: 7, background: "#eef1f6", flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {o.logoUrl ? <img src={o.logoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 13, fontWeight: 800, color: C.textLight }}>{(o.name || "?").slice(0, 1).toUpperCase()}</span>}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 700, color: active ? C.green : C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.name}</div>
                            <div style={{ fontSize: 11, color: C.textLight }}>{(o.category || "—")}{o.commissionRate != null ? " · " + o.commissionRate + "% commission" : ""}</div>
                          </div>
                          {active
                            ? <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.4px", textTransform: "uppercase", color: C.green, background: "#fff", border: "1px solid " + C.green, borderRadius: 20, padding: "3px 11px" }}>Managing</span>
                            : <span style={{ fontSize: 11, fontWeight: 700, color: C.textLight }}>Manage</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedOrg && (
                  <div style={organizers.length > 1 ? { marginTop: 16, paddingTop: 14, borderTop: "1px solid " + C.border } : {}}>
                    {/* Identity card: WHO this is, at a glance — name first,
                        then category / commission / onboarding state as chips.
                        The old layout led with "Cinemax logo", which made the
                        organizer's own name read like an image caption. */}
                    <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                      <ArtworkUpload width={56} height={56} value={selectedOrg.logoUrl || ""} onChange={(u) => updateOrgLogo(u)} recommended="Square · 400 × 400px" minWidth={200} minHeight={200} />
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{selectedOrg.name}</span>
                          <span style={S.badge(C.textMid, "#eef1f5")}>{selectedOrg.category || "GENERAL"}</span>
                          {selectedOrg.commissionRate != null && <span style={S.badge(C.blue, C.blueSoft)}>{selectedOrg.commissionRate}% commission</span>}
                          {onboarding && (
                            <span style={S.badge(
                              onboarding.status === "APPROVED" ? C.green
                              : onboarding.status === "PENDING_REVIEW" ? C.blue
                              : onboarding.status === "NEEDS_CHANGES" ? "#b42318"
                              : C.orange,
                              onboarding.status === "APPROVED" ? C.greenSoft
                              : onboarding.status === "PENDING_REVIEW" ? C.blueSoft
                              : onboarding.status === "NEEDS_CHANGES" ? "#fee4e2"
                              : C.orangeSoft
                            )}>{onboarding.status === "APPROVED" ? "Live" : String(onboarding.status || "DRAFT").replace(/_/g, " ")}</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11.5, color: C.textLight, marginTop: 3 }}>
                          {[selectedOrg.contactEmail, selectedOrg.contactPhone].filter(Boolean).join(" · ") || "No contact details yet — add them under Edit details."}
                          {" "}&middot; Click the logo to change it.
                        </div>
                      </div>
                      {canManageSelected && (
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <PButton variant="outline" style={{ padding: "6px 14px", fontSize: 12 }} onClick={() => (showEditOrg ? closeEditOrg() : startEditOrg())}>{showEditOrg ? "Close ✕" : "Edit details"}</PButton>
                          {/* Deliberately quiet: Delete used to sit beside Edit
                              details as an equal-weight solid red button — one
                              slip away. The confirm dialog still guards it. */}
                          {isSuper && <PButton variant="ghost" style={{ padding: "6px 14px", fontSize: 12, color: "#c62828", borderColor: "#f5c6cb" }} onClick={removeOrganizer}>Delete…</PButton>}
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: 12, maxWidth: 560 }}>
                      <ArtworkUpload label="Organizer banner (optional)" value={selectedOrg.bannerUrl || ""} onChange={(u) => updateOrgBanner(u)} height={80} aspect="16 / 5" recommended="1600 × 500px (16:5)" minWidth={1200} minHeight={375} hint="Wide banner shown across the top of the customer Events page." />
                    </div>
                    {showEditOrg && (
                      <form onSubmit={saveEditOrg} style={{ marginTop: 14 }}>
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
                          <div style={{ flex: "1 1 180px", minWidth: 150 }}>
                            <Field label="Organizer name" style={{ marginBottom: 0 }}><PInput id="edit-org-name" value={editOrgF.name} onChange={(e) => setEditOrgF({ ...editOrgF, name: e.target.value })} /></Field>
                          </div>
                          <div style={{ flex: "0 1 150px" }}>
                            <Field label="Type" style={{ marginBottom: 0 }}><PSelect value={editOrgF.category} onChange={(e) => setEditOrgF({ ...editOrgF, category: e.target.value })}>{CATEGORIES.map((cc) => <option key={cc}>{cc}</option>)}</PSelect></Field>
                          </div>
                          {isSuper && <div style={{ flex: "0 1 120px" }}>
                            <Field label="Commission %" style={{ marginBottom: 0 }}><PInput type="number" step="0.5" placeholder="10" value={editOrgF.commissionRate} onChange={(e) => setEditOrgF({ ...editOrgF, commissionRate: e.target.value })} /></Field>
                          </div>}
                          <div style={{ flex: "1 1 210px", minWidth: 180 }}>
                            <Field label="Contact email" style={{ marginBottom: 0 }}><PInput type="email" value={editOrgF.contactEmail} onChange={(e) => setEditOrgF({ ...editOrgF, contactEmail: e.target.value })} /></Field>
                          </div>
                          <div style={{ flex: "0 1 170px" }}>
                            <Field label="Contact phone" style={{ marginBottom: 0 }}><PInput value={editOrgF.contactPhone} onChange={(e) => setEditOrgF({ ...editOrgF, contactPhone: e.target.value })} /></Field>
                          </div>
                          <div style={{ flex: "1 1 100%" }}>
                            <Field label="Description" style={{ marginBottom: 0 }}><PInput value={editOrgF.description} onChange={(e) => setEditOrgF({ ...editOrgF, description: e.target.value })} /></Field>
                          </div>
                          <div style={{ flex: "0 1 210px" }}>
                            <Field label="Primary theme colour" hint="Buttons, showtimes and highlights."><PInput type="color" value={editOrgF.primaryColor} onChange={(e) => setEditOrgF({ ...editOrgF, primaryColor: e.target.value.toUpperCase() })} /></Field>
                          </div>
                          <div style={{ flex: "0 1 210px" }}>
                            <Field label="Secondary theme colour" hint="Hero and dark surfaces."><PInput type="color" value={editOrgF.secondaryColor} onChange={(e) => setEditOrgF({ ...editOrgF, secondaryColor: e.target.value.toUpperCase() })} /></Field>
                          </div>
                          <div style={{ flex: "1 1 100%", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
                            {[
                              ["ticketPurchase", "Ticket purchase terms"], ["admission", "Admission policy"],
                              ["showtime", "Showtime / late-entry policy"], ["refunds", "Returns & exchanges"],
                              ["venueRules", "Venue / cinema rules"], ["recording", "Recording policy"],
                              ["foodAndDrinks", "Food & drinks policy"],
                            ].map(([key, label]) => (
                              <Field key={key} label={label} style={{ marginBottom: 0 }}>
                                <textarea value={editOrgF.customerPolicies[key]} rows={3}
                                  onChange={(e) => setEditOrgF({ ...editOrgF, customerPolicies: { ...editOrgF.customerPolicies, [key]: e.target.value } })}
                                  style={{ width: "100%", border: `1px solid ${C.border}`, borderRadius: 6, padding: "9px 10px", fontSize: 12.5, color: C.text, resize: "vertical" }} />
                              </Field>
                            ))}
                          </div>
                          {isSuper && (<>
                            <div style={{ flex: "0 1 220px" }}><Field label="Inventory provider"><PSelect value={editOrgF.fulfillmentProvider} onChange={(e) => setEditOrgF({ ...editOrgF, fulfillmentProvider: e.target.value })}><option value="LUSUKU_NATIVE">Lusuku native</option><option value="EXTERNAL_API">Cinema real-time API</option><option value="ALLOTMENT">Provider allotment</option></PSelect></Field></div>
                            {editOrgF.fulfillmentProvider === "EXTERNAL_API" && (<>
                              <div style={{ flex: "1 1 300px" }}><Field label="Provider API origin" hint="Production host must be allowlisted on the server."><PInput placeholder="https://tickets.cinema.example" value={editOrgF.integrationBaseUrl} onChange={(e) => setEditOrgF({ ...editOrgF, integrationBaseUrl: e.target.value })} /></Field></div>
                              <div style={{ flex: "0 1 240px" }}><Field label="API key environment name" hint="Use a TICKETING_PROVIDER_* variable; the secret itself is never stored here."><PInput placeholder="TICKETING_PROVIDER_CENTURY_API_KEY" value={editOrgF.integrationApiKeyEnv} onChange={(e) => setEditOrgF({ ...editOrgF, integrationApiKeyEnv: e.target.value })} /></Field></div>
                              <div style={{ flex: "0 1 240px" }}><Field label="HMAC secret environment name" hint="Use a TICKETING_PROVIDER_* variable."><PInput placeholder="TICKETING_PROVIDER_CENTURY_HMAC_SECRET" value={editOrgF.integrationHmacSecretEnv} onChange={(e) => setEditOrgF({ ...editOrgF, integrationHmacSecretEnv: e.target.value })} /></Field></div>
                              <div style={{ flex: "0 1 180px" }}><Field label="Request timeout (ms)"><PInput type="number" min="1000" max="15000" value={editOrgF.integrationTimeoutMs} onChange={(e) => setEditOrgF({ ...editOrgF, integrationTimeoutMs: e.target.value })} /></Field></div>
                              <div style={{ flex: "0 1 210px" }}><Field label="Snapshot freshness (ms)"><PInput type="number" min="1000" max="60000" value={editOrgF.integrationMaxSnapshotAgeMs} onChange={(e) => setEditOrgF({ ...editOrgF, integrationMaxSnapshotAgeMs: e.target.value })} /></Field></div>
                              <div style={{ flex: "1 1 340px" }}><Field label="Availability path"><PInput placeholder="/showings/{showingRef}/availability" value={editOrgF.integrationAvailabilityPath} onChange={(e) => setEditOrgF({ ...editOrgF, integrationAvailabilityPath: e.target.value })} /></Field></div>
                              <div style={{ flex: "1 1 260px" }}><Field label="Reserve path"><PInput placeholder="/reservations" value={editOrgF.integrationReservePath} onChange={(e) => setEditOrgF({ ...editOrgF, integrationReservePath: e.target.value })} /></Field></div>
                              <div style={{ flex: "1 1 320px" }}><Field label="Release path"><PInput placeholder="/reservations/{reservationId}/release" value={editOrgF.integrationReleasePath} onChange={(e) => setEditOrgF({ ...editOrgF, integrationReleasePath: e.target.value })} /></Field></div>
                              <div style={{ flex: "1 1 320px" }}><Field label="Confirm path"><PInput placeholder="/reservations/{reservationId}/confirm" value={editOrgF.integrationConfirmPath} onChange={(e) => setEditOrgF({ ...editOrgF, integrationConfirmPath: e.target.value })} /></Field></div>
                            </>)}
                          </>)}
                          <PButton type="submit" loading={busy === "editorg"} disabled={!orgDirty} style={{ height: 38, padding: "0 22px" }}>{orgDirty ? "Save changes" : "Saved"}</PButton>
                        </div>
                      </form>
                    )}
                  </div>
                )}
                {showNewOrg && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid " + C.border }}>
                    <div style={S.sectionBreak}>Create a new organizer</div>
                    <form onSubmit={createOrganizer}>
                      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
                        <ArtworkUpload width={56} height={56} label="Logo" value={orgF.logoUrl} onChange={(u) => setOrgF({ ...orgF, logoUrl: u })} recommended="Square · 400 × 400px" minWidth={200} minHeight={200} />
                        <div style={{ flex: "1 1 180px", minWidth: 150 }}>
                          <Field label="Organizer name" style={{ marginBottom: 0 }}><PInput id="org-name" placeholder="e.g. Century Cinema" value={orgF.name} onChange={(e) => setOrgF({ ...orgF, name: e.target.value })} /></Field>
                        </div>
                        <div style={{ flex: "0 1 150px" }}>
                          <Field label="Type" style={{ marginBottom: 0 }}><PSelect value={orgF.category} onChange={(e) => setOrgF({ ...orgF, category: e.target.value })}>{CATEGORIES.map((cc) => <option key={cc}>{cc}</option>)}</PSelect></Field>
                        </div>
                        <div style={{ flex: "0 1 120px" }}>
                          <Field label="Commission %" style={{ marginBottom: 0 }}><PInput type="number" step="0.5" placeholder="10" value={orgF.commissionRate} onChange={(e) => setOrgF({ ...orgF, commissionRate: e.target.value })} /></Field>
                        </div>
                        <div style={{ flex: "1 1 210px", minWidth: 180 }}>
                          <Field label="Contact email" style={{ marginBottom: 0 }}><PInput type="email" value={orgF.contactEmail} onChange={(e) => setOrgF({ ...orgF, contactEmail: e.target.value })} /></Field>
                        </div>
                        <div style={{ flex: "0 1 170px" }}>
                          <Field label="Contact phone" style={{ marginBottom: 0 }}><PInput value={orgF.contactPhone} onChange={(e) => setOrgF({ ...orgF, contactPhone: e.target.value })} /></Field>
                        </div>
                        <PButton type="submit" loading={busy === "org"} style={{ height: 38, padding: "0 22px" }}>Create</PButton>
                      </div>
                      <div style={{ fontSize: 11, color: C.textLight, marginTop: 8 }}>Commission is what the platform keeps of ticket revenue — leave blank for the default 10%.</div>
                    </form>
                  </div>
                )}
              </div>
            </Panel>

            {orgId && onboarding && (
              <div id="organizer-onboarding" style={{ scrollMarginTop: 56 }}><Panel title="Organizer onboarding" sub="complete, submit, review, then go live" color={C.orange}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={S.badge(
                        onboarding.status === "APPROVED" ? C.green
                        : onboarding.status === "PENDING_REVIEW" ? C.blue
                        : onboarding.status === "NEEDS_CHANGES" ? "#b42318"
                        : C.textMid,
                        onboarding.status === "APPROVED" ? C.greenSoft
                        : onboarding.status === "PENDING_REVIEW" ? C.blueSoft
                        : onboarding.status === "NEEDS_CHANGES" ? "#fee4e2"
                        : "#eef1f5"
                      )}>{String(onboarding.status || "DRAFT").replace(/_/g, " ")}</span>
                      <strong style={{ fontSize: 13.5, color: C.text }}>{onboarding.completedRequired} of {onboarding.requiredCount} required steps complete</strong>
                      {onboarding.legacyApproved && <span style={{ fontSize: 11, color: C.textLight }}>Legacy organizer — preserved as approved</span>}
                    </div>
                    {onboarding.reviewNote && <div style={{ marginTop: 7, fontSize: 12.5, color: onboarding.status === "NEEDS_CHANGES" ? "#b42318" : C.textMid }}>Lusuku review: {onboarding.reviewNote}</div>}
                  </div>
                  <div style={{ minWidth: 180, flex: "0 1 280px" }}>
                    <div style={{ height: 8, borderRadius: 8, background: "#eef1f5", overflow: "hidden" }}>
                      <div style={{ width: `${onboarding.progressPercent}%`, height: "100%", background: onboarding.readyToSubmit ? C.green : C.orange, transition: "width 0.2s" }} />
                    </div>
                    <div style={{ fontSize: 11, color: C.textLight, textAlign: "right", marginTop: 4 }}>{onboarding.progressPercent}% ready</div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 8 }}>
                  {(onboarding.checks || []).map((check) => (
                    <button key={check.key} type="button" onClick={() => openOnboardingStep(check)} style={{ display: "flex", alignItems: "flex-start", gap: 10, textAlign: "left", border: `1px solid ${check.complete ? "#c8e6c9" : (check.required ? "#f4c790" : C.border)}`, background: check.complete ? C.greenSoft : "#fff", borderRadius: 8, padding: "10px 12px", cursor: "pointer" }}>
                      <span style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: check.complete ? "#fff" : C.textLight, background: check.complete ? C.green : "#eef1f5" }}>{check.complete ? "✓" : "·"}</span>
                      <span style={{ flex: 1 }}>
                        <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: C.text }}>{check.label}{!check.required ? " (optional)" : ""}</span>
                        <span style={{ display: "block", fontSize: 11, color: C.textLight, marginTop: 2, lineHeight: 1.4 }}>{check.detail}</span>
                      </span>
                      {/* Tiles are buttons that open the matching section, but
                          nothing said so — the chevron is the tell. */}
                      <span style={{ alignSelf: "center", fontSize: 16, fontWeight: 700, color: C.textLight }}>&rsaquo;</span>
                    </button>
                  ))}
                </div>

                {canManageSelected && ["DRAFT", "NEEDS_CHANGES"].includes(onboarding.status) && (
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: 12.5, color: C.text, cursor: "pointer" }}>
                      <input type="checkbox" checked={onboardingAttested} onChange={(e) => setOnboardingAttested(e.target.checked)} style={{ marginTop: 2 }} />
                      <span>I confirm that I am authorized to sell tickets for this organizer and that the submitted business, venue, event, pricing, and inventory information is accurate.</span>
                    </label>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                      <PButton onClick={submitOnboarding} loading={busy === "onboarding-submit"} disabled={!onboarding.readyToSubmit || !onboardingAttested}>Submit for review</PButton>
                      {!onboarding.readyToSubmit && <span style={{ fontSize: 11.5, color: C.textLight }}>Complete every required step first.</span>}
                    </div>
                  </div>
                )}

                {onboarding.status === "PENDING_REVIEW" && !isSuper && <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 8, background: C.blueSoft, color: C.text, fontSize: 12.5 }}>Submitted to Lusuku. You can keep preparing drafts, but ticket sales remain locked until approval.</div>}

                {onboarding.status === "PENDING_REVIEW" && isSuper && (
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                    <Field label="Review note" hint="Required when requesting changes."><PInput placeholder="What should the organizer correct?" value={onboardingReviewNote} onChange={(e) => setOnboardingReviewNote(e.target.value)} /></Field>
                    <div style={{ display: "flex", gap: 8 }}>
                      <PButton loading={busy === "onboarding-review"} onClick={() => reviewOnboarding("APPROVED")}>Approve organizer</PButton>
                      <PButton variant="danger" loading={busy === "onboarding-review"} disabled={!onboardingReviewNote.trim()} onClick={() => reviewOnboarding("NEEDS_CHANGES")}>Request changes</PButton>
                    </div>
                  </div>
                )}
              </Panel></div>
            )}

            {orgId && isSuper && (
              <div id="organizer-login" style={{ scrollMarginTop: 56 }}><Panel title="Organizer login" sub="give this organizer their own credentials" color={C.blue}>
                {selectedOrg?.owner?.operationMode === "EVENT_ORGANIZER"
                  ? <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", background: C.greenSoft, border: "1px solid #c8e6c9", borderRadius: 8, padding: "9px 13px", marginBottom: 12, fontSize: 12.5, color: C.text }}><span>Login active: <strong>{selectedOrg.owner.email || selectedOrg.owner.phoneNumber}</strong> can sign in and manage only this organizer.</span><span style={{ display: "inline-flex", gap: 8, flexWrap: "wrap" }}><AppButton variant="outline" size="sm" icon="fa fa-envelope" loading={busy === "reset"} onClick={resetAccount}>Email set-password link</AppButton>{setPasswordButton}</span></div>
                  : <div style={{ background: "#fff8e1", border: "1px solid #ffe0a3", borderRadius: 8, padding: "9px 13px", marginBottom: 12, fontSize: 12.5, color: C.text }}>No organizer login yet — create one below so they can manage their own events and scan tickets.</div>}
                <form onSubmit={provisionAccount}>
                  <Row className="align-items-end">
                    <Col md={4}><Field label="Login email"><PInput type="email" placeholder="organizer@email.com" value={acctF.email} onChange={(e) => setAcctF({ ...acctF, email: e.target.value })} /></Field></Col>
                    <Col md={3}><Field label="Phone (optional)"><PInput placeholder="07XXXXXXXX" value={acctF.phoneNumber} onChange={(e) => setAcctF({ ...acctF, phoneNumber: e.target.value })} /></Field></Col>
                    <Col md={3}><Field label="Contact name (optional)"><PInput placeholder="Manager name" value={acctF.name} onChange={(e) => setAcctF({ ...acctF, name: e.target.value })} /></Field></Col>
                    <Col md={2}><Field label="&nbsp;"><PButton type="submit" loading={busy === "acct"} style={{ width: "100%" }}>Create login</PButton></Field></Col>
                  </Row>
                </form>
                <div style={{ fontSize: 11, color: C.textLight }}>Creates (or links) a login that can manage only this organizer: its events and the entrance scanner, not the kill-switch. Lusuku emails them their login address and a single-use link to set their own password. When email is not getting through, Set password lets a super admin choose one and send it to them on WhatsApp; they change it after signing in.</div>
                {acctResult && (
                  <div style={{ background: C.greenSoft, border: "1px solid #c8e6c9", borderRadius: 8, padding: "11px 14px", marginTop: 12, fontSize: 13, color: C.text }}>
                    {acctResult.reset
                      ? <>Sent a set-password link to <strong>{acctResult.sentTo || acctResult.account.email}</strong>. It can be used once{acctResult.linkExpiresAt ? <> and expires at <strong>{new Date(acctResult.linkExpiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</strong></> : " and expires in a few hours"}; their current password keeps working until they use it.</>
                      : acctResult.credentialsEmailed
                      ? <>{acctResult.linkedExisting ? "Linked existing account" : "Login created"} for <strong>{acctResult.account.email || acctResult.account.phoneNumber}</strong>. Their login email and a single-use set-password link have been emailed to <strong>{acctResult.sentTo}</strong> — nobody at Lusuku ever sees their password.</>
                      : <>{acctResult.linkedExisting ? "Linked existing account" : "Login created"} for <strong>{acctResult.account.email || acctResult.account.phoneNumber}</strong>, but the set-password email could not be sent. {acctResult.message}</>}
                    {!acctResult.credentialsEmailed && setPasswordButton && (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                        <span style={{ fontSize: 12.5, color: C.textMid }}>While email is down, set a password here and send it to them on WhatsApp.</span>
                        {setPasswordButton}
                      </div>
                    )}
                    {acctResult.credentialsEmailed && acctResult.deliveryRefId ? <div style={{ marginTop: 8 }}><EmailDeliveryBadge refId={acctResult.deliveryRefId} /></div> : null}
                  </div>
                )}
              </Panel></div>
            )}

            {orgId && canManageSelected && (
              <div id="organizer-gate-staff" style={{ scrollMarginTop: 56 }}><Panel title="Gate staff" sub="scanner logins for your team — every scan records who admitted the guest" color={C.teal}>
                {staffList.length > 0 ? (
                  <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
                    <thead><tr>
                      <th style={{ textAlign: "left", fontSize: 11, color: C.textLight, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", padding: "6px 8px", borderBottom: `1px solid ${C.border}` }}>Name</th>
                      <th style={{ textAlign: "left", fontSize: 11, color: C.textLight, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", padding: "6px 8px", borderBottom: `1px solid ${C.border}` }}>Login</th>
                      <th style={{ borderBottom: `1px solid ${C.border}` }} />
                    </tr></thead>
                    <tbody>
                      {staffList.map((m) => (
                        <tr key={m._id}>
                          <td style={{ padding: "8px", fontSize: 13, fontWeight: 600, color: C.text }}>{m.name || "—"}</td>
                          <td style={{ padding: "8px", fontSize: 12.5, color: C.textMid }}>{m.email || m.phoneNumber || "—"}</td>
                          <td style={{ padding: "8px", textAlign: "right" }}>
                            <PButton variant="outline" style={{ padding: "4px 10px", fontSize: 11.5, color: "#c62828", borderColor: "#f5c6cb" }} onClick={() => removeStaff(m)}>Remove</PButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ fontSize: 12.5, color: C.textLight, marginBottom: 12 }}>No gate staff yet — add ushers below. Staff can scan tickets and view this organizer, nothing else.</div>
                )}
                <form onSubmit={addStaff}>
                  <Row className="align-items-end">
                    <Col md={4}><Field label="Staff email"><PInput type="email" placeholder="usher@email.com" value={staffF.email} onChange={(e) => setStaffF({ ...staffF, email: e.target.value })} /></Field></Col>
                    <Col md={3}><Field label="Phone (optional)"><PInput placeholder="07XXXXXXXX" value={staffF.phoneNumber} onChange={(e) => setStaffF({ ...staffF, phoneNumber: e.target.value })} /></Field></Col>
                    <Col md={3}><Field label="Name (optional)"><PInput placeholder="Staff name" value={staffF.name} onChange={(e) => setStaffF({ ...staffF, name: e.target.value })} /></Field></Col>
                    <Col md={2}><Field label="&nbsp;"><PButton type="submit" loading={busy === "staff"} style={{ width: "100%" }}>Add staff</PButton></Field></Col>
                  </Row>
                </form>
                {staffResult && (
                  <div style={{ background: C.greenSoft, border: "1px solid #c8e6c9", borderRadius: 8, padding: "11px 14px", marginTop: 12, fontSize: 13, color: C.text }}>
                    {staffResult.linkedExisting
                      ? <>Linked existing account <strong>{staffResult.staff.email || staffResult.staff.phoneNumber}</strong> as gate staff — they sign in with their existing password.</>
                      : staffResult.credentialsEmailed
                      ? <>Staff login created for <strong>{staffResult.staff.email || staffResult.staff.phoneNumber}</strong>. Their login email and a single-use set-password link have been emailed to <strong>{staffResult.sentTo}</strong> — nothing to copy or share by hand.</>
                      : <>Staff login created for <strong>{staffResult.staff.email || staffResult.staff.phoneNumber}</strong>.{staffResult.mailFallback ? <> The invite email could not be sent ({staffResult.mailFallbackReason || "mail delivery failed"}), so use this </> : <> No email inbox on file, so use this </>}one-time password: <code style={{ background: "#fff", padding: "2px 8px", borderRadius: 4, fontWeight: 700 }}>{staffResult.tempPassword}</code> — share it securely. They can set their own password later via “Forgot password” on the sign-in page.</>}
                    {staffResult.credentialsEmailed && staffResult.deliveryRefId ? <div style={{ marginTop: 8 }}><EmailDeliveryBadge refId={staffResult.deliveryRefId} /></div> : null}
                  </div>
                )}
                <div style={{ fontSize: 11, color: C.textLight, marginTop: 10 }}>
                  Staff added here can scan tickets and view this organizer, nothing else. For manager, finance or box-office roles — and to change someone's role — use the <strong>My Team</strong> page.
                </div>
              </Panel></div>
            )}

            {orgId && canManageSelected && (
              <div id="organizer-refunds" style={{ scrollMarginTop: 56 }}><Panel title="Refunds" sub="customer refund requests — approve or reject, then finance pays out" color={C.purple}>
                {refunds.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: C.textLight }}>No refund requests yet. Requests customers make from their tickets appear here for review.</div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th style={S.th}>Requested</th>
                      <th style={S.th}>Amount</th>
                      <th style={S.th}>Reason</th>
                      <th style={S.th}>Status</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Action</th>
                    </tr></thead>
                    <tbody>
                      {refunds.map((r) => (
                        <tr key={r._id}>
                          <td style={S.td}>{r.requestedAt ? new Date(r.requestedAt).toLocaleString() : "—"}</td>
                          <td style={{ ...S.td, fontWeight: 600 }}>{refundAmount(r)}</td>
                          <td style={S.td}>
                            {r.reason || "—"}
                            {r.decisionNote && <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>Note: {r.decisionNote}</div>}
                            {r.walletRefundRef && <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>Wallet ref: {r.walletRefundRef}</div>}
                          </td>
                          <td style={S.td}><span style={refundChip(r.status)}>{r.status}</span></td>
                          <td style={{ ...S.td, textAlign: "right" }}>
                            {r.status === "REQUESTED" && (
                              <span style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                                <PButton style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => decideRefund(r, true)}>Approve</PButton>
                                <PButton variant="danger" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => decideRefund(r, false)}>Reject</PButton>
                              </span>
                            )}
                            {r.status === "APPROVED" && isSuper && (
                              <span style={{ display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                                <PInput placeholder="Wallet refund ref" value={procRefF[r._id] || ""} onChange={(e) => { const v = e.target.value; setProcRefF((f) => ({ ...f, [r._id]: v })); }} style={{ width: 160, fontSize: 12 }} />
                                <PButton loading={busy === "proc" + r._id} style={{ padding: "5px 12px", fontSize: 12, whiteSpace: "nowrap" }} onClick={() => markRefundProcessed(r)}>Mark processed</PButton>
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Panel></div>
            )}

            {orgId && isStaffViewer && (
              <div style={{ background: "#eef6ff", border: "1px solid #cfe4fb", borderRadius: 10, padding: "10px 14px", marginBottom: 18, fontSize: 13, color: C.text }}>
                Read-only view — you can scan tickets at the Entrance Scanner and view this organizer's data. The organizer owner manages events and showings.
              </div>
            )}

            {orgId && (
              <>
                <div id="console-workspace" style={{ scrollMarginTop: 56, display: "flex", gap: 4, marginBottom: 18, borderBottom: `1px solid ${C.border}` }}>
                  {TABS.map(([k, label]) => (
                    <button key={k} onClick={() => setTab(k)} style={{
                      background: "none", border: "none", padding: "10px 18px", fontSize: 13, fontWeight: 700,
                      color: tab === k ? C.green : C.textLight, borderBottom: tab === k ? `2px solid ${C.green}` : "2px solid transparent",
                      cursor: "pointer", marginBottom: -1,
                    }} title={tabState[k]?.title}>
                      {label}
                      {tabState[k] && (
                        <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 900,
                                       color: tabState[k].tone === "done" ? C.green : tabState[k].tone === "warn" ? "#b26a00" : C.textLight }}>
                          {tabState[k].mark}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {tab === "events" && (
                  <>
                    <Panel title={`Events · ${selectedOrg?.name || ""}`} sub="the films / shows" color={C.blue}>
                      {canManageSelected && <div style={{ marginBottom: 10 }}><PButton variant="outline" onClick={() => setShowNewEvent((v) => !v)} style={{ padding: "6px 14px", fontSize: 12 }}>{showNewEvent ? "Close ✕" : "+ Add event"}</PButton></div>}
                      {canManageSelected && showNewEvent && (
                      <form onSubmit={createEvent}>
                        <Row>
                          <Col md={2}><ArtworkUpload label="Poster" width={104} value={evF.posterUrl} onChange={(u) => setEvF({ ...evF, posterUrl: u })} height={150} aspect="2 / 3" recommended="600 × 900px (2:3)" minWidth={400} minHeight={600} hint="Portrait — on the tile." /></Col>
                          <Col md={10}>
                            <Row className="align-items-end">
                              <Col md={5}><Field label="Event / film title"><PInput id="event-title" placeholder="e.g. The Odyssey" value={evF.title} onChange={(e) => setEvF({ ...evF, title: e.target.value })} /></Field></Col>
                              <Col md={3}><Field label="Category"><PSelect value={evF.category} onChange={(e) => setEvF({ ...evF, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</PSelect></Field></Col>
                              <Col md={2}><Field label="Age rating"><PInput placeholder="PG, 16+" value={evF.contentRating} onChange={(e) => setEvF({ ...evF, contentRating: e.target.value })} /></Field></Col>
                              <Col md={2}><Field label="Runtime"><PInput type="number" placeholder="120" value={evF.durationMinutes} onChange={(e) => setEvF({ ...evF, durationMinutes: e.target.value })} /></Field></Col>
                            </Row>
                            <Row>
                              <Col md={4}><Field label="Trailer URL" hint="YouTube or Vimeo HTTPS link."><PInput placeholder="https://youtu.be/..." value={evF.trailerUrl} onChange={(e) => setEvF({ ...evF, trailerUrl: e.target.value })} /></Field></Col>
                              <Col md={3}><Field label="Director"><PInput value={evF.director} onChange={(e) => setEvF({ ...evF, director: e.target.value })} /></Field></Col>
                              <Col md={3}><Field label="Genres" hint="Comma separated."><PInput placeholder="Action, Adventure" value={evF.genres} onChange={(e) => setEvF({ ...evF, genres: e.target.value })} /></Field></Col>
                              <Col md={2}><Field label="Language"><PInput value={evF.language} onChange={(e) => setEvF({ ...evF, language: e.target.value })} /></Field></Col>
                            </Row>
                            <Row>
                              <Col md={5}><Field label="Cast" hint="Comma separated."><PInput placeholder="Lead actor, supporting actor" value={evF.cast} onChange={(e) => setEvF({ ...evF, cast: e.target.value })} /></Field></Col>
                              <Col md={3}><Field label="Studio"><PInput value={evF.studio} onChange={(e) => setEvF({ ...evF, studio: e.target.value })} /></Field></Col>
                              <Col md={2}><Field label="Release date"><PInput type="date" value={evF.releaseDate} onChange={(e) => setEvF({ ...evF, releaseDate: e.target.value })} /></Field></Col>
                              <Col md={2}><Field label="Customer state"><PSelect value={evF.status} onChange={(e) => setEvF({ ...evF, status: e.target.value })}><option value="DRAFT">Draft</option><option value="COMING_SOON">Coming soon</option></PSelect></Field></Col>
                            </Row>
                            <Row><Col md={12}><Field label="Synopsis"><textarea rows={3} value={evF.synopsis} onChange={(e) => setEvF({ ...evF, synopsis: e.target.value })} style={{ width: "100%", border: `1px solid ${C.border}`, borderRadius: 6, padding: "9px 10px", fontSize: 12.5, resize: "vertical" }} /></Field></Col></Row>
                            <Row className="align-items-end">
                              <Col md={8}><ArtworkUpload label="Banner (optional)" value={evF.bannerUrl} onChange={(u) => setEvF({ ...evF, bannerUrl: u })} height={70} aspect="16 / 5" recommended="1600 × 500px (16:5)" minWidth={1200} minHeight={375} hint="Wide — on the event page." /></Col>
                              <Col md={4}><Field label="&nbsp;"><PButton type="submit" loading={busy === "event"} style={{ width: "100%" }}>Add event</PButton></Field></Col>
                            </Row>
                          </Col>
                        </Row>
                      </form>
                      )}
                      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}>
                        <thead><tr><th style={S.th}>Event</th><th style={S.th}>Category</th><th style={S.th}>Status</th><th style={{ ...S.th, textAlign: "right" }}>Showings</th></tr></thead>
                        <tbody>
                          {events.length === 0 && <tr><td style={{ ...S.td, color: C.textLight }} colSpan={4}>No events yet — add your first above.</td></tr>}
                          {events.map((e) => (
                            <tr key={e._id} style={{ background: e._id === eventId ? C.greenSoft : undefined }}>
                              <td style={{ ...S.td, fontWeight: 600 }}>{e.title}</td>
                              <td style={S.td}>{e.category}</td>
                              <td style={S.td}><Status s={e.status} /></td>
                              <td style={{ ...S.td, textAlign: "right" }}>
                                <span style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                                <PButton variant={e._id === eventId ? "solid" : "outline"} style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => setEventId(e._id === eventId ? "" : e._id)}>
                                  {e._id === eventId ? "Managing ✓" : "Manage showings"}</PButton>
                                <PButton variant="danger" loading={busy === "delev" + e._id} style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => removeEvent(e)}>Delete</PButton>
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Panel>

                    {selectedEvent && (
                      <Panel title={`Showings · ${selectedEvent.title}`} sub="dates & times on sale" color={C.orange}>
                        {canManageSelected && (<>
                        <div style={S.sectionBreak}>Event artwork — shown to customers on the web</div>
                        <div style={ART.tray}>
                          <div style={ART.trayHead}>
                            <span style={ART.trayTitle}>Customer-facing artwork</span>
                            <span style={S.badge(selectedEvent.posterUrl ? C.green : "#b26a00", selectedEvent.posterUrl ? C.greenSoft : "#fff8e1")}>{selectedEvent.posterUrl ? "Poster live" : "Poster missing"}</span>
                            <span style={S.badge(selectedEvent.bannerUrl ? C.green : C.textMid, selectedEvent.bannerUrl ? C.greenSoft : "#eef1f5")}>{selectedEvent.bannerUrl ? "Banner live" : "Banner optional"}</span>
                            <span style={ART.trayNote}>Uploads replace the live artwork immediately — previews are the exact shape customers see.</span>
                          </div>
                          <Row>
                            <Col md={3} style={{ display: "flex" }}>
                              <div style={ART.card}>
                                <div style={ART.cardHead}>
                                  <span style={ART.cardTitle}>Poster</span>
                                  <span style={S.badge(C.blue, C.blueSoft)}>2:3 portrait</span>
                                </div>
                                <ArtworkUpload width={104} height={150} value={selectedEvent.posterUrl || ""} onChange={(u) => updateEventArt({ posterUrl: u })} aspect="2 / 3" recommended="600 × 900px" minWidth={400} minHeight={600} />
                                <div style={ART.cardFoot}>The event tile and the movie page. Studio one-sheets are already this shape — upload the full-resolution original.</div>
                              </div>
                            </Col>
                            <Col md={9} style={{ display: "flex" }}>
                              <div style={ART.card}>
                                <div style={ART.cardHead}>
                                  <span style={ART.cardTitle}>Banner</span>
                                  <span style={S.badge(C.orange, C.orangeSoft)}>16:5 wide</span>
                                </div>
                                <ArtworkUpload height={90} aspect="16 / 5" value={selectedEvent.bannerUrl || ""} onChange={(u) => updateEventArt({ bannerUrl: u })} recommended="1600 × 500px" minWidth={1200} minHeight={375} />
                                <div style={ART.cardFoot}>The hero strip across the top of the event page. Optional — without one the page falls back to a dark backdrop. Keep titles and faces near the centre; edges may be trimmed on narrow screens.</div>
                              </div>
                            </Col>
                          </Row>
                        </div>
                        <form onSubmit={saveEventDetails} style={{ background: "#f8fafc", border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginBottom: 12 }}>
                          <Row>
                            <Col md={4}><Field label="Title *"><PInput id="edit-ev-title" value={editEvF.title} onChange={(e) => setEditEvF({ ...editEvF, title: e.target.value })} /></Field></Col>
                            <Col md={2}><Field label="Age rating"><PInput value={editEvF.contentRating || ""} onChange={(e) => setEditEvF({ ...editEvF, contentRating: e.target.value })} /></Field></Col>
                            <Col md={2}><Field label="Runtime (min)"><PInput type="number" value={editEvF.durationMinutes || ""} onChange={(e) => setEditEvF({ ...editEvF, durationMinutes: e.target.value })} /></Field></Col>
                            <Col md={2}><Field label="Language"><PInput value={editEvF.language || ""} onChange={(e) => setEditEvF({ ...editEvF, language: e.target.value })} /></Field></Col>
                            <Col md={2}><Field label="Subtitles"><PInput value={editEvF.subtitleLanguage || ""} onChange={(e) => setEditEvF({ ...editEvF, subtitleLanguage: e.target.value })} /></Field></Col>
                          </Row>
                          <Row>
                            <Col md={4}><Field label="Trailer URL"><PInput placeholder="https://youtu.be/..." value={editEvF.trailerUrl || ""} onChange={(e) => setEditEvF({ ...editEvF, trailerUrl: e.target.value })} /></Field></Col>
                            <Col md={3}><Field label="Director"><PInput value={editEvF.director || ""} onChange={(e) => setEditEvF({ ...editEvF, director: e.target.value })} /></Field></Col>
                            <Col md={3}><Field label="Genres"><PInput value={editEvF.genres || ""} onChange={(e) => setEditEvF({ ...editEvF, genres: e.target.value })} /></Field></Col>
                            <Col md={2}><Field label="Release date"><PInput type="date" value={editEvF.releaseDate || ""} onChange={(e) => setEditEvF({ ...editEvF, releaseDate: e.target.value })} /></Field></Col>
                          </Row>
                          <Row>
                            <Col md={6}><Field label="Cast"><PInput value={editEvF.cast || ""} onChange={(e) => setEditEvF({ ...editEvF, cast: e.target.value })} /></Field></Col>
                            <Col md={3}><Field label="Studio"><PInput value={editEvF.studio || ""} onChange={(e) => setEditEvF({ ...editEvF, studio: e.target.value })} /></Field></Col>
                            <Col md={3}><Field label="Customer state"><PSelect disabled={selectedEvent.status === "PUBLISHED"} value={selectedEvent.status === "PUBLISHED" ? "PUBLISHED" : editEvF.status} onChange={(e) => setEditEvF({ ...editEvF, status: e.target.value })}><option value="DRAFT">Draft</option><option value="COMING_SOON">Coming soon</option>{selectedEvent.status === "PUBLISHED" && <option value="PUBLISHED">Now showing</option>}</PSelect></Field></Col>
                          </Row>
                          <Field label="Synopsis"><textarea rows={3} value={editEvF.synopsis || ""} onChange={(e) => setEditEvF({ ...editEvF, synopsis: e.target.value })} style={{ width: "100%", border: `1px solid ${C.border}`, borderRadius: 6, padding: "9px 10px", fontSize: 12.5, resize: "vertical" }} /></Field>
                          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                            <PButton type="submit" loading={busy === "event-details"} disabled={!evDirty}>{evDirty ? "Save movie details" : "Saved"}</PButton>
                            {evDirty && <span style={{ fontSize: 11.5, color: "#b26a00", fontWeight: 700 }}>Unsaved changes</span>}
                            {evServerMoved && (
                              <span style={{ fontSize: 11.5, color: C.textMid, display: "flex", alignItems: "center", gap: 8 }}>
                                This movie was updated elsewhere — your edits are kept.
                                <button type="button" onClick={() => { const ev = events.find((i) => i._id === eventId); if (ev) { evSeedRef.current = `${ev._id}:${ev.updatedAt || ""}`; seedEditEv(ev); } }}
                                  style={{ background: "none", border: "none", padding: 0, color: C.green, fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>Load the server copy</button>
                              </span>
                            )}
                          </div>
                        </form>
                        </>)}
                        <div id="showtimes-section" style={S.sectionBreak}>Showtimes</div>
                        {selectedEvent && (
                          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                                        background: draftCount > 0 ? "#fff8e1" : liveUpcoming > 0 ? "#f3f9ee" : "#f8fafc",
                                        border: `1px solid ${draftCount > 0 ? "#f5d78e" : liveUpcoming > 0 ? "#cde6b4" : C.border}`,
                                        borderRadius: 8, padding: "9px 13px", marginBottom: 10 }}>
                            <span style={{ fontSize: 12.5, fontWeight: 800, color: C.text }}>
                              {selectedEvent.title}
                              {upcomingShowings.length > 0 && (
                                <span style={{ fontWeight: 600, color: C.textMid }}>
                                  {" · "}{scheduleVenues} {isCinemaOrg ? (scheduleVenues === 1 ? "cinema" : "cinemas") : (scheduleVenues === 1 ? "venue" : "venues")}
                                  {scheduleRange ? ` · ${scheduleRange}` : ""}
                                  {" · "}{upcomingShowings.length} showtime{upcomingShowings.length === 1 ? "" : "s"}
                                </span>
                              )}
                            </span>
                            <span style={{ fontSize: 12.5, fontWeight: 800, color: draftCount > 0 ? "#8a5a00" : liveUpcoming > 0 ? "#2e7d32" : C.textLight }}>
                              {upcomingShowings.length === 0
                                ? "● Nothing upcoming — customers see no showtimes for this event"
                                : draftCount > 0
                                  ? `● ${liveUpcoming} on sale, ${draftCount} still a draft`
                                  : "● On sale"}
                            </span>
                            {draftCount > 0 && (
                              <span style={{ fontSize: 11.5, color: "#8a5a00", flexBasis: "100%" }}>
                                Customers cannot see or buy {draftCount === 1 ? "a draft" : "drafts"} until you publish {draftCount === 1 ? "it" : "them"}.
                              </span>
                            )}
                            {publishableDraftIds.length > 0 && canManageSelected && onboardingApproved && (
                              <AppButton icon="fa fa-ticket-alt" loading={busy === "bulkpub"} disabled={busy === "dedupe"} style={{ marginLeft: "auto" }}
                                title={duplicateDrafts.length ? `Publishes one copy of each showtime. The ${duplicateDrafts.length} duplicate${duplicateDrafts.length === 1 ? " is" : "s are"} left as draft${duplicateDrafts.length === 1 ? "" : "s"}.` : undefined}
                                onClick={() => publishIds(publishableDraftIds)}>
                                Publish all {publishableDraftIds.length}
                              </AppButton>
                            )}
                            {(busy === "bulkpub" || busy === "dedupe") && (
                              <BulkProgress progress={bulkProgress} />
                            )}
                            {duplicateDrafts.length > 0 && canManageSelected && (
                              <span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", flexBasis: "100%", borderTop: "1px dashed #f5d78e", paddingTop: 8 }}>
                                <span style={{ fontSize: 11.5, color: "#8a5a00", fontWeight: 700, flex: "1 1 260px" }}>
                                  {duplicateDrafts.length} duplicate draft{duplicateDrafts.length === 1 ? "" : "s"}: {duplicateSlots} showtime{duplicateSlots === 1 ? " was" : "s were"} saved more than once on the same screen. Putting the copies on sale would sell the same seats twice.
                                </span>
                                <AppButton variant="danger" size="sm" icon="fa fa-trash" loading={busy === "dedupe"} disabled={busy === "bulkpub"} onClick={() => removeDuplicateDrafts(duplicateDrafts)}>
                                  Remove {duplicateDrafts.length} duplicate{duplicateDrafts.length === 1 ? "" : "s"}
                                </AppButton>
                              </span>
                            )}
                            {draftCount > 0 && canManageSelected && !onboardingApproved && (
                              <span style={{ fontSize: 11.5, color: "#8a5a00", marginLeft: "auto" }}>Onboarding must be approved before anything can go on sale.</span>
                            )}
                          </div>
                        )}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                          {[["UPCOMING", `Upcoming (${counts.upcoming})`], ["DRAFT", `Drafts (${counts.draft})`], ["LIVE", `Live (${counts.live})`], ["PAST", `Past (${counts.past})`], ["ALL", `All (${showings.length})`]].map(([key, label]) => (
                            <button key={key} type="button" onClick={() => { setShowingFilter(key); setSelectedShowings([]); }}
                              style={{ border: `1px solid ${showingFilter === key ? C.green : C.border}`, background: showingFilter === key ? C.greenSoft : "#fff", color: showingFilter === key ? "#2e7d32" : C.textMid, borderRadius: 20, padding: "4px 12px", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
                              {label}
                            </button>
                          ))}
                          <span style={{ display: "inline-flex", border: `1px solid ${C.border}`, borderRadius: 20, overflow: "hidden" }}>
                            {[["GRID", "Grid"], ["LIST", "List"]].map(([key, label]) => (
                              <button key={key} type="button" onClick={() => setShowingView(key)}
                                style={{ border: "none", background: resolvedView === key ? C.greenSoft : "#fff", color: resolvedView === key ? "#2e7d32" : C.textMid, padding: "4px 14px", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
                                {label}
                              </button>
                            ))}
                          </span>
                          {selectedShowings.length > 0 && (
                            <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ fontSize: 12, color: C.textMid, fontWeight: 700 }}>{selectedShowings.length} selected</span>
                              <PButton loading={busy === "bulkpub"} style={{ padding: "5px 12px", fontSize: 12 }} onClick={publishSelected}>Publish {selectedShowings.length}</PButton>
                              {busy === "bulkpub" && <BulkProgress progress={bulkProgress} />}
                              <PButton variant="ghost" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => setSelectedShowings([])}>Clear</PButton>
                            </span>
                          )}
                        </div>
                        {resolvedView === "GRID" ? (
                          <div style={{ overflowX: "auto", marginBottom: 8 }}>
                            {gridRows.length === 0 ? (
                              <div style={{ ...S.td, color: C.textLight, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 13px" }}>
                                {showings.length === 0 ? "No showings yet — add one below." : "No showtimes match this filter."}
                              </div>
                            ) : (
                              <table style={{ borderCollapse: "separate", borderSpacing: 0, minWidth: "100%" }}>
                                <thead>
                                  <tr>
                                    <th style={{ ...S.th, position: "sticky", left: 0, background: "#fff", zIndex: 1, minWidth: 210 }}>{spaceNoun}</th>
                                    {gridDays.map((d) => (
                                      <th key={d.toDateString()} style={{ ...S.th, textAlign: "center", minWidth: 96 }}>
                                        <div>{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                                        <div style={{ fontSize: 13, color: C.text }}>{d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}</div>
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {gridRows.map((row) => (
                                    <tr key={row.key}>
                                      <td style={{ ...S.td, position: "sticky", left: 0, background: "#fff", zIndex: 1, verticalAlign: "top" }}>
                                        <div style={{ fontWeight: 700, color: C.text }}>{row.venue}</div>
                                        <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
                                          {[row.screen, row.experience].filter(Boolean).join(" · ")}
                                        </div>
                                      </td>
                                      {gridDays.map((d) => {
                                        const cell = row.cells.get(d.toDateString()) || [];
                                        return (
                                          <td key={d.toDateString()} style={{ ...S.td, verticalAlign: "top", textAlign: "center" }}>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "stretch" }}>
                                              {cell.map((sh) => {
                                                const past = isPast(sh);
                                                const cancelled = sh.status === "CANCELLED";
                                                const live = sh.status === "PUBLISHED";
                                                const bg = cancelled ? "#f1f3f5" : past ? "#f8fafc" : live ? C.greenSoft : "#fff8e1";
                                                const bd = cancelled ? C.border : past ? C.border : live ? "#c8e6c9" : "#f5d78e";
                                                const fg = cancelled ? C.textLight : past ? C.textLight : live ? "#2e7d32" : "#8a5a00";
                                                return (
                                                  <button key={sh._id} type="button"
                                                    onClick={() => (canManageSelected && !past && !cancelled ? startEditShowing(sh) : undefined)}
                                                    title={`${live ? "On sale" : cancelled ? "Cancelled" : "Draft — not on sale"} · ${sh.seatsSold ?? 0}${sh.seatsTotal ? " / " + sh.seatsTotal : ""} sold${past ? " · ended" : ""}`}
                                                    style={{ border: `1px solid ${bd}`, background: bg, color: fg, borderRadius: 6, padding: "4px 6px",
                                                             fontSize: 12, fontWeight: 700, cursor: canManageSelected && !past && !cancelled ? "pointer" : "default",
                                                             textDecoration: cancelled ? "line-through" : "none" }}>
                                                    {fmtClock(sh.startsAt)}
                                                    {Number(sh.seatsSold) > 0 && (
                                                      <span style={{ display: "block", fontSize: 9.5, fontWeight: 600, opacity: 0.85 }}>{sh.seatsSold} sold</span>
                                                    )}
                                                  </button>
                                                );
                                              })}
                                              {canManageSelected && d.getTime() >= new Date().setHours(0, 0, 0, 0) && (
                                                <button type="button" onClick={() => prefillShowing(row.template, d)}
                                                  title={`Add another ${row.experience} showtime at ${row.venue} on ${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })} — copies this row's layout and prices`}
                                                  style={{ border: `1px dashed ${C.border}`, background: "none", color: C.textLight, borderRadius: 6, padding: "3px 6px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                                                  +
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, fontSize: 11, color: C.textLight }}>
                              <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: C.greenSoft, border: "1px solid #c8e6c9", marginRight: 5 }} />On sale</span>
                              <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "#fff8e1", border: "1px solid #f5d78e", marginRight: 5 }} />Draft — customers can't see it</span>
                              <span>Click a time to edit it · <strong style={{ color: C.textMid }}>+</strong> copies this row onto that day</span>
                              <span style={{ marginLeft: "auto" }}>Switch to <strong style={{ color: C.textMid }}>List</strong> to select and publish in bulk.</span>
                            </div>
                          </div>
                        ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8 }}>
                          <thead><tr>
                            <th style={{ ...S.th, width: 34 }}>
                              {selectableShowings.length > 0 && (
                                <input type="checkbox" aria-label="Select all draft showtimes"
                                  checked={selectedShowings.length > 0 && selectedShowings.length === selectableShowings.length}
                                  onChange={(e) => setSelectedShowings(e.target.checked ? selectableShowings.map((s) => String(s._id)) : [])} />
                              )}
                            </th>
                            <th style={S.th}>Starts</th><th style={S.th}>Venue</th><th style={S.th}>Experience</th><th style={S.th}>Type</th><th style={S.th}>Sold</th><th style={S.th}>Status</th><th style={{ ...S.th, textAlign: "right" }}>Action</th>
                          </tr></thead>
                          <tbody>
                            {visibleShowings.length === 0 && <tr><td style={{ ...S.td, color: C.textLight }} colSpan={8}>{showings.length === 0 ? "No showings yet." : "No showtimes match this filter."}</td></tr>}
                            {visibleShowings.map((s) => (
                              <tr key={s._id}>
                                <td style={S.td}>
                                  {s.status !== "PUBLISHED" && s.status !== "CANCELLED" && canManageSelected && (
                                    <input type="checkbox" aria-label="Select this showtime"
                                      checked={selectedShowings.includes(String(s._id))}
                                      onChange={(e) => setSelectedShowings((prev) => (e.target.checked ? [...prev, String(s._id)] : prev.filter((id) => id !== String(s._id))))} />
                                  )}
                                </td>
                                <td style={S.td}>{s.startsAt ? new Date(s.startsAt).toLocaleString() : "—"}</td>
                                <td style={S.td}>{venueName(s.venueId)}</td>
                                <td style={S.td}>{presentationLabel(s) || "Admission"}{s.presentation?.screenName ? <div style={{ fontSize: 11, color: C.textLight, marginTop: 3 }}>{s.presentation.screenName}</div> : null}</td>
                                <td style={S.td}>{s.inventoryModel === "RESERVED_SEATING" ? "Reserved seats" : s.inventoryModel === "RESERVED_UNITS" ? `Reserved ${String(s.inventoryUnitLabel || "units").toLowerCase()}s` : s.inventoryModel === "GENERAL_ADMISSION" ? "General admission" : s.inventoryModel === "ZONED" ? "Zoned admission" : s.inventoryModel}</td>
                                <td style={S.td}>{s.seatsSold ?? 0}{s.seatsTotal ? ` / ${s.seatsTotal}` : ""}</td>
                                <td style={S.td}>
                                  <Status s={s.status} startsAt={s.startsAt} />
                                  {s.publishedAt && (
                                    <div style={{ fontSize: 11, color: C.textLight, marginTop: 3 }} title="When this showtime went on sale">
                                      Activated {new Date(s.publishedAt).toLocaleString()}
                                    </div>
                                  )}
                                </td>
                                {/* Status lives in the STATUS chip — the action cell holds
                                    ACTIONS only (no duplicate "Live"/"Ended" labels). */}
                                <td style={{ ...S.td, textAlign: "right", verticalAlign: "middle" }}>{s.status !== "PUBLISHED"
                                  ? (canManageSelected
                                    ? (s.status === "CANCELLED"
                                      // Cancelled is terminal — offering Publish or Edit here
                                      // would promise something the server refuses.
                                      ? <span style={{ color: C.textLight, fontSize: 12 }}>—</span>
                                      : <span style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}><PButton variant="ghost" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => startEditShowing(s)}>Edit</PButton><PButton loading={busy === "pub" + s._id} disabled={!onboardingApproved} title={onboardingApproved ? undefined : "Organizer onboarding must be approved before a showtime can go on sale."} style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => publish(s._id)}>Publish</PButton><PButton variant="danger" loading={busy === "del" + s._id} style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => removeShowing(s._id)}>Delete</PButton></span>)
                                    : <span style={{ color: C.textLight, fontSize: 12 }}>—</span>)
                                  : (s.startsAt && new Date(s.startsAt).getTime() < Date.now()
                                    ? <span style={{ color: C.textLight, fontSize: 12 }}>—</span>
                                    : (canManageSelected
                                      ? <span style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}><PButton variant="ghost" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => startEditShowing(s)}>Edit</PButton><PButton variant="outline" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => unpublish(s)}>Unpublish</PButton><PButton variant="danger" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => cancelShowing(s)}>Cancel</PButton></span>
                                      : <span style={{ color: C.textLight, fontSize: 12 }}>—</span>))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        )}

                        {canManageSelected && (<>
                        <div style={S.sectionBreak}>{editShowingId ? "Edit showing" : "Add a showing"}</div>
                        {editShowingId && (
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                                        background: "#eef6ff", border: "1px solid #cfe3fb", borderRadius: 8,
                                        padding: "9px 12px", marginBottom: 12, fontSize: 12.5, color: C.textMid }}>
                            <span>
                              Editing an existing showtime — saving overwrites it rather than adding a new one.
                              {" "}The layout, inventory model, and prices are locked once reserved units have been created.
                            </span>
                            <PButton variant="ghost" style={{ padding: "4px 12px", fontSize: 12, marginLeft: "auto" }}
                              onClick={cancelEditShowing}>Cancel edit</PButton>
                          </div>
                        )}
                        <form onSubmit={createShowing}>
                          <style>{SHX_CSS}</style>
                          <div className="shx-grid">
                            <StepRail steps={[
                              { key: "when", step: "when", done: stepWhenDone, label: "When & where", target: "shx-card-when" },
                              { key: "exp", step: "exp", done: stepWhenDone, label: "Experience", target: "shx-card-exp" },
                              { key: "seats", step: "seats", done: stepInvDone, label: "Seats", target: "shx-card-inv" },
                              { key: "price", step: "price", done: stepPriceDone, label: "Tickets & prices", target: "shx-card-tt" },
                            ]} />
                            <div style={{ minWidth: 0 }}>

                            <StepCard id="shx-card-when" step="when" n="1" title="When & where" done={stepWhenDone} summary={stepWhenSummary} hint={stepWhenHint}>
                              <Row>
                                <Col md={4}><Field label="Venue *" hint="Picked first — it decides which layouts appear in step 3.">
                                  <PSelect id="sh-venue" value={shF.venueId} style={shErr === "venue" ? { borderColor: C.accent } : undefined} onChange={(e) => { const venueId = e.target.value; setShErr(null); setShF((f) => { const keep = seatMaps.find((m) => m._id === f.seatMapId); const stillValid = keep && (!keep.venueId || !venueId || String(keep.venueId) === String(venueId)); return { ...f, venueId, seatMapId: stillValid ? f.seatMapId : "" }; }); }}><option value="">— select a venue —</option>{venues.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}</PSelect>
                                </Field></Col>
                                <Col md={4}><Field label="Starts *" hint={BROWSER_TZ ? `Showtime the customer sees — entered in ${BROWSER_TZ}.` : "Showtime the customer sees."}>
                                  <div id="sh-starts" style={shErr === "starts" ? { outline: "1px solid " + C.accent, borderRadius: 6 } : undefined}>
                                    <DateTimeField value={shF.startsAt} onChange={(v) => { setShErr(null); setShF({ ...shF, startsAt: v }); }} placeholder="Pick date & time" />
                                  </div>
                                  {shErr === "starts" && <div style={{ fontSize: 11, color: C.accent, marginTop: 3 }}>Required.</div>}
                                </Field></Col>
                                {!editShowingId && (
                                  <Col md={4}><Field label="More times, same day" hint="Optional. One draft showing per time — venue, screen and prices are reused.">
                                    <PInput placeholder="e.g. 15:30, 18:45, 10:00 pm" value={bulkF.times} onChange={(e) => setBulkF({ ...bulkF, times: e.target.value })} />
                                  </Field></Col>
                                )}
                              </Row>
                              <SubDivider>Scheduling options</SubDivider>
                              <Row>
                                {!editShowingId && (
                                  <Col md={3}><Field label="Repeat daily until" hint="Repeats all the times above every day through this date.">
                                    <PInput type="date" value={bulkF.repeatUntil} onChange={(e) => setBulkF({ ...bulkF, repeatUntil: e.target.value })} />
                                  </Field></Col>
                                )}
                                <Col md={3}><Field label="Doors open" hint="Optional."><DateTimeField value={shF.doorsAt} onChange={(v) => setShF({ ...shF, doorsAt: v })} placeholder="Optional" /></Field></Col>
                                <Col md={3}><Field label="Sales close" hint="Optional. Default: showtime."><DateTimeField value={shF.salesCloseAt} onChange={(v) => setShF({ ...shF, salesCloseAt: v })} placeholder="Optional" /></Field></Col>
                                {bulkCount > 1 && (
                                  <Col md={3} style={{ alignSelf: "center" }}>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.orangeSoft, border: "1px solid #f5d78e", borderRadius: 20, padding: "5px 13px", fontSize: 11.5, fontWeight: 800, color: "#8a5a00" }}>
                                      {bulkCount} draft showings
                                    </span>
                                  </Col>
                                )}
                              </Row>
                            </StepCard>

                            <StepCard id="shx-card-exp" step="exp" n="2" title="Experience" done
                                      summary={[shF.presentation.format, shF.presentation.dimension, shF.presentation.sound, shF.presentation.screenName].filter(Boolean).join(" · ") || "Optional — how it shows on the ticket"}
                                      hint="Optional — how it shows on the ticket">
                              <Row>
                                <Col md={3}><Field label={isCinemaOrg ? "Experience format" : "Ticket area / experience"}
                                                    hint={isCinemaOrg ? "Standard, MAX, IMAX, VIP" : "Optional — e.g. Seated, Standing, VIP, Ringside. Groups the times customers see."}>
                                  <PInput value={shF.presentation.format} onChange={(e) => setShF({ ...shF, presentation: { ...shF.presentation, format: e.target.value } })} />
                                </Field></Col>
                                {isCinemaOrg && (
                                  <Col md={3}><Field label="Projection"><PSelect value={shF.presentation.dimension} onChange={(e) => setShF({ ...shF, presentation: { ...shF.presentation, dimension: e.target.value } })}><option value="">—</option><option>2D</option><option>3D</option><option>4DX</option><option>OTHER</option></PSelect></Field></Col>
                                )}
                                <Col md={3}><Field label="Sound" hint="Dolby Atmos, 7.1"><PInput value={shF.presentation.sound} onChange={(e) => setShF({ ...shF, presentation: { ...shF.presentation, sound: e.target.value } })} /></Field></Col>
                                <Col md={3}><Field label={isCinemaOrg ? "Screen / auditorium" : `${spaceNoun} / area`}>
                                  <PInput placeholder={isCinemaOrg ? "Screen 1" : "e.g. Main Arena, Hall B"} value={shF.presentation.screenName} onChange={(e) => setShF({ ...shF, presentation: { ...shF.presentation, screenName: e.target.value } })} />
                                </Field></Col>
                              </Row>
                              {selectedOrg?.fulfillmentProvider === "EXTERNAL_API" && (
                                <Row><Col md={6}><Field label="External showing reference *" hint="The immutable session/showtime id supplied by the cinema API."><PInput value={shF.externalShowingRef} onChange={(e) => setShF({ ...shF, externalShowingRef: e.target.value })} /></Field></Col></Row>
                              )}
                            </StepCard>

                            <StepCard id="shx-card-inv" step="seats" n="3" title="Seats" done={stepInvDone} summary={stepInvSummary} hint="Pick the layout this showtime sells seats from">
                              <label style={S.label}>How are places sold?</label>
                              <div className="shx-modelgrid" style={{ marginTop: 4 }}>
                                {[
                                  { id: "RESERVED_SEATING", title: "Reserved seats", desc: "Customers pick an exact seat on a map.", color: C.purple, soft: C.purpleSoft, icon: SHX_STEPS.seats.icon },
                                  { id: "RESERVED_UNITS", title: "Tables / booths", desc: "A whole table, booth or box per purchase.", color: C.teal, soft: C.tealSoft, icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="9" /></svg> },
                                  { id: "GENERAL_ADMISSION", title: "General admission", desc: "No seat picking — capacity only.", color: C.blue, soft: C.blueSoft, icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg> },
                                  { id: "ZONED", title: "Named zones", desc: "Sections with their own capacity (VIP, Regular…).", color: C.orange, soft: C.orangeSoft, icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h18v7H3z" /></svg> },
                                ].map((m) => (
                                  <button key={m.id} type="button" className={"shx-model" + (shF.inventoryModel === m.id ? " on" : "")} onClick={() => pickInventoryModel(m.id)}>
                                    <span style={{ width: 30, height: 30, borderRadius: 8, background: m.soft, color: m.color, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>{m.icon}</span>
                                    <span style={{ display: "block", fontSize: 12.5, fontWeight: 800, color: C.text, marginBottom: 2 }}>{m.title}</span>
                                    <span style={{ display: "block", fontSize: 10.5, color: C.textLight, lineHeight: 1.4 }}>{m.desc}</span>
                                  </button>
                                ))}
                              </div>
                              {reserved && (
                                <Row style={{ marginTop: 16 }}>
                                  <Col md={6}><Field label="Layout map *" hint={seatMaps.length === 0 ? "No layouts yet — create one in Reserved Layouts." : !shF.venueId ? "Locked until a venue is picked in step 1 — only that outlet's halls are listed." : venueMaps.length === 0 ? `No layout belongs to ${venueName(shF.venueId)} yet — create one in Reserved Layouts.` : null}>
                                    <PSelect id="sh-seatmap" value={shF.seatMapId} style={shErr === "seatmap" ? { borderColor: C.accent } : undefined} onChange={(e) => { const id = e.target.value; const cls = seatMapClasses(seatMaps.find((m) => m._id === id)); setShErr(null); setShF((f) => ({ ...f, seatMapId: id, ticketTypes: f.ticketTypes.map((t, idx) => (idx === 0 && !t.seatClasses.trim() ? { ...t, seatClasses: cls.join(", ") } : t)) })); }}>
                                      <option value="">— select a layout —</option>{venueMaps.map((m) => <option key={m._id} value={m._id}>{m.name} ({m.seatCount} units)</option>)}
                                    </PSelect>
                                  </Field></Col>
                                  {mapClasses.length > 0 && (
                                    <Col md={6}><Field label="Seat classes in this hall" hint="Every class must be covered by at least one ticket type in step 4.">
                                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingTop: 8 }}>
                                        {mapClasses.map((cl) => {
                                          const [fg, bgSoft] = classColor(mapClasses, cl);
                                          return <span key={cl} style={{ background: bgSoft, color: fg, borderRadius: 20, padding: "3px 11px", fontSize: 10.5, fontWeight: 800, letterSpacing: ".4px", textTransform: "uppercase" }}>{cl}</span>;
                                        })}
                                      </div>
                                    </Field></Col>
                                  )}
                                  {reservedUnits && <Col md={4}><Field label="Reserved unit name" hint="Singular: Table, Booth, Box, Pod"><PInput value={shF.inventoryUnitLabel} placeholder="Table" onChange={(e) => setShF({ ...shF, inventoryUnitLabel: e.target.value })} /></Field></Col>}
                                </Row>
                              )}
                              {reserved && selectedMap && <SeatPreview map={selectedMap} classes={mapClasses} />}
                            </StepCard>

                            <StepCard id="shx-card-tt" step="price" n="4" title="Tickets & prices" done={stepPriceDone} summary={stepPriceSummary} hint="Add at least one price">
                              <div className="shx-tt-intro">
                                <span>{reserved ? "Each ticket type is one price customers can pick, for example Standard and VIP. Seat classes decide which seats that price books." : "Each ticket type is one price customers can pick. Capacity is the number of sellable tickets, zones, or packages."}</span>
                                <AppButton icon="fa fa-plus" onClick={addTT}>Add ticket type</AppButton>
                              </div>
                              {shF.ticketTypes.map((t, i) => (
                                <TicketTypeCard key={i} index={i} t={t} reserved={reserved} mapClasses={mapClasses} errorField={shErr}
                                  canRemove={shF.ticketTypes.length > 1}
                                  onName={(v) => { setShErr(null); setTTName(i, v); }}
                                  onField={(k, v) => { setShErr(null); setTT(i, k, v); }}
                                  onRemove={() => removeTT(i)} />
                              ))}
                              {reserved && mapClasses.length > 0 && (() => {
                                const covered = new Set();
                                shF.ticketTypes.forEach((t) => splitClasses(t.seatClasses).forEach((cl) => covered.add(normClass(cl))));
                                const missing = mapClasses.filter((cl) => !covered.has(normClass(cl)));
                                return missing.length
                                  ? <div className="shx-cover warn"><strong>{missing.join(", ")}</strong> seats have no price yet, so customers can't book them. Tap that class on one of the ticket types above.</div>
                                  : <div className="shx-cover ok">{TT_ICONS.check}Every seat class in this layout is covered by a price.</div>;
                              })()}
                            </StepCard>

                            </div>{/* main column */}

                            <div className="shx-summary-col">
                              <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: C.shadow, overflow: "hidden" }}>
                                <div style={{ background: `linear-gradient(135deg, ${C.header} 0%, #243b6e 100%)`, color: "#fff", padding: "12px 16px", fontSize: 12, fontWeight: 800, letterSpacing: ".6px", textTransform: "uppercase" }}>Showing preview</div>
                                {lastBatch && (
                                  <div style={{ padding: "12px 16px 4px" }}>
                                    <BatchResult batch={lastBatch} canPublish={canManageSelected && onboardingApproved} publishing={busy === "bulkpub"} onPublish={publishBatch} onDismiss={() => setLastBatch(null)} />
                                  </div>
                                )}
                                {[
                                  { ic: "when", fg: C.blue, bg: C.blueSoft, k: "When", v: shF.startsAt ? [shortWhen(shF.startsAt), extraSlots ? `+${extraSlots} more time${extraSlots === 1 ? "" : "s"}` : "", bulkF.repeatUntil && !editShowingId ? `daily through ${dayLabel(new Date(bulkF.repeatUntil + "T00:00:00"))}` : ""].filter(Boolean).join(" · ") : null, m: lastBatch ? "Pick a start time for the next showing" : "Start time missing", soft: Boolean(lastBatch) },
                                  { ic: "venue", fg: C.teal, bg: C.tealSoft, k: "Venue", v: shF.venueId ? venueName(shF.venueId) : null, m: "Venue missing" },
                                  { ic: "exp", fg: C.purple, bg: C.purpleSoft, k: "Experience", v: [shF.presentation.format, shF.presentation.dimension, shF.presentation.sound, shF.presentation.screenName].filter(Boolean).join(" · ") || "—", m: null },
                                  reserved
                                    ? { ic: "seats", fg: C.orange, bg: C.orangeSoft, k: "Layout", v: selectedMap ? `${selectedMap.name} · ${selectedMap.seatCount} units` : null, m: "Layout missing" }
                                    : { ic: "seats", fg: C.orange, bg: C.orangeSoft, k: "Sales model", v: modelLabel, m: null },
                                  { ic: "price", fg: C.green, bg: C.greenSoft, k: "Prices", v: completeTypes.length ? completeTypes.map((t) => `${t.name} ${money(t.price)}`).join(" · ") : null, m: "No price yet" },
                                ].map((r) => (
                                  <div key={r.k} style={{ display: "flex", gap: 10, padding: "10px 16px", borderBottom: "1px solid #f0f3f8", alignItems: "flex-start" }}>
                                    <span style={{ width: 26, height: 26, borderRadius: 7, background: r.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: r.fg }}>{SHX_ROW_ICONS[r.ic]}</span>
                                    <span style={{ minWidth: 0 }}>
                                      <span style={{ display: "block", fontSize: 10, color: C.textLight, textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 800 }}>{r.k}</span>
                                      <span style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.35, color: r.v ? C.text : r.soft ? C.textLight : "#b26a00", overflowWrap: "anywhere" }}>{r.v || r.m || "—"}</span>
                                    </span>
                                  </div>
                                ))}
                                <div style={{ padding: "14px 16px" }}>
                                  <PButton type="submit" loading={busy === "showing"} disabled={!shF.startsAt} style={{ width: "100%", justifyContent: "center" }}>
                                    {editShowingId ? "Review & save changes" : bulkCount > 1 ? `Review ${bulkCount} showtimes` : "Review showtime"}
                                  </PButton>
                                  <div style={{ fontSize: 11, color: C.textLight, textAlign: "center", marginTop: 8, lineHeight: 1.45 }}>
                                    {shF.startsAt
                                      ? editShowingId ? "You'll see a full read-back before anything is saved." : "You'll see a full read-back, then choose: save as drafts or put on sale."
                                      : "Set a start time to continue."}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>{/* shx-grid */}

                          <div className="shx-mobile-actions">
                            {lastBatch && (
                              <div style={{ flexBasis: "100%" }}>
                                <BatchResult batch={lastBatch} canPublish={canManageSelected && onboardingApproved} publishing={busy === "bulkpub"} onPublish={publishBatch} onDismiss={() => setLastBatch(null)} />
                              </div>
                            )}
                            <PButton type="submit" loading={busy === "showing"} disabled={!shF.startsAt}>
                              {editShowingId ? "Review & save changes" : bulkCount > 1 ? `Review ${bulkCount} showtimes` : "Review showtime"}
                            </PButton>
                            {busy === "showing" && <BulkProgress progress={bulkProgress} />}
                            {!shF.startsAt && <span style={{ fontSize: 11, color: C.textLight }}>Set a start time to continue.</span>}
                          </div>
                        </form>

                        {reviewState && (
                          <div onClick={() => { if (busy !== "showing") setReviewState(null); }}
                               style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                            <div onClick={(e) => e.stopPropagation()}
                                 style={{ background: "#fff", borderRadius: 14, boxShadow: "0 18px 60px rgba(15,23,42,.35)", width: "min(640px, 100%)", maxHeight: "88vh", overflowY: "auto", padding: "22px 24px" }}>
                              <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{editShowingId ? "Review changes before saving" : "Read it back before creating"}</div>
                              <div style={{ fontSize: 12, color: C.textLight, margin: "4px 0 8px" }}>Nothing is {editShowingId ? "saved" : "created"} yet. Check each line; Edit jumps to that step.</div>
                              {editShowingId && (
                                <div style={{ background: "#fff8e1", border: "1px solid #f5d78e", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#8a5a00", fontWeight: 600, marginBottom: 4 }}>
                                  Saving overwrites the existing showtime rather than adding a new one.
                                </div>
                              )}
                              {[
                                { ic: "when", fg: C.blue, bg: C.blueSoft, k: "When", target: "sh-starts",
                                  v: editShowingId || !reviewState.slots
                                    ? shortWhen(shF.startsAt)
                                    : reviewState.slots.slice(0, 4).map((d) => shortWhen(d.toISOString())).join("  ·  ") + (reviewState.slots.length > 4 ? `  ·  +${reviewState.slots.length - 4} more` : "") },
                                { ic: "venue", fg: C.teal, bg: C.tealSoft, k: "Venue", target: "sh-venue", v: venueName(shF.venueId) },
                                { ic: "exp", fg: C.purple, bg: C.purpleSoft, k: "Experience", target: "shx-card-exp", v: [shF.presentation.format, shF.presentation.dimension, shF.presentation.sound, shF.presentation.screenName].filter(Boolean).join(" · ") || "—" },
                                { ic: "seats", fg: C.orange, bg: C.orangeSoft, k: "Seats", target: "shx-card-inv", v: reviewState.reserved ? [modelLabel, selectedMap ? `${selectedMap.name} · ${selectedMap.seatCount} units` : ""].filter(Boolean).join(" · ") : modelLabel },
                                { ic: "price", fg: C.green, bg: C.greenSoft, k: "Prices", target: "shx-card-tt",
                                  v: reviewState.ticketTypes.map((t) => `${t.name} — ${money(t.price)}` + (t.seatClasses ? ` (${t.seatClasses.join(", ")})` : t.gaCapacity ? ` (${t.gaCapacity} on sale)` : "") + (Number(t.admissionsPerUnit) > 1 ? ` · admits ${t.admissionsPerUnit}` : "")).join("   ·   ") },
                              ].map((r) => (
                                <div key={r.k} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 0", borderBottom: "1px solid #f0f3f8" }}>
                                  <span style={{ width: 30, height: 30, borderRadius: 8, background: r.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: r.fg }}>{SHX_ROW_ICONS[r.ic]}</span>
                                  <span style={{ minWidth: 0, flex: 1 }}>
                                    <span style={{ display: "block", fontSize: 10, color: C.textLight, textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 800, marginBottom: 2 }}>{r.k}</span>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text, lineHeight: 1.45, overflowWrap: "anywhere" }}>{r.v}</span>
                                  </span>
                                  <button type="button" onClick={() => { setReviewState(null); focusField(r.target); }}
                                          style={{ border: "none", background: "none", color: C.blue, fontSize: 11.5, fontWeight: 700, cursor: "pointer", padding: "2px 4px" }}>Edit</button>
                                </div>
                              ))}
                              {!editShowingId && reviewState.slots && (reviewState.slots.length > 1 || reviewState.skippedPast > 0 || reviewState.skippedExisting > 0) && (
                                <div style={{ background: C.orangeSoft, border: "1px solid #f5d78e", borderRadius: 10, padding: "11px 14px", fontSize: 12.5, color: "#8a5a00", fontWeight: 700, marginTop: 14, lineHeight: 1.5 }}>
                                  {reviewState.slots.length > 1 ? `This creates ${reviewState.slots.length} showtimes, one per time per day.` : "This creates 1 showtime."}
                                  {reviewState.skippedExisting ? ` ${reviewState.skippedExisting} time${reviewState.skippedExisting === 1 ? " already exists" : "s already exist"} on this screen and ${reviewState.skippedExisting === 1 ? "is" : "are"} skipped.` : ""}
                                  {reviewState.skippedPast ? ` ${reviewState.skippedPast} time${reviewState.skippedPast === 1 ? " had" : "s had"} already passed today and ${reviewState.skippedPast === 1 ? "was" : "were"} dropped.` : ""}
                                </div>
                              )}
                              {!editShowingId && !(canManageSelected && onboardingApproved) && (
                                <div style={{ fontSize: 12, color: "#8a5a00", marginTop: 10 }}>Sales open once Lusuku approves this organizer, so these are saved as drafts for now.</div>
                              )}
                              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
                                <AppButton variant="quiet" icon="fa fa-arrow-left" disabled={busy === "showing"} onClick={() => setReviewState(null)}>Go back</AppButton>
                                <span style={{ flex: 1 }} />
                                {busy === "showing" && <BulkProgress progress={bulkProgress} />}
                                {editShowingId
                                  ? <AppButton loading={busy === "showing"} onClick={() => submitShowing(false)}>Save changes</AppButton>
                                  : (() => {
                                      const n = reviewState.slots ? reviewState.slots.length : 1;
                                      const canGoLive = canManageSelected && onboardingApproved;
                                      return (
                                        <>
                                          <AppButton variant={canGoLive ? "outline" : "primary"} loading={busy === "showing" && !canGoLive} disabled={busy === "showing"} onClick={() => submitShowing(false)}>
                                            {n > 1 ? `Save ${n} as drafts` : "Save as draft"}
                                          </AppButton>
                                          {canGoLive && (
                                            <AppButton icon="fa fa-ticket-alt" loading={busy === "showing"} onClick={() => submitShowing(true)}>
                                              {n > 1 ? `Create ${n} and put on sale` : "Create and put on sale"}
                                            </AppButton>
                                          )}
                                        </>
                                      );
                                    })()}
                              </div>
                            </div>
                          </div>
                        )}
                        </>)}
                      </Panel>
                    )}
                  </>
                )}

                {tab === "venues" && (
                  <Panel title="Venues" sub="physical outlets" color={C.teal}>
                    <form onSubmit={createVenue}>
                      <Row className="align-items-start">
                        <Col md={4}><Field label="Venue / outlet name" hint="The branch or outlet name."><PInput id="venue-name" placeholder="e.g. Century Cinema — Acacia Mall" value={venueF.name} onChange={(e) => setVenueF({ ...venueF, name: e.target.value })} /></Field></Col>
                        <Col md={6}><Field label="Location"
                          hint={venueF.latitude == null ? "Start typing and pick from the list to capture GPS coordinates." : null}>
                          <AddressAutocomplete placeholder="Search the outlet's location…" onChange={(sel) => setVenueF((f) => ({ ...f, address: sel?.address || "", latitude: sel?.latLng?.lat ?? null, longitude: sel?.latLng?.lng ?? null }))} />
                          {venueF.latitude != null && <div style={{ fontSize: 12, color: C.green, marginTop: 4 }}>✓ {venueF.address} ({venueF.latitude.toFixed(5)}, {venueF.longitude.toFixed(5)})</div>}
                        </Field></Col>
                        <Col md={2}><Field label="&nbsp;"><PButton type="submit" loading={busy === "venue"} style={{ width: "100%" }}>Add venue</PButton></Field></Col>
                      </Row>
                    </form>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr><th style={S.th}>Venue</th><th style={S.th}>Address</th><th style={{ ...S.th, textAlign: "right" }}>Action</th></tr></thead>
                      <tbody>
                        {venues.length === 0 && <tr><td style={{ ...S.td, color: C.textLight }} colSpan={3}>No venues yet.</td></tr>}
                        {venues.map((v) => {
                          const missingPin = !Number.isFinite(v.latitude) || !Number.isFinite(v.longitude);
                          return (
                            <tr key={v._id}>
                              <td style={{ ...S.td, fontWeight: 600 }}>
                                {v.name}
                                {missingPin && <div style={{ fontSize: 11, color: "#b26a00", fontWeight: 700, marginTop: 3 }}>No map location — fix this or sales can't be approved.</div>}
                              </td>
                              <td style={S.td}>{v.address || "—"}</td>
                              <td style={{ ...S.td, textAlign: "right" }}>
                                <span style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                                  <PButton variant="ghost" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => startEditVenue(v)}>Edit</PButton>
                                  <PButton variant="danger" loading={busy === "delvenue" + v._id} style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => removeVenue(v)}>Delete</PButton>
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {editVenue && (
                      <div style={{ marginTop: 14, background: "#f8fafc", border: `1px solid ${C.border}`, borderRadius: 8, padding: 13 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: C.text, marginBottom: 10 }}>Edit venue — {editVenue.original.name}</div>
                        <Row className="align-items-start">
                          <Col md={4}><Field label="Venue / outlet name"><PInput id="edit-venue-name" value={editVenue.name} onChange={(e) => setEditVenue({ ...editVenue, name: e.target.value })} /></Field></Col>
                          <Col md={5}><Field label="Location" hint={editVenue.latitude == null ? "Pick from the list to set the map location." : null}>
                            <AddressAutocomplete placeholder={editVenue.address || "Search the outlet's location…"} onChange={(sel) => setEditVenue((f) => ({ ...f, address: sel?.address || f.address, latitude: sel?.latLng?.lat ?? f.latitude, longitude: sel?.latLng?.lng ?? f.longitude }))} />
                            {editVenue.latitude != null && <div style={{ fontSize: 12, color: C.green, marginTop: 4 }}>✓ {editVenue.address} ({Number(editVenue.latitude).toFixed(5)}, {Number(editVenue.longitude).toFixed(5)})</div>}
                          </Field></Col>
                          <Col md={3}><Field label="&nbsp;">
                            <span style={{ display: "flex", gap: 8 }}>
                              <PButton loading={busy === "editvenue"} onClick={saveEditVenue} style={{ flex: 1 }}>Save</PButton>
                              <PButton variant="ghost" onClick={() => setEditVenue(null)}>Cancel</PButton>
                            </span>
                          </Field></Col>
                        </Row>
                      </div>
                    )}
                  </Panel>
                )}

                {tab === "seatmaps" && (
                  <Panel title="Reserved layouts" sub="reusable seat, table, booth, or box grids" color={C.purple}>
                    <form onSubmit={createSeatMap}>
                      <Row className="align-items-end">
                        <Col md={3}><Field label="Name *" hint="Name it after the hall, not the experience — you pick this in the showtime form.">
                          <PInput id="sm-name" placeholder="e.g. Acacia Mall - Screen 1" value={smF.name} onChange={(e) => setSmF({ ...smF, name: e.target.value })} />
                        </Field></Col>
                        <Col md={3}><Field label="Venue *" hint="A hall lives in exactly one outlet.">
                          <PSelect id="sm-venue" value={smF.venueId} onChange={(e) => setSmF({ ...smF, venueId: e.target.value })}>
                            <option value="">— select a venue —</option>{venues.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
                          </PSelect>
                        </Field></Col>
                        <Col md={2}><Field label="Rows"><PInput type="number" min="1" value={smF.rows} onChange={(e) => setSmF({ ...smF, rows: e.target.value })} /></Field></Col>
                        <Col md={2}><Field label="Units / row"><PInput type="number" min="1" value={smF.cols} onChange={(e) => setSmF({ ...smF, cols: e.target.value })} /></Field></Col>
                        <Col md={2}><Field label="Seat class" hint="A PRICE TIER inside this hall (STANDARD, VIP, RECLINER) — not the 2D/MAX experience, which is set per showtime.">
                          <PInput placeholder="STANDARD" value={smF.seatClass} onChange={(e) => setSmF({ ...smF, seatClass: e.target.value })} />
                        </Field></Col>
                      </Row>
                      <div style={{ background: C.blueSoft, border: "1px solid #cfe2ff", borderRadius: 8, padding: "10px 14px", fontSize: 13, margin: "2px 0 14px", color: C.text }}>
                        Builds a <strong>{Number(smF.rows) || 0} × {Number(smF.cols) || 0}</strong> grid = <strong style={{ color: C.green }}>{(Number(smF.rows) || 0) * (Number(smF.cols) || 0)} reserved units</strong> — rows labelled A, B, C… Use it for seats, tables, booths, or boxes; bespoke layouts accept explicit API sections.
                      </div>
                      <PButton type="submit" loading={busy === "sm"} disabled={!(smF.name.trim() && smF.venueId && Number(smF.rows) > 0 && Number(smF.cols) > 0)}>Create layout</PButton>
                      {!smF.venueId && smF.name.trim() ? <span style={{ marginLeft: 12, fontSize: 12, color: C.textLight }}>Choose the venue this hall is in.</span> : null}
                    </form>
                    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10 }}>
                      <thead><tr><th style={S.th}>Layout</th><th style={S.th}>Venue</th><th style={S.th}>Units</th><th style={{ ...S.th, textAlign: "right" }}>Action</th></tr></thead>
                      <tbody>
                        {seatMaps.length === 0 && <tr><td style={{ ...S.td, color: C.textLight }} colSpan={4}>No reserved layouts yet.</td></tr>}
                        {seatMaps.map((m) => {
                          const mv = venues.find((v) => String(v._id) === String(m.venueId));
                          return (
                            <tr key={m._id}>
                              <td style={{ ...S.td, fontWeight: 600 }}>{m.name}</td>
                              <td style={S.td}>{mv ? mv.name : <span style={{ color: "#b26a00", fontWeight: 700 }}>Not linked to a venue</span>}</td>
                              <td style={S.td}>{m.seatCount}</td>
                              <td style={{ ...S.td, textAlign: "right" }}>
                                <span style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                                  <PButton variant="ghost" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => startEditMap(m)}>Edit</PButton>
                                  <PButton variant="danger" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => removeMap(m)}>Delete</PButton>
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {editMap && (
                      <div style={{ marginTop: 14, background: "#f8fafc", border: `1px solid ${C.border}`, borderRadius: 8, padding: 13 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: C.text, marginBottom: 10 }}>Edit layout — {editMap.original.name}</div>
                        <Row className="align-items-end">
                          <Col md={4}><Field label="Layout name"><PInput id="edit-sm-name" value={editMap.name} onChange={(e) => setEditMap({ ...editMap, name: e.target.value })} /></Field></Col>
                          <Col md={4}><Field label="Venue"><PSelect value={editMap.venueId} onChange={(e) => setEditMap({ ...editMap, venueId: e.target.value })}>
                            <option value="">— select a venue —</option>{venues.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
                          </PSelect></Field></Col>
                          <Col md={4}><Field label="&nbsp;">
                            <span style={{ display: "flex", gap: 8 }}>
                              <PButton loading={busy === "editsm"} onClick={saveEditMap} style={{ flex: 1 }}>Save</PButton>
                              <PButton variant="ghost" onClick={() => setEditMap(null)}>Cancel</PButton>
                            </span>
                          </Field></Col>
                        </Row>
                        <div style={{ fontSize: 11.5, color: C.textLight }}>The grid and seat classes are fixed once created — showtimes already reference this layout by id, so renaming is safe but re-shaping is not.</div>
                      </div>
                    )}
                  </Panel>
                )}

                {tab === "sales" && (
                  <Panel title="Sales summary" sub="excludes refunded and voided tickets">
                    {!sales ? <Loader label="Loading sales…" /> : (
                      <>
                      <Row>
                        {[["Units sold", sales.ticketsSold, C.blue], ["Admissions sold", sales.admissionsSold ?? sales.ticketsSold, C.orange], ["Admissions redeemed", sales.admissionsRedeemed ?? sales.redeemed, C.teal], ["Gross", money(sales.gross), C.green],
                          [`Commission (${Math.round(sales.commissionRate || 0)}%)`, money(sales.platformCommission), C.orange], ["Organizer net", money(sales.organizerNet), C.purple]]
                          .map(([label, val, color]) => (
                            <Col md key={label} className="mb-3"><Kpi label={label} value={val} color={color} /></Col>
                          ))}
                      </Row>
                      {Number(sales.refundedCount || 0) > 0 && (
                        <div style={{ fontSize: 12, color: C.textMid, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                          Excluded from the figures above: <strong style={{ color: C.text }}>{sales.refundedCount}</strong> refunded or voided ticket{Number(sales.refundedCount) === 1 ? "" : "s"} worth <strong style={{ color: C.text }}>{money(sales.refundedValue)}</strong>.
                        </div>
                      )}
                      </>
                    )}
                  </Panel>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Page>
  );
};

export default TicketingConsole;
